import { createSlice,
         createAsyncThunk,  
         PayloadAction,
         ActionReducerMapBuilder,
         createSelector           } from '@reduxjs/toolkit';
import { WritableDraft            } from 'immer';

import Preferences                  from 'src/services/preferences';
import Api,
       { ApiError                 } from 'src/services/api';
import * as types                   from 'src/services/api/types';
import Db                           from 'src/services/db';
import Util                         from 'src/services/util';
import Constants                    from 'src/services/constants';
import { InspectionState,
         ComplaintType,
         ComplaintMode,
         QCState,
         InspectionStatusCode,
         PieceMode,
         PieceFileMode,
         DefectType,
         DefectFileMode,
         DefectFileType,
         DefectMode,
         PieceFileType,
         ProductTypeKolpino,
         ModeControlDefect        } from 'src/services/api/types/inspection';
import { AccountRole              } from 'src/services/api/types/auth';

import { RootState                } from 'src/store';
import { authCommonSelectors      } from 'src/store/auth/common';

import { AsyncOpStatus,
         Nullable                 } from 'src/common';



const SLICE_NAME = 'inspection:edit';

type ExtraReducer = (builder: ActionReducerMapBuilder<InspectionEditState>) => void;
const extraReducers: ExtraReducer[] = [];

export interface InspectionEditState {
  generalOpStatus: AsyncOpStatus;
  generalOpStatusLabel: string;
  generalSelectModeOpened: boolean;

  generalAccount: Nullable<types.auth.Account>;
  generalInspectionState: InspectionState;
  generalOriginal: Nullable<types.inspection.Inspection>;
  generalEdited: Nullable<types.inspection.Inspection>;
  generalIsEntryAccounting: boolean;
  generalIsTransferOnly: boolean;
  generalIsChangeToComplaint: boolean;
  generalIsChangeToComplaintFinance: boolean;
  generalIsChangeComplaintCosts: boolean;
  generalIsChangeComplaintAll: boolean;
  generalIsGroup: boolean;
  generalIsNoTrans: boolean;

  generalPlaceOfFixationRefer: types.refers.PlaceOfFixationReferElement[];
  generalEnterpriseRefer: types.refers.EnterpriseReferElement[]
  generalDefectRefer: types.refers.DefectReferElement[];
  generalFavoriteDefects: number[];
  generalWaysToUseRegectedRefer: types.refers.WaysToUseRegectedReferElement[];
  generalTopographyRefer: types.refers.DefectTopographyReferElement[];
  generalProductRefer: types.refers.ProductReferElement[];
  generalUnitRefer: types.refers.UnitReferElement[];
  generalPriorityRefer: types.refers.PriorityReferElement[];
  generalLocationRefer: types.refers.LocationReferElement[];

  defectExpandedGuid: string;
  defectChangedManually: boolean;
  defectSelectTypeOpened: boolean;

  pieceMarkedForDeleteGuids: string[];
  pieceSelectedGuid: string;
  pieceCVCheckFilesAttempts: string[];
  pieceCVCheckFilesDuplicates: Record<string, string[]>;
  pieceCVCheckFilesBlur: Record<string, string[]>;

  pieceEditOpened: boolean;
  pieceEdited: Nullable<types.inspection.Piece>;
  pieceEditedWeight: string;
  pieceEditedWeightBeforeProcessing: string;
  pieceEditedWeightAfterProcessing: string;
  pieceEditedWeightAfterProcessingError: string;
  pieceEditedIsDouble: boolean;
  pieceEditedQmetIdError: string;
  pieceEditedCarriageError: string;
  pieceEditedCarriageQcsInError: string[];
  pieceEditedSpecificationNumError: string;
  pieceEditedSpecificationPosInError: string[];
  pieceEditedHeatError: string;
  pieceEditedPieceError: string;
  pieceEditedGroupAttError: string;
  pieceEditedQCNumError: string;
  pieceEditedCustomFieldsErrors: Record<string, string>;
  pieceEditedIsMarriageShareExpanded: boolean;
  pieceEditedWeightError: string;
  pieceEditedNoteError: string;
  pieceEditedIsTopographyExpanded: boolean;
  pieceEditedTopoSizeError: string;
  pieceEditedTopoSize: string;
  pieceEditedTopoStepError: string;
  pieceEditedTopoStep: string;
  pieceEditedTopoDistanceError: string;
  pieceEditedTopoDistance: string;
  pieceEditedExternalDoublesOpened: boolean;
  pieceEditedExternalDoublesList: types.inspection.Inspection[];
  pieceEditedCVCheckFilesResultOpened: boolean;
  pieceEditedCVCheckFilesResultGroup: Record<string, string[]>;
  pieceEditedCVCheckFilesResultSingle: string[];
  pieceCertDownloadOpened: boolean;

  complaintEditOpened: boolean;
  complaintDefectGuid: string;
  complaintEdited: Nullable<types.inspection.Complaint>;
  complaintEditedSale: string;
  complaintEditedScrapPrice: string;
  complaintEditedAmountOfCompensation: string;
  complaintEditedPenalty: string;
  complaintEditedSumReqRolledMetall: string;
  complaintEditedInitiatorAOSDSelected: boolean;
  complaintEditedMode: 'new' | 'edit' | 'tocomplaint' | 'tocomplaintfinance' | 'costs' | 'all' | 'view';
  complaintContactDataExpanded: boolean;

  endingOpened: boolean;
  endingEndCaption: string;
  endingDraftVisible: boolean;
  endingDraftCaption: string;
  endingCloseCaption: string;
  endingCancelCaption: string;
}

const initialState: InspectionEditState = {
  generalOpStatus: AsyncOpStatus.IDLE,
  generalOpStatusLabel: '',
  generalSelectModeOpened: false,

  generalAccount: null,
  generalInspectionState: InspectionState.NULL,
  generalOriginal: null,
  generalEdited: null,
  generalIsEntryAccounting: false,
  generalIsTransferOnly: false,
  generalIsChangeToComplaint: false,
  generalIsChangeToComplaintFinance: false,
  generalIsChangeComplaintCosts: false,
  generalIsChangeComplaintAll: false,
  generalIsGroup: false,
  generalIsNoTrans: false,

  generalPlaceOfFixationRefer: [],
  generalEnterpriseRefer: [],
  generalDefectRefer: [],
  generalFavoriteDefects: [],
  generalWaysToUseRegectedRefer: [],
  generalTopographyRefer: [],
  generalProductRefer: [],
  generalUnitRefer: [],
  generalPriorityRefer: [],
  generalLocationRefer: [],

  defectExpandedGuid: '',
  defectChangedManually: false,
  defectSelectTypeOpened: false,

  pieceMarkedForDeleteGuids: [],
  pieceSelectedGuid: '',
  pieceCVCheckFilesAttempts: [],
  pieceCVCheckFilesDuplicates: { },
  pieceCVCheckFilesBlur: { },
  
  pieceEditOpened: false,
  pieceEdited: null,
  pieceEditedWeight: '',
  pieceEditedWeightBeforeProcessing: '',
  pieceEditedWeightAfterProcessing: '',
  pieceEditedWeightAfterProcessingError: '',
  pieceEditedIsDouble: false,
  pieceEditedQmetIdError: '',
  pieceEditedCarriageError: '',
  pieceEditedCarriageQcsInError: [],
  pieceEditedSpecificationNumError: '',
  pieceEditedSpecificationPosInError: [],
  pieceEditedHeatError: '',
  pieceEditedPieceError: '',
  pieceEditedGroupAttError: '',
  pieceEditedQCNumError: '',
  pieceEditedCustomFieldsErrors: {},
  pieceEditedIsMarriageShareExpanded: false,
  pieceEditedWeightError: '',
  pieceEditedNoteError: '',
  pieceEditedIsTopographyExpanded: false,
  pieceEditedTopoSize: '',
  pieceEditedTopoSizeError: '',
  pieceEditedTopoStep: '',
  pieceEditedTopoStepError: '',
  pieceEditedTopoDistance: '',
  pieceEditedTopoDistanceError: '',
  pieceEditedExternalDoublesOpened: false,
  pieceEditedExternalDoublesList: [],
  pieceEditedCVCheckFilesResultOpened: false,
  pieceEditedCVCheckFilesResultGroup: {},
  pieceEditedCVCheckFilesResultSingle: [],
  pieceCertDownloadOpened: false,

  complaintEditOpened: false,
  complaintDefectGuid: '',
  complaintEdited: null,
  complaintEditedSale: '',
  complaintEditedScrapPrice: '',
  complaintEditedAmountOfCompensation: '',
  complaintEditedPenalty: '',
  complaintEditedSumReqRolledMetall: '',
  complaintEditedInitiatorAOSDSelected: false,
  complaintEditedMode: 'view',
  complaintContactDataExpanded: false,

  endingOpened: false,
  endingEndCaption: 'Завершить осмотр',
  endingDraftVisible: true,
  endingDraftCaption: 'Сохранить как черновик',
  endingCloseCaption: 'Выйти без сохранения',
  endingCancelCaption: 'Отмена',
}

// #region ОБЩЕЕ

// #region buildForEditAsync
type BuildForEditArgs = {
  guid: string;
  state: InspectionState;
  withDefect: boolean;
  isTransferOnly: boolean;
  isChangeToComplaint: boolean;
  isChangeToComplaintFinance: boolean;
  isChangeComplaintCosts: boolean;
  isChangeComplaintAll: boolean;
};
type BuildForEditResolve = {
  operationResult: 'get_error' | 'error_revocation' | 'error_fixed' | 'data_updated' | 'success';
  operationResultMessage: string;

  generalAccount: Nullable<types.auth.Account>;
  generalInspectionState: InspectionState;
  generalEdited: Nullable<types.inspection.Inspection>;
  generalIsEntryAccounting: boolean;
  generalIsTransferOnly: boolean;
  generalIsChangeToComplaint: boolean;
  generalIsChangeToComplaintFinance: boolean;
  generalIsChangeComplaintCosts: boolean;
  generalIsChangeComplaintAll: boolean;
  generalIsGroup: boolean;
  
  generalPlaceOfFixationRefer: types.refers.PlaceOfFixationReferElement[];
  generalEnterpriseRefer: types.refers.EnterpriseReferElement[];
  generalDefectRefer: types.refers.DefectReferElement[];
  generalWaysToUseRegectedRefer: types.refers.WaysToUseRegectedReferElement[];
  generalTopographyRefer: types.refers.DefectTopographyReferElement[];
  generalProductRefer: types.refers.ProductReferElement[];
  generalUnitRefer:types.refers.UnitReferElement[];
  generalFavoriteDefects: number[];
  generalPriorityRefer: types.refers.PriorityReferElement[];
  generalLocationRefer: types.refers.LocationReferElement[];

  defectExpandedGuid: string;
};
export type BuildForEditReject = string;
export const buildForEditAsync = createAsyncThunk<
  BuildForEditResolve,
  BuildForEditArgs,
  {
    state: RootState,
    rejectValue: BuildForEditReject,
  }
>(
  `${SLICE_NAME}/buildForEditAsync`,
  async (args, thunkAPI) => {
    try
    {
      const account = authCommonSelectors.selectAccount(thunkAPI.getState())!;
      const generalPlaceOfFixationRefer = await Db.getPlaceOfFixations();
      const generalEnterpriseRefer = await Db.getEnterprises();
      const generalWaysToUseRegectedRefer = await Db.getWaysToUseRegected();
      const generalTopographyRefer = await Db.getDefectTopographies();
      const generalProductRefer = await Db.getProducts();
      const generalUnitRefer = await Db.getUnits();
      const generalPriorityRefer = await Db.getPriorities();
      const generalLocationRefer = await Db.getLocations();
      const defects = await Db.getDefects();
      const generalDefectRefer = defects.map((defect) => ({
        ...defect,
        isGroup: defects.some((item) => item.parentId === defect.id)
      }));
      const generalFavoriteDefects = generalDefectRefer.filter((item) => item.isFavorite).map((item) => item.id);

      const createFilesMap: Record<string, string> = {};

      const result: BuildForEditResolve = {
        operationResult: 'success',
        operationResultMessage: '',

        generalAccount: account,
        generalInspectionState: args.state,
        generalEdited: null,
        generalIsEntryAccounting: false,
        generalIsTransferOnly: args.isTransferOnly,
        generalIsChangeToComplaint: args.isChangeToComplaint,
        generalIsChangeToComplaintFinance: args.isChangeToComplaintFinance,
        generalIsChangeComplaintCosts: args.isChangeComplaintCosts,
        generalIsChangeComplaintAll: args.isChangeComplaintAll,
        generalIsGroup: false,
        
        generalPlaceOfFixationRefer,
        generalEnterpriseRefer,
        generalDefectRefer,
        generalWaysToUseRegectedRefer,
        generalTopographyRefer,
        generalProductRefer,
        generalUnitRefer,
        generalFavoriteDefects,
        generalPriorityRefer,
        generalLocationRefer,

        defectExpandedGuid: '',
      }

      switch (args.state)
      {
        case InspectionState.NEW:
          const inspectionGuid = Util.guid(true);
          const defectGuid = Util.guid(true);

          result.generalIsEntryAccounting = args.withDefect ? false : true;
          result.generalIsGroup = true;
          result.defectExpandedGuid = defectGuid;

          result.generalEdited = {
            guid: inspectionGuid,
            inspectionNum: '',
            inspectionType: null,
            isGroup: true,
            isEntryAccounting: args.withDefect ? false : true,
            timestampCreated: Date.now(),
            timestampInspection: Date.now(),
            timestampCompletion: null,
            userId: account.id,
            userFio: `${account.lastName} ${account.firstName} ${account.middleName}`,
            userFilialId: null,
            timestampModified: Date.now(),
            status: {
              id: 0,
              code: types.inspection.InspectionStatusCode.DRAFT,
              title: 'Черновик',
              timestamp: Date.now(),
              fio: `${account.lastName} ${account.firstName} ${account.middleName}`,
              note: null,
            },
            inspectionDefects: [{
              guid: defectGuid,
              timestampDefect: Date.now(),
              defectType: null,
              defectId: args.withDefect ? -1 : null,
              defectTitle: args.withDefect ? '' : null,
              priorityId: null,
              priorityTitle: null,
              placeOfFixationId: (!args.withDefect && account.role !== AccountRole.Contractor) ? 1 : null,
              placeOfFixationTitle: (!args.withDefect && account.role !== AccountRole.Contractor) ? 'Входной контроль' : null,
              product: null,
              note: null,
              numExternal: null,
              isViolationOfRequirements: false,
              violatedRequirements: null,
              isPossibilityUsingRejected: false,
              wayToUseRegectedId: null,
              wayToUseRegectedTitle: null,
              rentalStateOfDelivery: null,
              deviationValue: null,
              locationsOfDefect: [],
              cntMarriage: 0,
              actJoinInsp: null,
              timestampJoinInsp: null,
              files: [],
              isSend: false,
              pieces: [],
              complaint: null,
              mode: types.inspection.DefectMode.NEW,
              __isJoinInspection: false,
              __deviationValueStr: '',
            }],
            qmmsgs: [],
            __isTrans: false,
            __isCompleted: false,
            __fromEntryAccounting: false,
          }
          break;
        default:
          thunkAPI.dispatch(inspectionEditActions.generalOpStatusChanged(AsyncOpStatus.BUSY));
          thunkAPI.dispatch(inspectionEditActions.generalOpStatusLabelChanged('Получение осмотра'));

          let remoteInspection: Nullable<types.inspection.Inspection> = null;

          try
          {
            remoteInspection = await Api.getInspectionByGuid({ guid: args.guid });
          }
          catch (apiError) { }

          let localInspection: Nullable<types.inspection.Inspection> = null;

          try
          {
            localInspection = (await Db.getInspections()).find((item) => item.guid === args.guid) ?? null;
          }
          catch (dbError) { }

          if (remoteInspection === null && localInspection === null)
          {
            result.operationResult = 'get_error';
          }
          else if (remoteInspection !== null && remoteInspection.status.code === InspectionStatusCode.REVOCATION)
          {
            result.operationResult = 'error_revocation';
            result.operationResultMessage = 'Осмотр отозван, редактирование недоступно. При необходимости восстановите обращение и после этого внесите корректировки.';
          }
          else if (remoteInspection !== null && localInspection === null)
          {
            if (args.state === InspectionState.ERROR && remoteInspection.status.code !== InspectionStatusCode.ERROR)
            {
              result.operationResult = 'error_fixed';
              result.operationResultMessage = `Ошибка исправлена. Осмотр в статусе ${remoteInspection.status.code}`;
            }
            else
            {
              result.generalEdited = JSON.parse(JSON.stringify(remoteInspection));
              result.generalEdited!.__isTrans = true;
              result.generalEdited!.__isCompleted = remoteInspection.status != null && remoteInspection.status.code !== InspectionStatusCode.DRAFT;
            }
          }
          else if (localInspection !== null && (remoteInspection === null || remoteInspection.timestampModified <= localInspection.timestampModified))
          {
            result.generalEdited = JSON.parse(JSON.stringify(localInspection)) as types.inspection.Inspection;
            result.generalEdited!.__isTrans = false;
            result.generalEdited!.__isCompleted = localInspection.status != null && localInspection.status.code !== InspectionStatusCode.DRAFT && localInspection.status.code !== InspectionStatusCode.NO_TRANS_DRAFT;

            if (args.state === InspectionState.ERROR && localInspection.status.code !== InspectionStatusCode.ERROR && localInspection.__isTrans)
            {
              result.operationResult = 'error_fixed';
              result.operationResultMessage = `Ошибка исправлена. Осмотр в статусе ${localInspection.status.code}`;
            }
          }
          else if (localInspection != null && remoteInspection !== null && remoteInspection.timestampModified > localInspection.timestampModified) {
            result.generalEdited = JSON.parse(JSON.stringify(remoteInspection));
            result.generalIsTransferOnly = false;
            result.operationResult = 'data_updated';
            result.operationResultMessage = 'Осмотр обновлен на более новую версию. При необходимости скорректируйте и сохраните заново.'
          }

          if (result.generalEdited !== null)
          {
            result.generalIsGroup = result.generalEdited.isGroup;
          }

          if (result.generalEdited !== null && args.state === InspectionState.CREATE)
          {
            result.generalEdited.inspectionNum = '';
            result.generalEdited.guid = Util.guid(true);
            result.generalEdited.timestampInspection = Date.now();
            result.generalEdited.isGroup = true;
            result.generalEdited.isEntryAccounting = false;
            result.generalEdited.userId = account.id;
            result.generalEdited.userFio = `${account.lastName} ${account.firstName} ${account.middleName}`;
            result.generalEdited.__isTrans = false;
            result.generalEdited.__isCompleted = false;
            result.generalEdited.__fromEntryAccounting = true;
            result.generalEdited.status = {
              id: 0,
              code: types.inspection.InspectionStatusCode.DRAFT,
              title: 'Черновик',
              timestamp: Date.now(),
              fio: null,
              note: null,
            };

            result.generalEdited.inspectionDefects.forEach((defect) => {
              defect.guid = Util.guid(true);
              defect.mode = types.inspection.DefectMode.NEW;
              defect.defectId = -1;
              defect.defectTitle = null;

              for (let file of defect.files)
              {
                const newGuid = Util.guid(true);
                createFilesMap[newGuid] = file.guid;
                file.guid = newGuid;
                file.mode = types.inspection.DefectFileMode.NEW;
              }

              (defect.pieces || []).forEach((piece) => {
                piece.guid = Util.guid(true);
                piece.mode = types.inspection.PieceMode.NEW;

                piece.weight = null;
                piece.weightBeforeProcessing = null;
                piece.weightAfterProcessing = null;

                if (piece.isPiece)
                {
                  piece.pieceNumId = null;

                  if (piece.qmetData)
                  {
                    piece.qmetData.productType = '';
                    piece.qmetData.qcNum = null;
                    piece.qmetData.group = null;
                    piece.qmetData.qcState = QCState.NONE;
                  }

                  piece.qmetId = null;
                  if (piece.groupAtt == null)
                  {
                    piece.qcNum = null;
                  }
                  else
                  {
                    piece.piece = null;
                  }
                }
                else
                {
                  /*
                  https://help.severstal.com/spaces/Robotizationprocess/pages/618340820/%D0%A0%D0%B5%D0%BB%D0%B8%D0%B7%D1%8B+%D0%A4%D0%B5%D0%B2%D1%80%D0%B0%D0%BB%D1%8C+%D0%9C%D0%B0%D1%80%D1%82+2025
                  Для осмотров, созданных из входного учета, нужно показывать плавку, лист и сертификат
                  если заводили по идентификационному номеру.
                  piece.pieceNumId = null;

                  if (piece.qmetData)
                  {
                    piece.qmetData.productType = '';
                    piece.qmetData.qcNum = null;
                    piece.qmetData.group = null;
                    piece.qmetData.qcState = QCState.NONE;
                  }

                  piece.heat = null;
                  piece.piece = null;
                  piece.groupAtt = null;
                  piece.qcNum = null;
                  */
                }

                if (piece.unit.id === 35)
                {
                  piece.unit.id = 1;
                  piece.unit.title = 'кг';
                }

                const unitDetail = generalUnitRefer
                  .find((item) => item.id === piece.unit.id)?.details?.find((detail) => !detail.isEntryAccounting) ?? null;

                if (unitDetail !== null)
                {
                  piece.unit.fieldTitle = unitDetail.fieldTitle;
                  piece.unit.fieldHint = unitDetail.fieldHint;
                }

                for (let file of piece.files)
                {
                  const newGuid = Util.guid(true);
                  createFilesMap[newGuid] = file.guid;
                  file.guid = newGuid;
                  file.mode = types.inspection.PieceFileMode.NEW;
                }
              })
            });

            thunkAPI.dispatch(inspectionEditActions.generalOpStatusLabelChanged('Получение фото осмотра'));

            const docProcessing = async (files: types.inspection.PieceFile[]): Promise<void> => {
              if (files.length > 0)
              {
                const file = files[0];
                if (file.__name === undefined)
                {
                  try
                  {
                    const remoteFile = await Api.getInspectionFileByGuid({
                      guid: createFilesMap[file.guid] ?? file.guid,
                      source: 'piece'
                    });

                    if (file.type === PieceFileType.PDF)
                    {
                      
                      let mime = '';

                      switch (Util.getFileExt(file.name).toUpperCase())
                      {
                        case 'PDF':
                          mime = 'application/pdf';
                          break;
                        case 'XLSX':
                          mime = 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';
                          break;
                        default:
                          break;
                      }

                      const fileFromBlob = await Util.blobToFile(remoteFile.blob, file.name, mime);
                      const binaryStr = await Util.fileToBinaryString(fileFromBlob);
                      const dataUrlResult = await Util.fileToDataURL(fileFromBlob);

                      if (dataUrlResult.ok)
                      {
                        file.__dataUrl = dataUrlResult.result;
                        file.__binaryStr = binaryStr;
                        file.__name = Util.buildPieceAttachmentName(file.type, file.name);
                        file.__mime = mime;
                      }
                      else
                      {
                        file.__dataUrl = "";
                      }
                    }
                    else
                    {
                      const isTIF = Util.checkFileTypeByExt(file.name) === 'TIF';
                      const mime = isTIF ? 'image/tiff' : 'image/jpeg';
                      const fileFromBlob = await Util.blobToFile(remoteFile.blob, file.name, mime);
                      const dataUrlResult = await Util.imageToDataURL(fileFromBlob);
                      const binaryStr = await Util.fileToBinaryString(fileFromBlob);
                      if (dataUrlResult.ok)
                      {
                        file.__dataUrl = dataUrlResult.result;
                        file.__binaryStr = binaryStr;
                        file.__name = Util.buildPieceAttachmentName(file.type, file.name);
                        file.__mime = mime;
                      }
                      else
                      {
                        file.__dataUrl = Util.noPhoto;
                      }
                    }
                  }
                  catch(error)
                  {
                    file.__dataUrl = Util.noPhoto;
                  }
                }
                return docProcessing(files.slice(1));
              }
            }

            const pieceFiles: types.inspection.PieceFile[] = [];

            result.generalEdited.inspectionDefects.forEach((defect) => {
              (defect.pieces ?? []).forEach((piece) => {
                pieceFiles.push(...piece.files);
              })
            });

            await docProcessing(pieceFiles);
          }

          if (result.generalEdited !== null)
          {
            result.generalEdited.inspectionDefects.forEach((defect) => {
              defect.__isJoinInspection = defect.actJoinInsp !== null && defect.actJoinInsp !== '';
              defect.__deviationValueStr = `${defect.deviationValue ?? ''}`;
            })
            result.defectExpandedGuid = result.generalEdited.inspectionDefects[0].guid;
          }

          /** Если isChangeToComplaint = true или isChangeToComplaintFinance = true или isChangeComplaintAll = true */
          if (args.isChangeToComplaint || args.isChangeToComplaintFinance || args.isChangeComplaintAll)
          {
            if (result.generalEdited !== null)
            {
              if (result.generalEdited.inspectionDefects.length > 0)
              {
                const defect = result.generalEdited.inspectionDefects[0];

                if (defect.complaint !== null)
                {
                  defect.complaint.typeComplaint = ComplaintType.COMPLAINT;
                }
              }
            }
          }

          /** Если isChangeComplaintCosts = true  */
          if (args.isChangeComplaintCosts)
          {
            if (result.generalEdited !== null)
            {
              if (result.generalEdited.inspectionDefects.length > 0)
              {
                const defect = result.generalEdited.inspectionDefects[0];

                if (defect.complaint !== null)
                {
                  defect.complaint.isToOffsetCosts = true;
                }
              }
            }
          }

          if (result.generalEdited !== null)
          {
            result.generalIsEntryAccounting = result.generalEdited.isEntryAccounting;

            result.generalEdited.inspectionDefects.forEach((defect) => {
              if (
                defect.complaint !== null
                &&
                (defect.complaint.typeComplaint === null || defect.complaint.typeComplaint === types.inspection.ComplaintType.NONE)
              )
              {
                defect.complaint = null;
              }
            })
          }

          if (result.generalEdited !== null)
          {
            result.generalEdited.inspectionDefects.forEach((defect) => {
              if (
                defect.complaint !== null
                &&
                defect.complaint.__initiatorAOSDSelected === undefined
              )
              {
                defect.complaint.__initiatorAOSDSelected = defect.complaint.initiator !== null;
              }

              if (
                defect.complaint !== null
                &&
                defect.complaint.__contactDataExpanded === undefined
              )
              {
                defect.complaint.__contactDataExpanded = [
                  defect.complaint.fio, defect.complaint.enterprise, defect.complaint.phone, defect.complaint.email
                ].some((v) => v !== null);
              }
            })
          }

          thunkAPI.dispatch(inspectionEditActions.generalOpStatusChanged(AsyncOpStatus.SUCCESS));
      }

      return result;
    }
    catch (error)
    {
      thunkAPI.dispatch(inspectionEditActions.generalOpStatusChanged(AsyncOpStatus.SUCCESS));
      return thunkAPI.rejectWithValue('Ошибка при открытии осмотра');
    }
  }
);
extraReducers.push((builder) => {
  builder
    .addCase(buildForEditAsync.fulfilled, (state, action) => {
      state.generalAccount = action.payload.generalAccount;
      state.generalInspectionState = action.payload.generalInspectionState;
      state.generalOriginal = action.payload.generalInspectionState === InspectionState.NEW ? null : JSON.parse(JSON.stringify(action.payload.generalEdited));
      state.generalEdited = JSON.parse(JSON.stringify(action.payload.generalEdited));
      state.generalIsEntryAccounting = action.payload.generalIsEntryAccounting;
      state.generalIsTransferOnly = action.payload.generalIsTransferOnly;
      state.generalIsChangeToComplaint = action.payload.generalIsChangeToComplaint;
      state.generalIsChangeToComplaintFinance = action.payload.generalIsChangeToComplaintFinance;
      state.generalIsChangeComplaintCosts = action.payload.generalIsChangeComplaintCosts;
      state.generalIsChangeComplaintAll = action.payload.generalIsChangeComplaintAll;
      state.generalIsGroup = action.payload.generalIsGroup;
      state.generalIsNoTrans = [InspectionState.NEW, InspectionState.CREATE].includes(action.payload.generalInspectionState) ? false : !(action.payload.generalEdited?.__isTrans ?? false);
      state.generalPlaceOfFixationRefer = action.payload.generalPlaceOfFixationRefer;
      state.generalEnterpriseRefer = action.payload.generalEnterpriseRefer;
      state.generalWaysToUseRegectedRefer = action.payload.generalWaysToUseRegectedRefer;
      state.generalTopographyRefer = action.payload.generalTopographyRefer;
      state.generalDefectRefer = action.payload.generalDefectRefer;
      state.generalFavoriteDefects = action.payload.generalFavoriteDefects;
      state.defectExpandedGuid = action.payload.defectExpandedGuid;
      state.generalProductRefer = action.payload.generalProductRefer;
      state.generalUnitRefer = action.payload.generalUnitRefer;
      state.generalPriorityRefer = action.payload.generalPriorityRefer;
      state.generalLocationRefer = action.payload.generalLocationRefer;

      state.pieceCVCheckFilesAttempts = [];
      state.pieceCVCheckFilesDuplicates = { };
      state.pieceCVCheckFilesBlur = { };

      const inspectionState = action.payload.generalInspectionState;
      const isEntryAccounting = action.payload.generalIsEntryAccounting;

      if (inspectionState === InspectionState.EDIT || inspectionState === InspectionState.ERROR)
      {
        state.endingDraftVisible = false;
        state.endingEndCaption = 'Завершить изменения';
      }
      else
      {
        state.endingEndCaption = isEntryAccounting ? 'Завершить' : 'Завершить осмотр';
        if (inspectionState !== InspectionState.NEW && inspectionState !== InspectionState.CREATE)
        {
          state.endingDraftCaption = 'Сохранить черновик'
        }
      }
    })
    .addCase(buildForEditAsync.rejected, (state, action) => {
    });
});
// #endregion

// #region saveImmediatelyAsync
type SaveImmediatelyArgs = {
  isSaveEditedPiece: boolean;
  isSaveEditedComplaint: boolean;
  isApplyEditedPiece?: boolean;
  isApplyEditedComplaint?: boolean;
};
type SaveImmediatelyResolve = 'saved' | 'savedwithapplypiece' | 'savedwithapplycomplaint' | 'skiped';
export type SaveImmediatelyReject = void;
export const saveImmediatelyAsync = createAsyncThunk<
  SaveImmediatelyResolve,
  SaveImmediatelyArgs,
  {
    state: RootState,
    rejectValue: SaveImmediatelyReject,
  }
>(
  `${SLICE_NAME}/saveImmediatelyAsync`,
  async (args, thunkAPI) => {
    try
    {
      const state = thunkAPI.getState();
      const inspection = JSON.parse(JSON.stringify(state.inspectionEdit.generalEdited)) as Nullable<types.inspection.Inspection>;

      if (inspection !== null)
      {
        if (args.isSaveEditedPiece)
        {
          const pieceEdited = JSON.parse(JSON.stringify(state.inspectionEdit.pieceEdited)) as Nullable<types.inspection.Piece>;
          const expandedDefect = state.inspectionEdit.defectExpandedGuid;

          if (pieceEdited !== null)
          {
            inspection.inspectionDefects.forEach((defect) => {
              if (defect.guid === expandedDefect)
              {
                let isFound = false;
                if (defect.pieces !== null)
                {
                  defect.pieces = defect.pieces.map((piece) => {
                    if (piece.guid === pieceEdited.guid)
                    {
                      isFound = true;
                      return pieceEdited;
                    }
                    return piece;
                  });

                  if (!isFound)
                  {
                    defect.pieces.push(pieceEdited);
                  }
                }
                else
                {
                  defect.pieces = [pieceEdited];
                }
              }
            })
          }
        }

        if (args.isSaveEditedComplaint)
        {
          const complaintEdited = JSON.parse(JSON.stringify(state.inspectionEdit.complaintEdited)) as Nullable<types.inspection.Complaint>;
          const expandedDefect = state.inspectionEdit.defectExpandedGuid;

          if (complaintEdited !== null)
          {
            inspection.inspectionDefects = inspection.inspectionDefects.map((defect) => {
              if (defect.guid === expandedDefect)
              {
                return {
                  ...defect,
                  complaint: complaintEdited,
                };
              }

              return defect;
            });
          }
        }
        
        await Db.saveInspection(inspection);

        if (args.isApplyEditedPiece)
        {
          return 'savedwithapplypiece';
        }

        if (args.isApplyEditedComplaint)
        {
          return 'savedwithapplycomplaint';
        }
        
        return 'saved';
      }

      return 'skiped';
    }
    catch (error) {
      return thunkAPI.rejectWithValue();
    }
  }
);
extraReducers.push((builder) => {
  builder
    .addCase(saveImmediatelyAsync.fulfilled, (state, action) => {
      if (state.generalEdited !== null && (action.payload !== 'skiped'))
      {
        state.generalEdited.__isTrans = false;
        state.generalEdited.status.id = -1;

        const { NEW, CREATE, DRAFT, ERROR } = InspectionState;
        const { NO_TRANS_DRAFT, NO_TRANS_ERROR, INTERNAL, INTERNAL_NO_TRANS_COMPLETED, NO_TRANS_COMPLETED } = InspectionStatusCode;

        if ([NEW, CREATE, DRAFT].includes(state.generalInspectionState))
        {
          state.generalEdited.status.code = NO_TRANS_DRAFT;
        }
        else if (state.generalInspectionState === ERROR)
        {
          state.generalEdited.status.code = NO_TRANS_ERROR;
        }
        else
        {
          if ([INTERNAL, INTERNAL_NO_TRANS_COMPLETED].includes(state.generalEdited.status.code))
          {
            state.generalEdited.status.code = INTERNAL_NO_TRANS_COMPLETED;
          }
          else
          {
            state.generalEdited.status.code = NO_TRANS_COMPLETED;
          }
        }
        state.generalEdited.status.title = 'Не отправлен';

        if (action.payload === 'savedwithapplypiece')
        {
          const pieceEdited = JSON.parse(JSON.stringify(state.pieceEdited)) as Nullable<types.inspection.Piece>;
          const expandedDefect = state.defectExpandedGuid;

          if (pieceEdited !== null)
          {
            state.generalEdited.inspectionDefects.forEach((defect) => {
              if (defect.guid === expandedDefect)
              {
                let isFound = false;
                if (defect.pieces !== null)
                {
                  defect.pieces = defect.pieces.map((piece) => {
                    if (piece.guid === pieceEdited.guid)
                    {
                      isFound = true;
                      return pieceEdited;
                    }
                    return piece;
                  });

                  if (!isFound)
                  {
                    defect.pieces.push(pieceEdited);
                  }
                }
                else
                {
                  defect.pieces = [pieceEdited];
                }
              }
            })
          }
        }

        if (action.payload === 'savedwithapplycomplaint')
        {
          const complaintEdited = JSON.parse(JSON.stringify(state.complaintEdited)) as Nullable<types.inspection.Complaint>;
          const expandedDefect = state.defectExpandedGuid;

          if (complaintEdited !== null)
          {
            state.generalEdited.inspectionDefects = state.generalEdited.inspectionDefects.map((defect) => {
              if (defect.guid === expandedDefect)
              {
                return {
                  ...defect,
                  complaint: complaintEdited,
                };
              }

              return defect;
            });
          }
        }
      }
    })
    .addCase(saveImmediatelyAsync.rejected, (state, action) => {
    });
});
// #endregion

// #region deleteImmediatelyAsync
type DeleteImmediatelyArgs = void;
type DeleteImmediatelyResolve = void;
export type DeleteImmediatelyReject = void;
export const deleteImmediatelyAsync = createAsyncThunk<
  DeleteImmediatelyResolve,
  DeleteImmediatelyArgs,
  {
    state: RootState,
    rejectValue: DeleteImmediatelyReject,
  }
>(
  `${SLICE_NAME}/deleteImmediatelyAsync`,
  async (args, thunkAPI) => {
    try
    {
      const state = thunkAPI.getState();
      const inspection = state.inspectionEdit.generalEdited;

      if (inspection !== null)
      {
        await Db.removeInspection(inspection.guid);
      }
    }
    catch (error) { }
  }
);
// #endregion

// #region changeDefectFavoriteAsync
type ChangeDefectFavoriteArgs = {
  defectId: number;
  isFavorite: boolean;
};
type ChangeDefectFavoriteResolve = {
  generalFavoriteDefects: number[],
};
export type ChangeDefectFavoriteReject = ApiError;
export const changeDefectFavoriteAsync = createAsyncThunk<
  ChangeDefectFavoriteResolve,
  ChangeDefectFavoriteArgs,
  {
    state: RootState,
    rejectValue: ChangeDefectFavoriteReject,
  }
>(
  `${SLICE_NAME}/changeDefectFavoriteAsync`,
  async (args, thunkAPI) => {
    try
    {
      await Api.postRefersDefectFavorite({ defectId: args.defectId, isFavorite: args.isFavorite });

      const state = thunkAPI.getState();

      if (args.isFavorite)
      {
        return {
          generalFavoriteDefects: [...state.inspectionEdit.generalFavoriteDefects, args.defectId]
        }
      }
      else
      {
        return {
          generalFavoriteDefects: state.inspectionEdit.generalFavoriteDefects.filter((item) => item !== args.defectId)
        }
      }
    }
    catch (error)
    {
      return thunkAPI.rejectWithValue(error as ApiError);
    }
  }
);
extraReducers.push((builder) => {
  builder
    .addCase(changeDefectFavoriteAsync.fulfilled, (state, action) => {
      state.generalFavoriteDefects = action.payload.generalFavoriteDefects;
    })
    .addCase(changeDefectFavoriteAsync.rejected, (state, action) => {
    });
});
// #endregion

const generalReducers = {
  generalOpStatusChanged: (state: WritableDraft<InspectionEditState>, action: OpStatusChangedAction) => {
    state.generalOpStatus = action.payload;
  },

  generalOpStatusLabelChanged: (state: WritableDraft<InspectionEditState>, action: OpStatusLabelChangedAction) => {
    state.generalOpStatusLabel = action.payload;
  },

  generalSelectModeOpened: (state: WritableDraft<InspectionEditState>) => {
    state.generalSelectModeOpened = true;
  },

  generalSelectModeClosed: (state: WritableDraft<InspectionEditState>) => {
    state.generalSelectModeOpened = false;
  },
}

const generalSelectors = {
  selectGeneralOpStatus: (state: RootState) => state.inspectionEdit.generalOpStatus,
  selectGeneralOpStatusLabel: (state: RootState) => state.inspectionEdit.generalOpStatusLabel,
  selectGeneralSelectModeOpened: (state: RootState) => state.inspectionEdit.generalSelectModeOpened,

  selectGeneralAccount: (state: RootState) => state.inspectionEdit.generalAccount,

  selectGeneralUserFilialId: (state: RootState) => state.inspectionEdit.generalOriginal?.userFilialId ?? null,

  selectGeneralInspectionState: (state: RootState) => state.inspectionEdit.generalInspectionState,
  selectGeneralInspectionStatusCode: (state: RootState) => state.inspectionEdit.generalEdited?.status.code ?? null,
  selectGeneralIsTransferOnly: (state: RootState) => state.inspectionEdit.generalIsTransferOnly,
  selectGeneralIsChangeToComplaint: (state: RootState) => state.inspectionEdit.generalIsChangeToComplaint,
  selectGeneralIsChangeToComplaintFinance: (state: RootState) => state.inspectionEdit.generalIsChangeToComplaintFinance,
  selectGeneralIsChangeComplaintCosts: (state: RootState) => state.inspectionEdit.generalIsChangeComplaintCosts,
  selectGeneralIsChangeComplaintAll: (state: RootState) => state.inspectionEdit.generalIsChangeComplaintAll,
  selectGeneralIsChangeRequirements: (state: RootState) => state.inspectionEdit.generalIsChangeToComplaint
                                                           ||
                                                           state.inspectionEdit.generalIsChangeToComplaintFinance
                                                           ||
                                                           state.inspectionEdit.generalIsChangeComplaintCosts
                                                           ||
                                                           state.inspectionEdit.generalIsChangeComplaintAll,
  selectGeneralIsGroup: (state: RootState) => state.inspectionEdit.generalIsGroup,
  selectGeneralIsNoTrans: (state: RootState) => state.inspectionEdit.generalIsNoTrans,

  selectGeneralIsEntryAccounting: (state: RootState) => state.inspectionEdit.generalIsEntryAccounting,
  selectGeneralPlaceOfFixationRefer: (state: RootState) => state.inspectionEdit.generalPlaceOfFixationRefer,
  selectGeneralEnterpriseRefer: (state: RootState) => state.inspectionEdit.generalEnterpriseRefer,
  selectGeneralWaysToUseRegectedRefer: (state: RootState) => state.inspectionEdit.generalWaysToUseRegectedRefer,
  selectGeneralTopographyRefer: (state: RootState) => state.inspectionEdit.generalTopographyRefer,
  selectGeneralDefectRefer: (state: RootState) => state.inspectionEdit.generalDefectRefer,
  selectGeneralProductRefer: (state: RootState) => state.inspectionEdit.generalProductRefer,
  selectGeneralUnitRefer: (state: RootState) => state.inspectionEdit.generalUnitRefer,
  selectGeneralPriorityRefer: (state: RootState) => state.inspectionEdit.generalPriorityRefer,
  selectGeneralLocationRefer: (state: RootState) => state.inspectionEdit.generalLocationRefer,
  selectGeneralFavoriteDefects: (state: RootState) => state.inspectionEdit.generalFavoriteDefects,
  selectGeneralIsEditMode: (state: RootState) => state.inspectionEdit.generalOriginal !== null,
  selectGeneralEditedGuid: (state: RootState) => state.inspectionEdit.generalEdited?.guid ?? '',
  selectGeneralEditedNum: (state: RootState) => state.inspectionEdit.generalEdited?.inspectionNum ?? '',
  selectGeneralEditedFromEntryAccounting: (state: RootState) => state.inspectionEdit.generalEdited?.__fromEntryAccounting ?? false,

  selectGeneralMainNavbarText: (state: RootState) => {
    const isEditMode = state.inspectionEdit.generalOriginal !== null;
    const isEntryAccounting = state.inspectionEdit.generalIsEntryAccounting;
    const isGroup = state.inspectionEdit.generalIsGroup;
    const inspectionNum = state.inspectionEdit.generalEdited?.inspectionNum ?? '';
    const account = state.authCommon.account!;

    if (!isEditMode)
    {
      return isEntryAccounting ?
        (account.role === AccountRole.Contractor ? 'Без дефекта' : 'Входной учет')
        :
        'Добавление дефектов';
    }
    else
    {
      if (isGroup)
      {
        return isEntryAccounting ?
          (account.role === AccountRole.Contractor ? 'Без дефекта' : 'Входной учет')
          :
          'Редактирование дефектов';
      }
      else
      {
        return isEntryAccounting ?
          (account.role === AccountRole.Contractor ? `Без дефекта №${inspectionNum}` : `Входной учет №${inspectionNum}`) 
          :
          `Осмотр №${inspectionNum}`;
      } 
    }
  },

  selectGeneralIsDraftSaveVisible: (state: RootState) => {
    const inspectionState = state.inspectionEdit.generalInspectionState;

    return [InspectionState.NEW, InspectionState.CREATE, InspectionState.DRAFT, InspectionState.ERROR].includes(inspectionState);
  },

  selectGeneralIsEquals: createSelector(
    [
      (state: RootState, isComplete: boolean) => ({ state, isComplete })
    ],
    ({ state, isComplete }) => {
      const inspectionState = state.inspectionEdit.generalInspectionState;
      const original = state.inspectionEdit.generalOriginal;
      const edited = state.inspectionEdit.generalEdited;

      let isEquals = true;
      if ([InspectionState.NEW, InspectionState.CREATE].includes(inspectionState))
      {
        isEquals = false;
      }
      else
      {
        if (original === null || edited === null)
        {
          isEquals = false;
        }
        else
        {
          edited.inspectionDefects.forEach((defect) => {
            if ([DefectMode.NEW, DefectMode.EDIT, DefectMode.DEL].includes(defect.mode))
            {
              isEquals = false;
            }
            else
            {
              original.inspectionDefects.forEach((defectOld) => {
                if (defectOld.guid === defect.guid)
                {
                  /**
                   * Проверка совместной инспекции. У нас switch совместной инспекции управляется переменной для 
                   * внутреннего пользования __isJoinInspection. Если она true, то в редактируемой версии все поля
                   * заполнены - акт, дата и документ. Если она false, то поля при сохранении будут очищены, даже
                   * если они сейчас заполнены. Поскольку все поля обязательны при включенном switch-е, то для 
                   * определенных проверок достаточно заполненного акта.
                   * Какие варианты возможны:
                   * 1. В оригинальном дефекте акт заполнен, а в редактируемом переменная __isJoinInspection равна
                   *    false. Это означает, что сначала данные были заполнены, а сейчас мы их очищаем.
                   * 2. В оригинальном дефекте акт не заполнен, а в редактируемом переменная __isJoinInspection равна
                   *    true. Это означает, что сначала данные не были заполнены, а сейчас мы их заполнили.
                   * 3. В оригинальном дефекте акт заполнен и в редактируемом переменная __isJoinInspection равна
                   *    true. Это означает, что сначала данные были заполнены и сейчас мы их не очищаем. А значит нужно
                   *    сравнивать уже сами поля. Если акт изменился, или дата изменилась или есть документы совместной
                   *    инспекции на редактируемом дефекте в состоянии DEL или NEW (а точнее будет и DEL и NEW, т.к.
                   *    мы меняем файл) - значит есть изменения
                   */

                  const oldJoinInspectionAct = Util.nullOrEmptyStringTo(defectOld.actJoinInsp, 'X');
                  const newJoinInspectionAct = Util.nullOrEmptyStringTo(defect.actJoinInsp, 'X');
                  const oldJoinInspectionTime = defectOld.timestampJoinInsp ?? 0;
                  const newJoinInspectionTime = defect.timestampJoinInsp ?? 0;
                  const ACT_JOIN_INSP = types.inspection.DefectFileType.ACT_JOIN_INSP;
                  const DEL = types.inspection.DefectFileMode.DEL;
                  const NEW = types.inspection.DefectFileMode.NEW;
                  const isJoinInspectionFilesChanged = defect.files.some(
                    (file) => file.type === ACT_JOIN_INSP && [DEL, NEW].includes(file.mode)
                  );
                  const isJoinInspectionExpanded = defect.__isJoinInspection;

                  const isJoinInspectionClear = (oldJoinInspectionAct !== 'X' && !isJoinInspectionExpanded);
                  const isJoinInspectionFill = (oldJoinInspectionAct === 'X' && isJoinInspectionExpanded);
                  const isJoinInspectionFieldsChanged = (oldJoinInspectionAct !== newJoinInspectionAct)
                                                        || (oldJoinInspectionTime !== newJoinInspectionTime)
                                                        || isJoinInspectionFilesChanged;
                  const isJoinInspectionChanged = isJoinInspectionClear || isJoinInspectionFill || isJoinInspectionFieldsChanged;

                  const newDefectId = defect.defectId ?? 0;
                  const oldDefectId = defectOld.defectId ?? 0;
                  const newPriorityId = defect.priorityId ?? 0;
                  const oldPriorityId = defectOld.priorityId ?? 0;
                  const newPlaceOfFixationId = defect.placeOfFixationId ?? 0;
                  const oldPlaceOfFixationId = defectOld.placeOfFixationId ?? 0;
                  const newRentalStateOfDelivery = defect.rentalStateOfDelivery ?? 0;
                  const oldRentalStateOfDelivery = defectOld.rentalStateOfDelivery ?? 0;
                  const newDeviationValue = defect.deviationValue ?? 0;
                  const oldDeviationValue = defectOld.deviationValue ?? 0;
                  const newLocationsOfDefect = defect.locationsOfDefect
                    .map((item) => item.id)
                    .sort((a: number, b: number) => a - b)
                    .join(',');
                  const oldLocationsOfDefect = defectOld.locationsOfDefect
                    .map((item) => item.id)
                    .sort((a: number, b: number) => a - b)
                    .join(',');
                  const newProduct = Util.nullOrEmptyStringTo(defect.product, 'X');
                  const oldProduct = Util.nullOrEmptyStringTo(defectOld.product, 'X');
                  const newNote = Util.nullOrEmptyStringTo(defect.note, 'X');
                  const oldNote = Util.nullOrEmptyStringTo(defectOld.note, 'X');
                  const newExternalNumber = Util.nullOrEmptyStringTo(defect.numExternal, 'X');
                  const oldExternalNumber = Util.nullOrEmptyStringTo(defectOld.numExternal, 'X');
                  const newViolatedRequirements = Util.nullOrEmptyStringTo(defect.violatedRequirements, 'X');
                  const oldViolatedRequirements = Util.nullOrEmptyStringTo(defectOld.violatedRequirements, 'X');
                  const newWayToUseRegectedId = defect.wayToUseRegectedId ?? 0;
                  const oldWayToUseRegectedId = defectOld.wayToUseRegectedId ?? 0;
                  if (
                    (newDefectId !== oldDefectId)
                    ||
                    (newPriorityId !== oldPriorityId)
                    ||
                    (newPlaceOfFixationId !== oldPlaceOfFixationId)
                    ||
                    (newRentalStateOfDelivery !== oldRentalStateOfDelivery)
                    ||
                    (newDeviationValue !== oldDeviationValue)
                    ||
                    (newLocationsOfDefect !== oldLocationsOfDefect)
                    ||
                    (newProduct !== oldProduct)
                    ||
                    (newNote !== oldNote)
                    ||
                    (newExternalNumber !== oldExternalNumber)
                    ||
                    (defect.isViolationOfRequirements !== defectOld.isViolationOfRequirements)
                    ||
                    (newViolatedRequirements !== oldViolatedRequirements)
                    ||
                    (defect.isPossibilityUsingRejected !== defectOld.isPossibilityUsingRejected)
                    ||
                    (newWayToUseRegectedId !== oldWayToUseRegectedId)
                    ||
                    isJoinInspectionChanged
                  ) 
                  {
                    isEquals = false;
                  }
                }
              });

              if (isEquals)
              {
                (defect.pieces ?? []).forEach((piece) => {
                  if (piece.mode !== PieceMode.EXIST)
                  {
                    isEquals = false;
                  }
                });

                if (isEquals)
                {
                  if (defect.complaint !== null && defect.complaint.mode !== ComplaintMode.EXIST)
                  {
                    isEquals = false;
                  }
                }

                if (isEquals)
                {
                  if (isComplete !== original.__isCompleted)
                  {
                    isEquals = false;
                  }
                }
              }
            }
          });
        }
      }

      return isEquals;
    }
  )
}

// #endregion

// #region ДЕФЕКТЫ

// #region addDefectAsync
type AddDefectAsyncArgs = void;
type AddDefectAsyncResolve = {
  defect: types.inspection.Defect;
  defectExpandedGuid: string;
};
export type AddDefectAsyncReject = {
  error: string;
  defectExpandedGuid: string;
}
export const addDefectAsync = createAsyncThunk<
  AddDefectAsyncResolve,
  AddDefectAsyncArgs,
  {
    state: RootState,
    rejectValue: AddDefectAsyncReject,
  }
>(
  `${SLICE_NAME}/addDefectAsync`,
  async (args, thunkAPI) => {
    const defects = thunkAPI.getState().inspectionEdit.generalEdited!.inspectionDefects;
    const inspectionState = thunkAPI.getState().inspectionEdit.generalInspectionState;
    const account = thunkAPI.getState().authCommon.account!;

    for (let i = 0; i < defects.length; i++)
    {
      const defect = defects[i];
      const pieces = defect.pieces || [];
      const emptyRequired: string[] = [];

      if (defect.defectId === null || defect.defectId === -1)
      {
        emptyRequired.push('Вид дефекта');
      }

      if (defect.isViolationOfRequirements && defect.violatedRequirements === null)
      {
        emptyRequired.push('Укажите какое требование нарушено');
      }

      if (defect.isPossibilityUsingRejected && defect.wayToUseRegectedId === null)
      {
        emptyRequired.push('Способ использования забракованного проката');
      }

      if (defect.__isJoinInspection && defect.actJoinInsp === null)
      {
        emptyRequired.push('Номер акта СИ');
      }

      if (defect.__isJoinInspection && defect.timestampJoinInsp === null)
      {
        emptyRequired.push('Дата СИ');
      }

      {
        const { DEL } = types.inspection.DefectFileMode;
        const { ACT_JOIN_INSP } = types.inspection.DefectFileType;
        const joinInspFiles = defect.files.filter(
          (item) => item.mode !== DEL && item.type === ACT_JOIN_INSP
        )
        if (defect.__isJoinInspection && joinInspFiles.length === 0)
        {
          emptyRequired.push('PDF-файл СИ');
        }
      }

      if (pieces.length === 0)
      {
        emptyRequired.push('Информация о продукции');
      }
      /**
       * mobi-1541 Для сварного шва (id = 99) место фиксации обязательно для клиента, клиента-админа и стпк
       */
      if (
        [AccountRole.Client, AccountRole.ClientAdmin, AccountRole.Stpk].includes(account.role)
        &&
        defect.defectId === 99
        &&
        defect.placeOfFixationId === null
      )
      {
        emptyRequired.push('Место фиксации');
      }

      const defectRefer = thunkAPI.getState().inspectionEdit.generalDefectRefer;
      const defectReferItem = defectRefer.find((item) => item.id === defect.defectId) ?? null;
      const defectConfigs = defectReferItem?.configs ?? [];

      /**
       * Создаем блок, чтобы скрыть в нем используемые именно для этой проверки
       * переменные, чтобы не называть их длинными именами.
       * Это проверка на значение поля "Прокат в состоянии поставки" и мы называем
       * переменную, которая отвечает за видимость этого поля, просто isVisible,
       * вместо isFieldRentalStateOfDeliveryVisible, чтобы название этой переменной
       * было понятно в общем контексте и не конфликтовало с другими переменными.
       */
      {
        const configValue = defectConfigs.find((item) => item.code === 'RENTAL_STATE_OF_DELIVERY_FILL')?.value ?? '0';
        const defectType = defect.defectType;

        const notContractor = Util.isNotContractor(account.role);
        const isQualityOrService = Util.isQualityOrService(defectType);
        const isVisible = notContractor && isQualityOrService && configValue === '1';
        const isRequired = Util.isInspectionNotCompleted(inspectionState);
        
        if (isVisible && isRequired && defect.rentalStateOfDelivery === null)
        {
          emptyRequired.push('Прокат в состоянии поставки');
        }
      }

      {
        const configValue = defectConfigs.find((item) => item.code === 'FIELD_DEVIATION_VALUE')?.value ?? '0';
        const defectType = defect.defectType;

        const notContractor = Util.isNotContractor(account.role);
        const isQualityOrService = Util.isQualityOrService(defectType);
        const isVisible = notContractor && isQualityOrService && configValue === '1';
        const isRequired = Util.isInspectionNotCompleted(inspectionState);
        
        if (isVisible && isRequired && defect.deviationValue === null)
        {
          emptyRequired.push('Величина отклонения');
        }
      }

      {
        const configValue = defectConfigs.find((item) => item.code === 'FIELD_LOCATION_OF_DEFECT')?.value ?? '0';
        const defectType = defect.defectType;

        const notContractor = Util.isNotContractor(account.role);
        const isQualityOrService = Util.isQualityOrService(defectType);
        const isVisible = notContractor && isQualityOrService && configValue === '1';
        const isRequired = Util.isInspectionNotCompleted(inspectionState);
        
        if (isVisible && isRequired && defect.locationsOfDefect.length === 0)
        {
          emptyRequired.push('Расположение дефекта');
        }
      }

      if (emptyRequired.length > 0)
      {
        return thunkAPI.rejectWithValue({
          error: `Заполните обязательные поля: ${emptyRequired.join(', ')}`,
          defectExpandedGuid: defect.guid,
        })
      }

      const state = thunkAPI.getState();

      for (let j = 0; j < pieces.length; j++)
      {
        const pieceError = getPieceErrorMessage(
          pieces[j],
          pieces,
          state.inspectionEdit.generalIsEntryAccounting,
          state.authCommon.account!.isWireRod,
          true,
          state.authCommon.account!,
          defect.defectType,
          defectReferItem,
          inspectionState,
          state.inspectionEdit.generalOriginal !== null && inspectionState !== InspectionState.CREATE,
        );

        if (pieceError !== '')
        {
          return thunkAPI.rejectWithValue({
            error: `Ошибки в позиции: ${pieceError}`,
            defectExpandedGuid: defect.guid,
          })
        }
      }

    }

    const guid = Util.guid(true);

    return {
      defect: {
        guid: guid,
        timestampDefect: Date.now(),
        defectType: null,
        defectId: null,
        defectTitle: null,
        priorityId: null,
        priorityTitle: null,
        placeOfFixationId: null,
        placeOfFixationTitle: null,
        product: null,
        note: null,
        numExternal: null,
        isViolationOfRequirements: false,
        violatedRequirements: null,
        isPossibilityUsingRejected: false,
        wayToUseRegectedId: null,
        wayToUseRegectedTitle: null,
        rentalStateOfDelivery: null,
        deviationValue: null,
        locationsOfDefect: [],
        cntMarriage: 0,
        actJoinInsp: null,
        timestampJoinInsp: null,
        files: [],
        isSend: false,
        pieces: [],
        complaint: null,
        mode: types.inspection.DefectMode.NEW,
        __isJoinInspection: false,
        __deviationValueStr: '',
      },
      defectExpandedGuid: guid,
    }
  }
);
extraReducers.push((builder) => {
  builder
    .addCase(addDefectAsync.fulfilled, (state, action) => {
      state.generalEdited!.inspectionDefects.push(action.payload.defect);
      state.defectExpandedGuid = action.payload.defectExpandedGuid;
    })
    .addCase(addDefectAsync.rejected, (state, action) => {
      if (action.payload)
      {
        state.defectExpandedGuid = action.payload?.defectExpandedGuid;
      }
    });
});
// #endregion

// #region gotoComplaintAsync
type GotoComplaintAsyncArgs = void;
type GotoComplaintAsyncResolve = 'gotolist' | 'gotoitem' | 'none';
export type GotoComplaintAsyncReject = void;
export const gotoComplaintAsync = createAsyncThunk<
  GotoComplaintAsyncResolve,
  GotoComplaintAsyncArgs,
  {
    state: RootState,
    rejectValue: GotoComplaintAsyncReject,
  }
>(
  `${SLICE_NAME}/gotoComplaintAsync`,
  async (args, thunkAPI) => {
    const state = thunkAPI.getState();
    const defects = state.inspectionEdit.generalEdited!.inspectionDefects;

    if (defects.length > 0)
    {
      if (state.inspectionEdit.generalIsGroup)
      {
        return 'gotolist';
      }
      else
      {
        return 'gotoitem';
      }
    }
    else
    {
      return 'none';
    }
  }
);
// #endregion

// #region checkDefectsFilledAsync
type CheckDefectsFilledAsyncArgs = {
  modeControl: ModeControlDefect;
};
type CheckDefectsFilledAsyncResolve = void;
export type CheckDefectsFilledAsyncReject = {
  error: string;
  defectExpandedGuid: string;
};
export const checkDefectsFilledAsync = createAsyncThunk<
  CheckDefectsFilledAsyncResolve,
  CheckDefectsFilledAsyncArgs,
  {
    state: RootState,
    rejectValue: CheckDefectsFilledAsyncReject,
  }
>(
  `${SLICE_NAME}/checkDefectsFilledAsync`,
  async (args, thunkAPI) => {
    const state = thunkAPI.getState();
    const defects = state.inspectionEdit.generalEdited!.inspectionDefects;
    const inspectionState = state.inspectionEdit.generalInspectionState;
    const account = state.authCommon.account!;
    const defectRefer = state.inspectionEdit.generalDefectRefer;

    if (defects.length > 0)
    {
      for (let i = 0; i < defects.length; i++)
      {
        const errorResult = getDefectErrorMessage(
          defects[i],
          args.modeControl,
          state.inspectionEdit.generalIsEntryAccounting,
          account,
          inspectionState,
          state.inspectionEdit.generalEdited!.status.code,
          defectRefer,
          state.inspectionEdit.generalOriginal !== null && inspectionState !== InspectionState.CREATE,
          state.inspectionEdit.generalIsChangeComplaintCosts,
          state.inspectionEdit.generalIsChangeToComplaint,
          state.inspectionEdit.generalIsChangeToComplaintFinance,
          state.inspectionEdit.generalIsChangeComplaintAll,
        );

        if (errorResult !== '')
        {
          return thunkAPI.rejectWithValue({
            error: errorResult,
            defectExpandedGuid: defects[i].guid,
          });
        }
      }
    }
    else
    {
      return thunkAPI.rejectWithValue({
        error: 'Не заполнены дефекты',
        defectExpandedGuid: '',
      });
    }
  }
);
extraReducers.push((builder) => {
  builder
    .addCase(checkDefectsFilledAsync.rejected, (state, action) => {
      if (action.payload)
      {
        state.defectExpandedGuid = action.payload.defectExpandedGuid;
      }
    });
});
// #endregion

// #region changeDefectAsync
type ChangeDefectAsyncArgs = Nullable<number>;
type ChangeDefectAsyncResolve = {
  defectId: Nullable<number>;
  defectType: Nullable<types.inspection.DefectType>;
  defectTitle: Nullable<string>;
  isNullableRentalStateOfDelivery: boolean;
  isNullableDeviationValue: boolean;
  isNullableLocationsOfDefect: boolean;
  isOnlyClaim: boolean;
  isOnlyRemark: boolean;
};
export type ChangeDefectAsyncReject = void;
export const changeDefectAsync = createAsyncThunk<
  ChangeDefectAsyncResolve,
  ChangeDefectAsyncArgs,
  {
    state: RootState,
    rejectValue: ChangeDefectAsyncReject,
  }
>(
  `${SLICE_NAME}/changeDefectAsync`,
  async (defectId, thunkAPI) => {
    const result: ChangeDefectAsyncResolve = {
      defectId,
      defectType: null,
      defectTitle: null,
      isNullableRentalStateOfDelivery: false,
      isNullableDeviationValue: false,
      isNullableLocationsOfDefect: false,
      isOnlyClaim: false,
      isOnlyRemark: false,
    };

    const state = thunkAPI.getState();

    const account = state.authCommon.account!;
    const defectRefer = state.inspectionEdit.generalDefectRefer;
    const defect = defectRefer.find((item) => item.id === defectId) ?? null;
    const RENTAL_STATE_OF_DELIVERY_FILL = defect?.configs?.find((item) => item.code === "RENTAL_STATE_OF_DELIVERY_FILL")?.value ?? "";
    const FIELD_DEVIATION_VALUE = defect?.configs?.find((item) => item.code === "FIELD_DEVIATION_VALUE")?.value ?? "";
    const FIELD_LOCATION_OF_DEFECT = defect?.configs?.find((item) => item.code === "FIELD_LOCATION_OF_DEFECT")?.value ?? "";

    const markOnlyClaim = defect?.configs?.find((conf) => conf.code === 'ONLY_CLAIM')?.value ?? '0';
    const markOnlyRemark = defect?.configs?.find((conf) => conf.code === 'ONLY_REMARK')?.value ?? '0';
    const markProhibitionOfRemarks = defect?.configs?.find((conf) => conf.code === 'PROHIBITION_OF_REMARKS')?.value ?? '0';

    result.isOnlyClaim = markOnlyClaim === '1' || markProhibitionOfRemarks === '1';
    result.isOnlyRemark = markOnlyRemark === '1';

    result.defectType = defect?.defectType ?? null;
    result.defectTitle = defect?.title ?? null;

    const notContractor = Util.isNotContractor(account.role);
    const isQualityOrService = Util.isQualityOrService(result.defectType);

    result.isNullableRentalStateOfDelivery = !(notContractor && isQualityOrService && RENTAL_STATE_OF_DELIVERY_FILL === '1');
    result.isNullableDeviationValue = !(notContractor && isQualityOrService && FIELD_DEVIATION_VALUE === '1');
    result.isNullableLocationsOfDefect = !(notContractor && isQualityOrService && FIELD_LOCATION_OF_DEFECT === '1');

    return result;
  }
);
extraReducers.push((builder) => {
  builder
    .addCase(changeDefectAsync.fulfilled, (state, action) => {
      const gE = state.generalEdited;

      if (gE !== null)
      {
        const eD = gE.inspectionDefects.find((defect) => defect.guid === state.defectExpandedGuid) ?? null;

        if (eD !== null)
        {
          state.defectChangedManually = true;

          eD.defectId = action.payload.defectId;
          eD.defectType = action.payload.defectType;
          eD.defectTitle = action.payload.defectTitle;

          switch (eD.defectType ?? DefectType.QUALITY)
          {
            case DefectType.QUALITY:
            case DefectType.SERVICE:
              // TODO: Удалить данные, которые не должны заполняться для данного типа дефекта
              break;
            case DefectType.DIRTY_WAGONS:
              break;
            case DefectType.TERM:
              break;
          }

          if (action.payload.isNullableRentalStateOfDelivery)
          {
            eD.rentalStateOfDelivery = null;
          }

          if (action.payload.isNullableDeviationValue)
          {
            eD.deviationValue = null;
            eD.__deviationValueStr = '';
          }

          if (action.payload.isNullableLocationsOfDefect)
          {
            eD.locationsOfDefect = [];
          }

          if (action.payload.isOnlyClaim && eD.complaint !== null)
          {
            eD.complaint.typeComplaint = ComplaintType.COMPLAINT;
            eD.complaint.otherReq = null;
          }

          if (action.payload.isOnlyRemark && eD.complaint !== null)
          {
            eD.complaint.typeComplaint = ComplaintType.REMARK;
            eD.complaint.isToScrap = null;
            eD.complaint.scrapPrice = null;
            eD.complaint.isEvaluateRolledMetal = null;
            eD.complaint.requirementRolledMetall = null;
            eD.complaint.sumReqRolledMetall = null;
            eD.complaint.isToOffsetCosts = null;
            eD.complaint.amountOfCompensation = null;
            eD.files = eD.files
              .filter((file) => file.type !== DefectFileType.ADDITIONAL_COSTS || file.mode !== DefectFileMode.NEW)
              .map((file) => file.type === DefectFileType.ADDITIONAL_COSTS ? { ...file, mode: DefectFileMode.DEL } : file);
            eD.complaint.isReturnOfRolledMetal = null;
            eD.complaint.isSale = null;
            eD.complaint.sale = null;
            eD.complaint.isRepresentative = null;
            eD.complaint.isProvideResponse = null;
            eD.complaint.isIdentifyDangerous = null;
            eD.complaint.isAnalizProblem = null;
            eD.complaint.is8d = null;
            eD.complaint.isAddMetall = null;
            eD.complaint.isInformDeadline = null;
            eD.complaint.isCompensatePenalty = null;
            eD.complaint.penalty = null;
          }

          if (eD.mode !== types.inspection.DefectMode.NEW)
          {
            eD.mode = types.inspection.DefectMode.EDIT;
          }
        }
      }
    });
});
// #endregion

const defectReducers = {
  defectAdded: (state: WritableDraft<InspectionEditState>) => {
    const gE = state.generalEdited;

    if (gE !== null)
    {
      gE.inspectionDefects.push({
        guid: Util.guid(true),
        timestampDefect: Date.now(),
        defectId: null,
        defectTitle: null,
        priorityId: null,
        priorityTitle: null,
        placeOfFixationId: null,
        placeOfFixationTitle: null,
        product: null,
        note: null,
        numExternal: null,
        isViolationOfRequirements: false,
        violatedRequirements: null,
        isPossibilityUsingRejected: false,
        wayToUseRegectedId: null,
        wayToUseRegectedTitle: null,
        rentalStateOfDelivery: null,
        deviationValue: null,
        locationsOfDefect: [],
        cntMarriage: 0,
        isSend: false,
        actJoinInsp: null,
        timestampJoinInsp: null,
        defectType: null,
        files: [],
        pieces: [],
        complaint: null,
        mode: types.inspection.DefectMode.NEW,
        __isJoinInspection: false,
        __deviationValueStr: '',
      });
    }
  },

  defectDeleted: (state: WritableDraft<InspectionEditState>, action: StringChangedAction) => {
    const gE = state.generalEdited;

    if (gE !== null)
    {
      const defectForDelete = gE.inspectionDefects.find((item) => item.guid === action.payload)!;

      if (defectForDelete.mode === types.inspection.DefectMode.NEW)
      {
        gE.inspectionDefects = gE.inspectionDefects.filter((defect) => defect.guid !== action.payload);
      }
      else
      {
        gE.inspectionDefects = gE.inspectionDefects.map((defect) => defect.guid === action.payload ? { ...defect, mode: types.inspection.DefectMode.DEL } : defect);
      }

      const defectList = gE.inspectionDefects.filter((item) => item.mode !== types.inspection.DefectMode.DEL);

      if (defectList.length === 1)
      {
        state.defectExpandedGuid = defectList[0].guid;
      }
    }
  },

  defectExpandedGuidChanged: (state: WritableDraft<InspectionEditState>, action: StringChangedAction) => {
    state.defectExpandedGuid = action.payload;
  },

  defectPlaceOfFixationChanged: (state: WritableDraft<InspectionEditState>, action: NumberChangedAction) => {
    const gE = state.generalEdited;

    if (gE !== null)
    {
      const eD = gE.inspectionDefects.find((defect) => defect.guid === state.defectExpandedGuid) ?? null;

      if (eD !== null)
      {
        eD.placeOfFixationId = action.payload;
        eD.placeOfFixationTitle = state.generalPlaceOfFixationRefer.find((item) => item.id === action.payload)!.title;

        if (eD.placeOfFixationId === 1)
        {
          eD.rentalStateOfDelivery = 1;
        }

        if (eD.placeOfFixationId === 3)
        {
          eD.rentalStateOfDelivery = 2;
        }

        if (![1, 3].includes(eD.placeOfFixationId))
        {
          eD.rentalStateOfDelivery = null;
        }

        if (eD.placeOfFixationId !== 3)
        {
          eD.product = '';
        }

        if (eD.mode !== types.inspection.DefectMode.NEW)
        {
          eD.mode = types.inspection.DefectMode.EDIT;
        }
      }
    }
  },

  defectRentalStateOfDeliveryChanged: (state: WritableDraft<InspectionEditState>, action: NumberChangedAction) => {
    const gE = state.generalEdited;

    if (gE !== null)
    {
      const eD = gE.inspectionDefects.find((defect) => defect.guid === state.defectExpandedGuid) ?? null;

      if (eD !== null)
      {
        eD.rentalStateOfDelivery = action.payload;

        if (eD.mode !== types.inspection.DefectMode.NEW)
        {
          eD.mode = types.inspection.DefectMode.EDIT;
        }
      }
    }
  },

  defectDeviationValueChanged: (state: WritableDraft<InspectionEditState>, action: StringChangedAction) => {
    const gE = state.generalEdited;

    if (gE !== null)
    {
      const eD = gE.inspectionDefects.find((defect) => defect.guid === state.defectExpandedGuid) ?? null;

      if (eD !== null)
      {
        eD.__deviationValueStr = action.payload;
        eD.deviationValue = action.payload === '' ? null : parseFloat(action.payload);

        if (eD.mode !== types.inspection.DefectMode.NEW)
        {
          eD.mode = types.inspection.DefectMode.EDIT;
        }
      }
    }
  },

  defectLocationsOfDefectChanged: (state: WritableDraft<InspectionEditState>, action: LocationsArrayChangedAction) => {
    const gE = state.generalEdited;

    if (gE !== null)
    {
      const eD = gE.inspectionDefects.find((defect) => defect.guid === state.defectExpandedGuid) ?? null;

      if (eD !== null)
      {
        eD.locationsOfDefect = action.payload;

        if (eD.mode !== types.inspection.DefectMode.NEW)
        {
          eD.mode = types.inspection.DefectMode.EDIT;
        }
      }
    }
  },

  defectPriorityChanged: (state: WritableDraft<InspectionEditState>, action: NumberChangedAction) => {
    const gE = state.generalEdited;

    if (gE !== null)
    {
      const eD = gE.inspectionDefects.find((defect) => defect.guid === state.defectExpandedGuid) ?? null;

      if (eD !== null)
      {
        eD.priorityId = action.payload;
        eD.priorityTitle = state.generalPriorityRefer.find((item) => item.id === action.payload)!.title;

        if (eD.mode !== types.inspection.DefectMode.NEW)
        {
          eD.mode = types.inspection.DefectMode.EDIT;
        }
      }
    }
  },

  defectSelectTypeOpened: (state: WritableDraft<InspectionEditState>) => {
    state.defectSelectTypeOpened = true;
  },

  defectSelectTypeClosed: (state: WritableDraft<InspectionEditState>) => {
    state.defectSelectTypeOpened = false;
  },

  defectDefectTypeChanged: (state: WritableDraft<InspectionEditState>, action: DefectTypeChangedAction) => {
    const gE = state.generalEdited;

    if (gE !== null)
    {
      const eD = gE.inspectionDefects.find((defect) => defect.guid === state.defectExpandedGuid) ?? null;

      if (eD !== null)
      {
        const dtFrom = action.payload.from;
        const dtTo = action.payload.to;

        // Если dtFrom = null, значит дефект еще не определен и действие вызвало окно
        // выбора типа дефекта. Поэтому нам нужно установить defectType. В противном
        // случае текущий дефект существует и действие вызывается при смене дефекта,
        // поэтому тип дефекта устанавливается в действии смены дефекта
        if (dtFrom === null)
        {
          eD.defectType = dtTo;
        }

        switch (dtTo)
        {
          case DefectType.QUALITY:
          case DefectType.SERVICE:
            if (eD.pieces !== null)
            {
              eD.pieces = eD.pieces.map((piece) => {
                if (piece.mode !== PieceMode.DEL)
                {
                  piece.isPiece = Preferences.preferredMethodUEEnter === 'piece';
                  piece.carriage = null;
                  piece.carriageQcs = [];
                  piece.isAllPoses = false;
                  piece.metalDelivered = null;
                  piece.specificationNum = null;
                  piece.specificationPoses = [];

                  piece.mode = piece.mode === PieceMode.EXIST ? PieceMode.EDIT : piece.mode;
                }

                return piece;
              });
            }

            if (eD.complaint !== null)
            {
              if (dtFrom === DefectType.TERM)
              {
                // Копируем в дополниетльные затраты штрафную неустойку, т.к. это одно и то же
                eD.complaint.isToOffsetCosts = eD.complaint.isCompensatePenalty;
                eD.complaint.amountOfCompensation = eD.complaint.penalty;
              }

              eD.complaint.isAddMetall = null;
              eD.complaint.isCompensatePenalty = null;
              eD.complaint.penalty = null;
              eD.complaint.isInformDeadline = null;

              eD.complaint.mode = eD.complaint.mode === ComplaintMode.EXIST ? ComplaintMode.EDIT : eD.complaint.mode;
            }
            break;
          case DefectType.DIRTY_WAGONS:
            if (eD.pieces !== null)
            {
              eD.placeOfFixationId = 1;
              eD.placeOfFixationTitle = 'Входной контроль';
              eD.product = null;

              eD.pieces = eD.pieces.map((piece) => {
                if (piece.mode !== PieceMode.DEL)
                {
                  piece.fields = [];
                  piece.groupAtt = null;
                  piece.heat = null;
                  piece.isPiece = false;
                  piece.isSmc = false;
                  piece.marriageShare = null;
                  piece.piece = null;
                  piece.pieceNumId = null;
                  piece.qcNum = null;
                  piece.qmetData = {
                    actualWeight: null,
                    group: null,
                    qcNum: null,
                    qcState: QCState.NONE,
                  };
                  piece.qmetId = null;
                  piece.topography = {
                    defectDistanceFromEdge: null,
                    defectSize: null,
                    defectStep: null,
                    defectTpgIdDistance: null,
                    defectTpgIdSize: null,
                    defectTpgIdStep: null,
                    defectTpgTitleDistance: null,
                    defectTpgTitleSize: null,
                    defectTpgTitleStep: null,
                  };
                  piece.weight = null;
                  piece.weightAfterProcessing = null;
                  piece.weightBeforeProcessing = null;
                  piece.isAllPoses = false;
                  piece.metalDelivered = null;
                  piece.specificationNum = null;
                  piece.specificationPoses = [];
                  piece.carriage = null;
                  piece.carriageQcs = [{ isControl: true, qcNum: '', qcState: QCState.NONE }];

                  piece.mode = piece.mode === PieceMode.EXIST ? PieceMode.EDIT : piece.mode;
                }

                return piece;
              });
            }

            if (eD.complaint !== null)
            {
              if (dtFrom === DefectType.TERM)
              {
                // Копируем в дополниетльные затраты штрафную неустойку, т.к. это одно и то же
                eD.complaint.isToOffsetCosts = eD.complaint.isCompensatePenalty;
                eD.complaint.amountOfCompensation = eD.complaint.penalty;
              }

              eD.complaint.isAddMetall = null;
              eD.complaint.isCompensatePenalty = null;
              eD.complaint.penalty = null;
              eD.complaint.isInformDeadline = null;

              eD.complaint.mode = eD.complaint.mode === ComplaintMode.EXIST ? ComplaintMode.EDIT : eD.complaint.mode;
            }
            break;
          case DefectType.TERM:
            if (eD.pieces !== null)
            {
              eD.pieces = eD.pieces.map((piece) => {
                if (piece.mode !== PieceMode.DEL)
                {
                  piece.fields = [];
                  piece.groupAtt = null;
                  piece.heat = null;
                  piece.isPiece = false;
                  piece.isSmc = false;
                  piece.marriageShare = null;
                  piece.piece = null;
                  piece.pieceNumId = null;
                  piece.qcNum = null;
                  piece.qmetData = {
                    actualWeight: null,
                    group: null,
                    qcNum: null,
                    qcState: QCState.NONE,
                  };
                  piece.qmetId = null;
                  piece.topography = {
                    defectDistanceFromEdge: null,
                    defectSize: null,
                    defectStep: null,
                    defectTpgIdDistance: null,
                    defectTpgIdSize: null,
                    defectTpgIdStep: null,
                    defectTpgTitleDistance: null,
                    defectTpgTitleSize: null,
                    defectTpgTitleStep: null,
                  };
                  piece.weight = null;
                  piece.weightAfterProcessing = null;
                  piece.weightBeforeProcessing = null;
                  piece.isAllPoses = false;
                  piece.metalDelivered = null;
                  piece.specificationNum = null;
                  piece.specificationPoses = [{ 
                    isControl: true,
                    pos: null,
                    metalDelivered: null,
                    volumeDeliveredTime: null,
                    unit: {
                      id: 2,
                      title: 'T',
                      isInteger: false,
                    },
                    daysOverdue: null,
                    __volumeDeliveredTime: '',
                  }];
                  piece.carriage = null;
                  piece.carriageQcs = [];

                  piece.mode = piece.mode === PieceMode.EXIST ? PieceMode.EDIT : piece.mode;
                }

                return piece;
              });
            }

            if (eD.complaint !== null)
            {
              if (dtFrom === DefectType.TERM)
              {
                // Копируем в дополниетльные затраты штрафную неустойку, т.к. это одно и то же
                eD.complaint.isToOffsetCosts = eD.complaint.isCompensatePenalty;
                eD.complaint.amountOfCompensation = eD.complaint.penalty;
              }

              eD.complaint.isAddMetall = null;
              eD.complaint.isCompensatePenalty = null;
              eD.complaint.penalty = null;
              eD.complaint.isInformDeadline = null;

              eD.complaint.mode = eD.complaint.mode === ComplaintMode.EXIST ? ComplaintMode.EDIT : eD.complaint.mode;
            }
            break;
        }

        eD.mode = eD.mode === DefectMode.EXIST ? DefectMode.EDIT : eD.mode;
      }
    }
  },

  defectProductChanged: (state: WritableDraft<InspectionEditState>, action: StringChangedAction) => {
    const gE = state.generalEdited;

    if (gE !== null)
    {
      const eD = gE.inspectionDefects.find((defect) => defect.guid === state.defectExpandedGuid) ?? null;

      if (eD !== null)
      {
        eD.product = action.payload !== '' ? action.payload : null;

        if (eD.mode !== types.inspection.DefectMode.NEW)
        {
          eD.mode = types.inspection.DefectMode.EDIT;
        }
      }
    }
  },

  defectDefectChangedManuallyReset: (state: WritableDraft<InspectionEditState>) => {
    state.defectChangedManually = false
  },

  defectNoteChanged: (state: WritableDraft<InspectionEditState>, action: StringChangedAction) => {
    const gE = state.generalEdited;

    if (gE !== null)
    {
      const eD = gE.inspectionDefects.find((defect) => defect.guid === state.defectExpandedGuid) ?? null;

      if (eD !== null)
      {
        eD.note = action.payload !== '' ? action.payload : null;

        if (eD.mode !== types.inspection.DefectMode.NEW)
        {
          eD.mode = types.inspection.DefectMode.EDIT;
        }
      }
    }
  },

  defectExternalNumberChanged: (state: WritableDraft<InspectionEditState>, action: StringChangedAction) => {
    const gE = state.generalEdited;

    if (gE !== null)
    {
      const eD = gE.inspectionDefects.find((defect) => defect.guid === state.defectExpandedGuid) ?? null;

      if (eD !== null)
      {
        eD.numExternal = action.payload !== '' ? action.payload : null;

        if (eD.mode !== types.inspection.DefectMode.NEW)
        {
          eD.mode = types.inspection.DefectMode.EDIT;
        }
      }
    }
  },

  defectIsViolationOfRequirementsChanged: (state: WritableDraft<InspectionEditState>, action: BooleanChangedAction) => {
    const gE = state.generalEdited;

    if (gE !== null)
    {
      const eD = gE.inspectionDefects.find((defect) => defect.guid === state.defectExpandedGuid) ?? null;

      if (eD !== null)
      {
        eD.isViolationOfRequirements = action.payload;

        if (!action.payload)
        {
          eD.violatedRequirements = null;
        }

        if (eD.mode !== types.inspection.DefectMode.NEW)
        {
          eD.mode = types.inspection.DefectMode.EDIT;
        }
      }
    }
  },

  defectViolatedRequirementsChanged: (state: WritableDraft<InspectionEditState>, action: StringChangedAction) => {
    const gE = state.generalEdited;

    if (gE !== null)
    {
      const eD = gE.inspectionDefects.find((defect) => defect.guid === state.defectExpandedGuid) ?? null;

      if (eD !== null)
      {
        eD.violatedRequirements = action.payload === '' ? null : action.payload;

        if (eD.mode !== types.inspection.DefectMode.NEW)
        {
          eD.mode = types.inspection.DefectMode.EDIT;
        }
      }
    }
  },

  defectPossibilityUsingRejectedChanged: (state: WritableDraft<InspectionEditState>, action: BooleanChangedAction) => {
    const gE = state.generalEdited;

    if (gE !== null)
    {
      const eD = gE.inspectionDefects.find((defect) => defect.guid === state.defectExpandedGuid) ?? null;

      if (eD !== null)
      {
        eD.isPossibilityUsingRejected = action.payload;

        if (!action.payload)
        {
          eD.wayToUseRegectedId = null;
          eD.wayToUseRegectedTitle = null;
        }

        if (eD.mode !== types.inspection.DefectMode.NEW)
        {
          eD.mode = types.inspection.DefectMode.EDIT;
        }
      }
    }
  },

  defectWayToUseRegectedChanged: (state: WritableDraft<InspectionEditState>, action: NumberChangedAction) => {
    const gE = state.generalEdited;

    if (gE !== null)
    {
      const eD = gE.inspectionDefects.find((defect) => defect.guid === state.defectExpandedGuid) ?? null;

      if (eD !== null)
      {
        eD.wayToUseRegectedId = action.payload;
        eD.wayToUseRegectedTitle = state.generalWaysToUseRegectedRefer.find((item) => item.id === action.payload)!.title;

        if (eD.mode !== types.inspection.DefectMode.NEW)
        {
          eD.mode = types.inspection.DefectMode.EDIT;
        }
      }
    }
  },

  defectJoinInspectionChanged: (state: WritableDraft<InspectionEditState>, action: BooleanChangedAction) => {
    const gE = state.generalEdited;

    if (gE !== null)
    {
      const eD = gE.inspectionDefects.find((defect) => defect.guid === state.defectExpandedGuid) ?? null;

      if (eD !== null)
      {
        eD.__isJoinInspection = action.payload;

        if (eD.mode !== types.inspection.DefectMode.NEW)
        {
          eD.mode = types.inspection.DefectMode.EDIT;
        }
      }
    }
  },

  defectJoinInspectionActChanged: (state: WritableDraft<InspectionEditState>, action: StringChangedAction) => {
    const gE = state.generalEdited;

    if (gE !== null)
    {
      const eD = gE.inspectionDefects.find((defect) => defect.guid === state.defectExpandedGuid) ?? null;

      if (eD !== null)
      {
        eD.actJoinInsp = action.payload === '' ? null : action.payload;

        if (eD.mode !== types.inspection.DefectMode.NEW)
        {
          eD.mode = types.inspection.DefectMode.EDIT;
        }
      }
    }
  },

  defectJoinInspectionTimestampChanged: (state: WritableDraft<InspectionEditState>, action: NullableNumberChangedAction) => {
    const gE = state.generalEdited;

    if (gE !== null)
    {
      const eD = gE.inspectionDefects.find((defect) => defect.guid === state.defectExpandedGuid) ?? null;

      if (eD !== null)
      {
        eD.timestampJoinInsp = action.payload;

        if (eD.mode !== types.inspection.DefectMode.NEW)
        {
          eD.mode = types.inspection.DefectMode.EDIT;
        }
      }
    }
  },

  defectDocumentDeleted: (state: WritableDraft<InspectionEditState>, action: StringChangedAction) => {
    const gE = state.generalEdited;

    if (gE !== null)
    {
      const eD = gE.inspectionDefects.find((defect) => defect.guid === state.defectExpandedGuid) ?? null;

      if (eD !== null)
      {
        eD.files = eD.files
          .map((file) => {
            if (file.guid !== action.payload)
            {
              return file;
            }

            if (file.mode === types.inspection.DefectFileMode.EXIST)
            {
              return { ...file, mode: types.inspection.PieceFileMode.DEL };
            }
            
            return null;
          })
          .filter((file) => file !== null) as types.inspection.DefectFile[];

        if (eD.mode !== types.inspection.DefectMode.NEW)
        {
          eD.mode = types.inspection.DefectMode.EDIT;
        }
      }
    }
  },

  defectDocumentAdded: (state: WritableDraft<InspectionEditState>, action: DefectDocumentAddedAction) => {
    const gE = state.generalEdited;

    if (gE !== null)
    {
      switch (action.payload.from)
      {
        case 'defect':
          {
            const eD = gE.inspectionDefects.find((defect) => defect.guid === state.defectExpandedGuid) ?? null;

            if (eD !== null)
            {
              eD.files.push(action.payload.file);

              if (eD.mode !== types.inspection.DefectMode.NEW)
              {
                eD.mode = types.inspection.DefectMode.EDIT;
              }
            }
          }
          break;
        case 'complaint':
          {
            const eD = gE.inspectionDefects.find((item) => item.guid === state.complaintDefectGuid) ?? null;

            if (eD !== null)
            {
              eD.files.push(action.payload.file);

              if (eD.mode !== types.inspection.DefectMode.NEW)
              {
                eD.mode = types.inspection.DefectMode.EDIT;
              }
            }
          }
      }
    }
  },

}

const defectSelectors = {
  selectDefectExpandedGuid: (state: Pick<RootState, "inspectionEdit">) => state.inspectionEdit.defectExpandedGuid,

  selectDefectChangedManually: (state: Pick<RootState, "inspectionEdit">) => state.inspectionEdit.defectChangedManually,

  selectDefectSelectTypeOpened: (state: Pick<RootState, "inspectionEdit">) => state.inspectionEdit.defectSelectTypeOpened,

  selectDefectFirstGuid: (state: Pick<RootState, "inspectionEdit">) => {
    const allDefects = state.inspectionEdit.generalEdited?.inspectionDefects ?? [];
    
    return allDefects.length > 0 ? allDefects[0].guid : null;
  },

  selectDefectAllGuidsList: (state: Pick<RootState, "inspectionEdit">) => {
    const allDefects = state.inspectionEdit.generalEdited?.inspectionDefects ?? [];
    
    return allDefects.filter((defect) => defect.mode !== types.inspection.DefectMode.DEL).map((defect) => defect.guid).join(';');
  },

  selectDefectAllCount: (state: Pick<RootState, "inspectionEdit">) => {
    const allDefects = state.inspectionEdit.generalEdited?.inspectionDefects ?? [];
    
    return allDefects.filter((defect) => defect.mode !== types.inspection.DefectMode.DEL).length;
  },

  selectDefectComplaintsIsOk: (state: Pick<RootState, "inspectionEdit">) => {
    const allDefects = state.inspectionEdit.generalEdited?.inspectionDefects ?? [];
    
    return allDefects.every((defect) =>
      defect.complaint !== null && defect.complaint.typeComplaint !== null
    )
  },

  selectDefectType: createSelector(
    [
      (state: Pick<RootState, "inspectionEdit">, defectGuid: string) => ({ state, defectGuid })
    ],
    ({ state, defectGuid }) => {
      const allDefects = state.inspectionEdit.generalEdited?.inspectionDefects ?? [];

      return allDefects.find((defect) => defect.guid === defectGuid)?.defectType ?? null;
    }
  ),

  selectDefectTitle: createSelector(
    [
      (state: Pick<RootState, "inspectionEdit">, defectGuid: string) => ({ state, defectGuid })
    ],
    ({ state, defectGuid }) => {
      const allDefects = state.inspectionEdit.generalEdited?.inspectionDefects ?? [];

      return allDefects.find((defect) => defect.guid === defectGuid)?.defectTitle ?? null;
    }
  ),

  selectDefectDateTime: createSelector(
    [
      (state: Pick<RootState, "inspectionEdit">, defectGuid: string) => ({ state, defectGuid })
    ],
    ({ state, defectGuid }) => {
      const allDefects = state.inspectionEdit.generalEdited?.inspectionDefects ?? [];

      return allDefects.find((defect) => defect.guid === defectGuid)?.timestampDefect ?? null;
    }
  ),

  selectDefectQmetIds: createSelector(
    [
      (state: Pick<RootState, "inspectionEdit">, defectGuid: string) => ({ state, defectGuid })
    ],
    ({ state, defectGuid }) => {
      const defect = (state.inspectionEdit.generalEdited?.inspectionDefects ?? [])
        .find((item) => item.guid === defectGuid) ?? null;

      if (defect === null)
      {
        return '';
      }

      const qmetIds: string[] = [];

      (defect.pieces || []).forEach((piece) => {
        if (!piece.isPiece && piece.qmetId != null && piece.qmetId > 0)
        {
          qmetIds.push(`${piece.qmetId}`);
        }
      })

      return qmetIds.join(', ');
    }
  ),

  selectDefectPieceIds: createSelector(
    [
      (state: Pick<RootState, "inspectionEdit">, defectGuid: string) => ({ state, defectGuid })
    ],
    ({ state, defectGuid }) => {
      const account = state.inspectionEdit.generalAccount!;
      const isKolpino = (account.configs.find((item) => item.code === 'KOLPINO')?.value ?? '0') === '1';

      if (isKolpino)
      {
        return '';
      }

      const defect = (state.inspectionEdit.generalEdited?.inspectionDefects ?? [])
        .find((item) => item.guid === defectGuid) ?? null;

      if (defect === null)
      {
        return '';
      }

      const pieceIds: string[] = [];

      (defect.pieces || []).forEach((piece) => {
        if (piece.isPiece && piece.heat != null && piece.piece != null && piece.heat !== '' && piece.piece.trim() !== '' && (piece.groupAtt === null || piece.groupAtt === ''))
        {
          pieceIds.push(`${piece.heat}/${piece.piece}`);
        }
      })

      return pieceIds.join(', ');
    }
  ),

  selectDefectGroupAttIds: createSelector(
    [
      (state: Pick<RootState, "inspectionEdit">, defectGuid: string) => ({ state, defectGuid })
    ],
    ({ state, defectGuid }) => {
      const account = state.inspectionEdit.generalAccount!;
      const isKolpino = (account.configs.find((item) => item.code === 'KOLPINO')?.value ?? '0') === '1';

      if (isKolpino)
      {
        return '';
      }

      const defect = (state.inspectionEdit.generalEdited?.inspectionDefects ?? [])
        .find((item) => item.guid === defectGuid) ?? null;

      if (defect === null)
      {
        return '';
      }

      const groupAttIds: string[] = [];

      (defect.pieces || []).forEach((piece) => {
        if (piece.isPiece && piece.groupAtt != null && piece.groupAtt !== '')
        {
          if (groupAttIds.length === 0)
          {
            if (piece.heat != null && piece.heat !== '') 
            {
              groupAttIds.push(`${piece.heat}/`);
            }
            if (piece.groupAtt != null && piece.groupAtt !== '' && piece.qcNum != null && piece.qcNum !== '')
            {
              groupAttIds.push(`${piece.groupAtt}/${piece.qcNum}`);
            }
            else
            {
              if (piece.groupAtt != null && piece.groupAtt !== '')
              {
                groupAttIds.push(`${piece.groupAtt}`);
              }
              else
              {
                groupAttIds.push(`${piece.qcNum}`);
              }
            }
          }
        }
      })

      return groupAttIds.join(', ');
    }
  ),

  selectDefectKolpinoMechanicalEngineeringParts: createSelector(
    [
      (state: Pick<RootState, "inspectionEdit">, defectGuid: string) => ({ state, defectGuid })
    ],
    ({ state, defectGuid }) => {
      const account = state.inspectionEdit.generalAccount!;
      const isKolpino = (account.configs.find((item) => item.code === 'KOLPINO')?.value ?? '0') === '1';

      if (!isKolpino)
      {
        return '';
      }

      const defect = (state.inspectionEdit.generalEdited?.inspectionDefects ?? [])
        .find((item) => item.guid === defectGuid) ?? null;

      if (defect === null)
      {
        return '';
      }

      const mechanicalEngineeringParts: string[] = [];

      (defect.pieces || [])
        .filter((piece) => piece.isPiece && piece.productTypeKolpinoId === ProductTypeKolpino.MECHANICAL_ENGINEERING_PART)
        .forEach((piece) => {
          mechanicalEngineeringParts.push(getPieceTitle(piece, defect.defectType, isKolpino) ?? '');
        });

      return mechanicalEngineeringParts.join(', ');
    }
  ),

  selectDefectKolpinoProcessedSheets: createSelector(
    [
      (state: Pick<RootState, "inspectionEdit">, defectGuid: string) => ({ state, defectGuid })
    ],
    ({ state, defectGuid }) => {
      const account = state.inspectionEdit.generalAccount!;
      const isKolpino = (account.configs.find((item) => item.code === 'KOLPINO')?.value ?? '0') === '1';

      if (!isKolpino)
      {
        return '';
      }

      const defect = (state.inspectionEdit.generalEdited?.inspectionDefects ?? [])
        .find((item) => item.guid === defectGuid) ?? null;

      if (defect === null)
      {
        return '';
      }

      const processedSheets: string[] = [];

      (defect.pieces || [])
        .filter((piece) => piece.isPiece && piece.productTypeKolpinoId === ProductTypeKolpino.PROCESSED_SHEET)
        .forEach((piece) => {
          processedSheets.push(getPieceTitle(piece, defect.defectType, isKolpino) ?? '');
        });

      return processedSheets.join(', ');
    }
  ),

  selectDefectKolpinoUnprocessedSheets: createSelector(
    [
      (state: Pick<RootState, "inspectionEdit">, defectGuid: string) => ({ state, defectGuid })
    ],
    ({ state, defectGuid }) => {
      const account = state.inspectionEdit.generalAccount!;
      const isKolpino = (account.configs.find((item) => item.code === 'KOLPINO')?.value ?? '0') === '1';

      if (!isKolpino)
      {
        return '';
      }

      const defect = (state.inspectionEdit.generalEdited?.inspectionDefects ?? [])
        .find((item) => item.guid === defectGuid) ?? null;

      if (defect === null)
      {
        return '';
      }

      const unprocessedSheets: string[] = [];

      (defect.pieces || [])
        .filter((piece) => piece.isPiece && piece.productTypeKolpinoId === ProductTypeKolpino.UNPROCESSED_SHEET)
        .forEach((piece) => {
          unprocessedSheets.push(getPieceTitle(piece, defect.defectType, isKolpino) ?? '');
        });

      return unprocessedSheets.join(', ');
    }
  ),

  selectDefectPlaceOfFixationId: createSelector(
    [
      (state: Pick<RootState, "inspectionEdit">, defectGuid: string) => ({ state, defectGuid })
    ],
    ({ state, defectGuid }) => {
      const allDefects = state.inspectionEdit.generalEdited?.inspectionDefects ?? [];

      return allDefects.find((defect) => defect.guid === defectGuid)?.placeOfFixationId ?? null;
    }
  ),

  selectDefectRentalStateOfDelivery: createSelector(
    [
      (state: Pick<RootState, "inspectionEdit">, defectGuid: string) => ({ state, defectGuid })
    ],
    ({ state, defectGuid }) => {
      const allDefects = state.inspectionEdit.generalEdited?.inspectionDefects ?? [];

      return allDefects.find((defect) => defect.guid === defectGuid)?.rentalStateOfDelivery ?? null;
    }
  ),

  selectDefectDeviationValue: createSelector(
    [
      (state: Pick<RootState, "inspectionEdit">, defectGuid: string) => ({ state, defectGuid })
    ],
    ({ state, defectGuid }) => {
      const allDefects = state.inspectionEdit.generalEdited?.inspectionDefects ?? [];

      return allDefects.find((defect) => defect.guid === defectGuid)?.__deviationValueStr ?? null;
    }
  ),

  selectDefectLocationsOfDefect: createSelector(
    [
      (state: Pick<RootState, "inspectionEdit">, defectGuid: string) => ({ state, defectGuid })
    ],
    ({ state, defectGuid }) => {
      const allDefects = state.inspectionEdit.generalEdited?.inspectionDefects ?? [];

      return allDefects.find((defect) => defect.guid === defectGuid)?.locationsOfDefect ?? [];
    }
  ),

  selectDefectPriorityId: createSelector(
    [
      (state: Pick<RootState, "inspectionEdit">, defectGuid: string) => ({ state, defectGuid })
    ],
    ({ state, defectGuid }) => {
      const allDefects = state.inspectionEdit.generalEdited?.inspectionDefects ?? [];

      return allDefects.find((defect) => defect.guid === defectGuid)?.priorityId ?? null;
    }
  ),

  selectDefectProduct: createSelector(
    [
      (state: Pick<RootState, "inspectionEdit">, defectGuid: string) => ({ state, defectGuid })
    ],
    ({ state, defectGuid }) => {
      const allDefects = state.inspectionEdit.generalEdited?.inspectionDefects ?? [];

      return allDefects.find((defect) => defect.guid === defectGuid)?.product ?? null;
    }
  ),

  selectDefectDefectConfigs: createSelector(
    [
      (state: Pick<RootState, "inspectionEdit">, defectGuid: string) => ({ state, defectGuid })
    ],
    ({ state, defectGuid }) => {
      const allDefects = state.inspectionEdit.generalEdited?.inspectionDefects ?? [];
      const defectId = allDefects.find((defect) => defect.guid === defectGuid)?.defectId ?? null;

      if (defectId !== null)
      {
        const defectReferElement = state.inspectionEdit.generalDefectRefer.find((item) => item.id === defectId) ?? null;

        return defectReferElement?.configs ?? [];
      }

      return [];
    }
  ),

  selectDefectDefectId: createSelector(
    [
      (state: Pick<RootState, "inspectionEdit">, defectGuid: string) => ({ state, defectGuid })
    ],
    ({ state, defectGuid }) => {
      const allDefects = state.inspectionEdit.generalEdited?.inspectionDefects ?? [];

      return allDefects.find((defect) => defect.guid === defectGuid)?.defectId ?? null;
    }
  ),

  selectDefectDefectType: createSelector(
    [
      (state: Pick<RootState, "inspectionEdit">, defectGuid: string) => ({ state, defectGuid })
    ],
    ({ state, defectGuid }) => {
      const allDefects = state.inspectionEdit.generalEdited?.inspectionDefects ?? [];

      return allDefects.find((defect) => defect.guid === defectGuid)?.defectType ?? null;
    }
  ),

  selectDefectExternalNumber: createSelector(
    [
      (state: Pick<RootState, "inspectionEdit">, defectGuid: string) => ({ state, defectGuid })
    ],
    ({ state, defectGuid }) => {
      const allDefects = state.inspectionEdit.generalEdited?.inspectionDefects ?? [];

      return allDefects.find((defect) => defect.guid === defectGuid)?.numExternal ?? null;
    }
  ),

  selectDefectNote: createSelector(
    [
      (state: Pick<RootState, "inspectionEdit">, defectGuid: string) => ({ state, defectGuid })
    ],
    ({ state, defectGuid }) => {
      const allDefects = state.inspectionEdit.generalEdited?.inspectionDefects ?? [];

      return allDefects.find((defect) => defect.guid === defectGuid)?.note ?? null;
    }
  ),

  selectDefectIsViolationOfRequirements: createSelector(
    [
      (state: Pick<RootState, "inspectionEdit">, defectGuid: string) => ({ state, defectGuid })
    ],
    ({ state, defectGuid }) => {
      const allDefects = state.inspectionEdit.generalEdited?.inspectionDefects ?? [];

      return allDefects.find((defect) => defect.guid === defectGuid)?.isViolationOfRequirements ?? false;
    }
  ),

  selectDefectViolatedRequirements: createSelector(
    [
      (state: Pick<RootState, "inspectionEdit">, defectGuid: string) => ({ state, defectGuid })
    ],
    ({ state, defectGuid }) => {
      const allDefects = state.inspectionEdit.generalEdited?.inspectionDefects ?? [];

      return allDefects.find((defect) => defect.guid === defectGuid)?.violatedRequirements ?? null;
    }
  ),

  selectDefectIsPossibilityUsingRejected: createSelector(
    [
      (state: Pick<RootState, "inspectionEdit">, defectGuid: string) => ({ state, defectGuid })
    ],
    ({ state, defectGuid }) => {
      const allDefects = state.inspectionEdit.generalEdited?.inspectionDefects ?? [];

      return allDefects.find((defect) => defect.guid === defectGuid)?.isPossibilityUsingRejected ?? false;
    }
  ),

  selectDefectWayToUseRegectedId: createSelector(
    [
      (state: Pick<RootState, "inspectionEdit">, defectGuid: string) => ({ state, defectGuid })
    ],
    ({ state, defectGuid }) => {
      const allDefects = state.inspectionEdit.generalEdited?.inspectionDefects ?? [];

      return allDefects.find((defect) => defect.guid === defectGuid)?.wayToUseRegectedId ?? null;
    }
  ),

  selectDefectIsJoinInspectionExpanded: createSelector(
    [
      (state: Pick<RootState, "inspectionEdit">, defectGuid: string) => ({ state, defectGuid })
    ],
    ({ state, defectGuid }) => {
      const allDefects = state.inspectionEdit.generalEdited?.inspectionDefects ?? [];

      return allDefects.find((defect) => defect.guid === defectGuid)?.__isJoinInspection ?? false;
    }
  ),

  selectDefectJoinInspectionAct: createSelector(
    [
      (state: Pick<RootState, "inspectionEdit">, defectGuid: string) => ({ state, defectGuid })
    ],
    ({ state, defectGuid }) => {
      const allDefects = state.inspectionEdit.generalEdited?.inspectionDefects ?? [];

      return allDefects.find((defect) => defect.guid === defectGuid)?.actJoinInsp ?? null;
    }
  ),

  selectDefectJoinInspectionTimestamp: createSelector(
    [
      (state: Pick<RootState, "inspectionEdit">, defectGuid: string) => ({ state, defectGuid })
    ],
    ({ state, defectGuid }) => {
      const allDefects = state.inspectionEdit.generalEdited?.inspectionDefects ?? [];

      return allDefects.find((defect) => defect.guid === defectGuid)?.timestampJoinInsp ?? null;
    }
  ),

  selectDefectIsOk: createSelector(
    [
      (state: RootState, defectGuid: string) => ({ state, defectGuid })
    ],
    ({ state, defectGuid }) => {
      const allDefects = state.inspectionEdit.generalEdited?.inspectionDefects ?? [];
      const defect = allDefects.find((defect) => defect.guid === defectGuid) ?? null
      const defectRefer = state.inspectionEdit.generalDefectRefer;
      const inspectionState = state.inspectionEdit.generalInspectionState;
      const account = state.authCommon.account!;

      if (defect === null)
      {
        return true;
      }

      const defectType = defect.defectType;
      const notContractor = Util.isNotContractor(account.role);
      const isQualityOrService = Util.isQualityOrService(defectType);
      const defectReferItem = defectRefer.find((item) => item.id === defect.defectId) ?? null;
      const defectConfigs = defectReferItem?.configs ?? [];

      const defectPass= defect.defectId !== null && defect.defectId !== -1;
      const piecesPass = (defect.pieces || []).length > 0;
      const violationPass = !defect.isViolationOfRequirements || defect.violatedRequirements !== null;
      const rejectPass = !defect.isPossibilityUsingRejected || defect.wayToUseRegectedId !== null;
      const joinInspectionPass = !defect.__isJoinInspection ||
        (
          defect.actJoinInsp !== null
          &&
          defect.timestampJoinInsp !== null
          &&
          defect.files.some((file) => file.type === types.inspection.DefectFileType.ACT_JOIN_INSP && file.mode !== types.inspection.DefectFileMode.DEL)
        );
      
      let isRentalStateOfDeliveryPass = true;
      {
        const configValue = defectConfigs.find((item) => item.code === 'RENTAL_STATE_OF_DELIVERY_FILL')?.value ?? '0';
        const isVisible = notContractor && isQualityOrService && configValue === '1';
        const isRequired = Util.isInspectionNotCompleted(inspectionState);
        
        if (isVisible && isRequired && defect.rentalStateOfDelivery === null)
        {
          isRentalStateOfDeliveryPass = false;
        }
      }

      let isDeviationValuePass = true;
      {
        const configValue = defectConfigs.find((item) => item.code === 'FIELD_DEVIATION_VALUE')?.value ?? '0';
        const isVisible = notContractor && isQualityOrService && configValue === '1';
        const isRequired = Util.isInspectionNotCompleted(inspectionState);
        
        if (isVisible && isRequired && defect.deviationValue === null)
        {
          isDeviationValuePass = false;
        }
      }

      let isLocationsOfDefectPass = true;
      {
        const configValue = defectConfigs.find((item) => item.code === 'FIELD_LOCATION_OF_DEFECT')?.value ?? '0';
        const isVisible = notContractor && isQualityOrService && configValue === '1';
        const isRequired = Util.isInspectionNotCompleted(inspectionState);
        
        if (isVisible && isRequired && defect.locationsOfDefect.length === 0)
        {
          isLocationsOfDefectPass = false;
        }
      }

      const piecesCorrectPass = (defect.pieces || []).every((piece) => 
        getPieceErrorMessage(
          piece,
          defect.pieces || [],
          state.inspectionEdit.generalIsEntryAccounting,
          state.authCommon.account!.isWireRod,
          false,
          state.authCommon.account!,
          defect.defectType,
          defectReferItem,
          state.inspectionEdit.generalInspectionState,
          state.inspectionEdit.generalOriginal !== null && state.inspectionEdit.generalInspectionState !== InspectionState.CREATE,
        ) === ''
      )

      return [
        defectPass,
        piecesPass,
        piecesCorrectPass,
        violationPass,
        rejectPass,
        joinInspectionPass,
        isRentalStateOfDeliveryPass,
        isDeviationValuePass,
        isLocationsOfDefectPass
      ].every((item) => item);
    }
  ),

  selectDefectComplaintType: createSelector(
    [
      (state: Pick<RootState, "inspectionEdit">, defectGuid: string) => ({ state, defectGuid })
    ],
    ({ state, defectGuid }) => {
      const defect = (state.inspectionEdit.generalEdited?.inspectionDefects ?? [])
        .find((item) => item.guid === defectGuid) ?? null;

      if (defect === null || defect.complaint === null || defect.complaint.typeComplaint === null)
      {
        return null;
      }

      return defect.complaint.typeComplaint;
    }
  ),

  selectDefectComplaintMode: createSelector(
    [
      (state: Pick<RootState, "inspectionEdit">, defectGuid: string) => ({ state, defectGuid })
    ],
    ({ state, defectGuid }) => {
      const defect = (state.inspectionEdit.generalEdited?.inspectionDefects ?? [])
        .find((item) => item.guid === defectGuid) ?? null;

      if (defect === null || defect.complaint === null || defect.complaint.typeComplaint === null)
      {
        return null;
      }

      return defect.complaint.mode;
    }
  ),

  selectDefectComplaintTypeStr: createSelector(
    [
      (state: Pick<RootState, "inspectionEdit">, defectGuid: string) => ({ state, defectGuid })
    ],
    ({ state, defectGuid }) => {
      const defect = (state.inspectionEdit.generalEdited?.inspectionDefects ?? [])
        .find((item) => item.guid === defectGuid) ?? null;

      if (defect === null || defect.complaint === null || defect.complaint.typeComplaint === null)
      {
        return null;
      }

      return defect.complaint.typeComplaint === ComplaintType.COMPLAINT ? 'Рекламация': 'Замечание'
    }
  ),

  selectDefectControlButtons: (state: RootState):
    Record<
      'adddefect' | 'addcomplaint' | 'gotocomplaint' | 'gotocomplaints' | 'next',
      { visible: boolean, label: string }
    >=> {

    const IS_NEW = InspectionState.NEW;
    const IS_DRAFT = InspectionState.DRAFT;
    const IS_CREATE = InspectionState.CREATE;
    
    const isEntryAccounting = state.inspectionEdit.generalIsEntryAccounting;
    const inspectionState = state.inspectionEdit.generalInspectionState;
    const isGroup = state.inspectionEdit.generalIsGroup;
    const isExistComplaint = (state.inspectionEdit.generalEdited?.inspectionDefects ?? []).some((defect) => defect.complaint !== null);
    const account = state.authCommon.account!;

    const result = {
      adddefect: {
        visible: true,
        label: 'Добавить дефект'
      },
      addcomplaint: {
        visible: false,
        label: 'Добавить требование'
      },
      gotocomplaint: {
        visible: false,
        label: 'Перейти к требованию'
      },
      gotocomplaints: {
        visible: false,
        label: 'Перейти к требованиям'
      },
      next: {
        visible: true,
        label: 'Далее'
      },
    };

    if (isEntryAccounting)
    {
      result.adddefect.visible = false;
      result.next.label = [IS_NEW, IS_DRAFT, IS_CREATE].includes(inspectionState) ? 'Завершить' : 'Изменить';
    }
    else
    {
      if (!isGroup)
      {
			  result.adddefect.visible = false;
			  if (account.role === AccountRole.ClientAdmin)
        {
				  if (isExistComplaint)
          {
            result.gotocomplaint.visible = true;
				  }
          else
          {
            result.addcomplaint.visible = true;
				  }
			  }
        else if (account.role === AccountRole.Stpk)
        {
				  result.gotocomplaint.visible = true;
			  }
		  }
      else if (inspectionState === InspectionState.ERROR)
      {
			  if ((account.role === AccountRole.ClientAdmin && isExistComplaint) || account.role === AccountRole.Stpk)
        {
				  result.gotocomplaint.visible = true;
			  }
		  }
		  if (account.role === AccountRole.Client || account.role === AccountRole.Contractor || !isGroup || inspectionState === InspectionState.ERROR)
      {
        result.next.label = [IS_NEW, IS_DRAFT, IS_CREATE].includes(inspectionState) ? 'Завершить осмотр' : 'Завершить изменения';
		  }
      else
      {
        result.gotocomplaints.visible = true;
        result.next.visible = false;
      }
    }

    return result;
  },

  selectDefectControlButtonsV2: (state: RootState):
    Record<
      'adddefect' | 'addcomplaint' | 'gotocomplaint' | 'next',
      { visible: boolean, label: string, action: string }
    >=> {

    const IS_NEW = InspectionState.NEW;
    const IS_DRAFT = InspectionState.DRAFT;
    const IS_CREATE = InspectionState.CREATE;
    
    const isEntryAccounting = state.inspectionEdit.generalIsEntryAccounting;
    const inspectionState = state.inspectionEdit.generalInspectionState;
    const inspection = state.inspectionEdit.generalEdited;
    const isGroup = state.inspectionEdit.generalIsGroup;
    const isExistComplaint = (state.inspectionEdit.generalEdited?.inspectionDefects ?? []).some((defect) => defect.complaint !== null);
    const isChangeToComplaint = state.inspectionEdit.generalIsChangeToComplaint;
    const isChangeToComplaintFinance = state.inspectionEdit.generalIsChangeToComplaintFinance;
    const isChangeComplaintCosts = state.inspectionEdit.generalIsChangeComplaintCosts;
    const isChangeComplaintAll = state.inspectionEdit.generalIsChangeComplaintAll;

    const account = state.authCommon.account!;

    const result = {
      adddefect: {
        visible: true,
        label: 'Добавить дефект',
        action: 'adddefect'
      },
      addcomplaint: {
        visible: false,
        label: 'Добавить требование',
        action: 'addcomplaint',
      },
      gotocomplaint: {
        visible: false,
        label: 'Перейти к требованию',
        action: '',
      },
      next: {
        visible: true,
        label: 'Далее',
        action: '',
      },
    };

    if (isEntryAccounting)
    {
      result.adddefect.visible = false;
      result.next.label = [IS_NEW, IS_DRAFT, IS_CREATE].includes(inspectionState) ? 'Завершить' : 'Изменить';
    }
    else
    {
      if (!isGroup)
      {
			  result.adddefect.visible = false;
			  if (account.role === AccountRole.ClientAdmin)
        {
				  if (isExistComplaint)
          {
            result.gotocomplaint.visible = true;
				  }
          else
          {
            result.addcomplaint.visible = true;
				  }
			  }
        else if (account.role === AccountRole.Stpk)
        {
				  result.gotocomplaint.visible = true;
			  }
		  }
      else if (inspectionState === InspectionState.ERROR)
      {
        result.adddefect.visible = false;
			  if ((account.role === AccountRole.ClientAdmin && isExistComplaint) || account.role === AccountRole.Stpk)
        {
				  result.gotocomplaint.visible = true;
			  }
		  }
		  if (account.role === AccountRole.Client || account.role === AccountRole.Contractor || !isGroup || inspectionState === InspectionState.ERROR)
      {
        result.next.label = [IS_NEW, IS_DRAFT, IS_CREATE].includes(inspectionState) ? 'Завершить осмотр' : 'Завершить изменения';
		  }
    }

    if (isGroup)
    {
      result.gotocomplaint.action = 'goto_complaintlist';
    }
    else
    {
      if (isChangeToComplaint)
      {
        result.gotocomplaint.action = 'goto_complaintitem_changetocomplaint';
      }
      else if (isChangeToComplaintFinance)
      {
        result.gotocomplaint.action = 'goto_complaintitem_changetocomplaintfinance';
      }
      else if (isChangeComplaintCosts)
      {
        result.gotocomplaint.action = 'goto_complaintitem_changecomplaintcosts';
      }
      else if (isChangeComplaintAll)
      {
        result.gotocomplaint.action = 'goto_complaintitem_changecomplaintall';
      }
      else if (
        inspectionState === InspectionState.EDIT
        &&
        [InspectionStatusCode.INTERNAL, InspectionStatusCode.INTERNAL_NO_TRANS_COMPLETED].includes(inspection!.status.code))
      {
        result.gotocomplaint.action = 'goto_complaintitem_edit';
      }
      else
      {
        result.gotocomplaint.action = 'goto_complaintitem_view';
      }
    }
    
    if (
      [AccountRole.Client, AccountRole.Contractor].includes(account.role)
      ||
      isEntryAccounting
      ||
      !isGroup
      ||
      inspectionState === InspectionState.ERROR
    )
    {
      result.next.action = 'complete';
    }
    else
    {
      result.next.action = 'goto_complaintlist';
    }

    return result;
  },

  selectDefectIsFilledError: (state: Pick<RootState, "inspectionEdit">) => {
    const allDefects = state.inspectionEdit.generalEdited?.inspectionDefects ?? [];
    const isEntryAccounting = state.inspectionEdit.generalIsEntryAccounting;
    
    return allDefects.some((defect) => {
      return !isEntryAccounting && (defect.defectTitle === null || defect.defectTitle === '')
    })
  },

  selectDefectFiles: createSelector(
    [
      (state: Pick<RootState, "inspectionEdit">, defectGuid: string) => ({ state, defectGuid })
    ],
    ({ state, defectGuid }) => {
      const defect = (state.inspectionEdit.generalEdited?.inspectionDefects ?? [])
        .find((item) => item.guid === defectGuid) ?? null;

      if (defect === null)
      {
        return null;
      }

      return defect.files;
    }
  ),
}

// #endregion

// #region ПОЗИЦИИ ПРОДУКЦИИ

const isPiecesEquals = (
  defectType: Nullable<types.inspection.DefectType>,
  edited: types.inspection.Piece,
  original:types.inspection.Piece,
  account: types.auth.Account
): boolean => {
  let isEquals = true;

  const { nullOrEmptyStringTo, nullOrZeroNumberTo } = Util;
  const { QUALITY, SERVICE, DIRTY_WAGONS, TERM } = types.inspection.DefectType;

  const isKolpino = (account.configs.find((item) => item.code === 'KOLPINO')?.value ?? '0') === '1';

  if (edited.mode === types.inspection.PieceMode.NEW)
  {
    isEquals = false;
  }
  else
  {
    const eIsAllPoses = edited.isAllPoses;
    const oIsAllPoses = original.isAllPoses;

    if (edited.isPiece !== original.isPiece)
    {
      isEquals = false;
    }
    else
    {
      if (edited.isPiece)
      {
        const eHeat = nullOrEmptyStringTo(edited.heat, 'X');
        const oHeat = nullOrEmptyStringTo(original.heat, 'X');
        const ePiece = nullOrEmptyStringTo(edited.piece, 'X');
        const oPiece = nullOrEmptyStringTo(original.piece, 'X');
        const eGroupAtt = nullOrEmptyStringTo(edited.groupAtt, 'X');
        const oGroupAtt = nullOrEmptyStringTo(original.groupAtt, 'X');
        const eQcNum = nullOrEmptyStringTo(edited.qcNum, 'X');
        const oQcNum = nullOrEmptyStringTo(original.qcNum, 'X');
        const eDrawNum = nullOrEmptyStringTo(edited.drawNum, 'X');
        const oDrawNum = nullOrEmptyStringTo(original.drawNum, 'X');
        const eKolpinoPT = nullOrEmptyStringTo(`${edited.productTypeKolpinoId ?? ''}`, 'X');
        const oKolpinoPT = nullOrEmptyStringTo(`${original.productTypeKolpinoId ?? ''}`, 'X');

        if (isKolpino)
        {
          if (eKolpinoPT !== oKolpinoPT)
          {
            isEquals = false;
          }
          else
          {
            if ((eDrawNum !== oDrawNum) || (eHeat !== oHeat) || (ePiece !== oPiece) || (eGroupAtt !== oGroupAtt) || (eQcNum !== oQcNum))
            {
              isEquals = false;
            }
          }
        }
        else
        {
          if (account.isWireRod)
          {
            if ((eHeat !== oHeat) || (ePiece !== oPiece) || (eGroupAtt !== oGroupAtt) || (eQcNum !== oQcNum))
            {
              isEquals = false;
            }
          }
          else
          {
            if ((eHeat !== oHeat) || (ePiece !== oPiece))
            {
              isEquals = false;
            }
          }
        }
      }
      else
      {
        const eCarriage = nullOrEmptyStringTo(edited.carriage, 'X');
        const oCarriage = nullOrEmptyStringTo(original.carriage, 'X');
        const eSpecificationNum = nullOrEmptyStringTo(edited.specificationNum, 'X');
        const oSpecificationNum = nullOrEmptyStringTo(original.specificationNum, 'X');
        const eMetalDelivered = edited.metalDelivered;
        const oMetalDelivered = original.metalDelivered;
        const eQmetId = nullOrZeroNumberTo(edited.qmetId, 0);
        const oQmetId = nullOrZeroNumberTo(original.qmetId, 0);

        switch (defectType)
        {
          case DIRTY_WAGONS:
            if (eCarriage !== oCarriage)
            {
              isEquals = false;
            }
            break;
          case TERM:
            if (eSpecificationNum !== oSpecificationNum)
            {
              isEquals = false;
            }
            if (isEquals)
            {
              if (eIsAllPoses !== oIsAllPoses)
              {
                isEquals = false;
              }
            }
            if (isEquals && eIsAllPoses && oIsAllPoses)
            {
              if (
                (eMetalDelivered !== null && oMetalDelivered !== null && eMetalDelivered !== oMetalDelivered)
                ||
                (eMetalDelivered === null && oMetalDelivered !== null)
                || 
                (eMetalDelivered !== null && oMetalDelivered === null )
              )
              {
                isEquals = false
              }
            }
            break;
          default:
            if (eQmetId !== oQmetId)
            {
              isEquals = false;
            }
        }
      }
    }

    if (isEquals)
    {
      const eWeightBeforeProcessing = nullOrZeroNumberTo(edited.weightBeforeProcessing, -1);
      const oWeightBeforeProcessing = nullOrZeroNumberTo(original.weightBeforeProcessing, -1);
      const eWeightAfterProcessing = nullOrZeroNumberTo(edited.weightAfterProcessing, -1);
      const oWeightAfterProcessing = nullOrZeroNumberTo(original.weightAfterProcessing, -1);
      const eWeight = nullOrZeroNumberTo(edited.weight, -1);
      const oWeight = nullOrZeroNumberTo(original.weight, -1);
      const eMarriageShare = nullOrZeroNumberTo(edited.marriageShare, -1);
      const oMarriageShare = nullOrZeroNumberTo(original.marriageShare, -1);
      const eUnitId = nullOrZeroNumberTo(edited.unit?.id, -1);
      const oUnitId = nullOrZeroNumberTo(original.unit?.id, -1);
      const eNote = nullOrEmptyStringTo(edited.note, 'X');
      const oNote = nullOrEmptyStringTo(original.note, 'X');

      if (
        (eWeightBeforeProcessing !== oWeightBeforeProcessing)
        ||
        (eWeightAfterProcessing !== oWeightAfterProcessing)
        ||
        (eWeight !== oWeight)
        ||
        (eMarriageShare !== oMarriageShare)
        ||
        (eUnitId !== oUnitId)
        ||
        (eNote !== oNote)
      )
      {
        isEquals = false;
      }
    }

    if (isEquals && ([null, QUALITY, SERVICE].includes(defectType)))
    {
      const eDefectTpgIdSize = nullOrZeroNumberTo(edited.topography?.defectTpgIdSize, 0);
      const oDefectTpgIdSize = nullOrZeroNumberTo(original.topography?.defectTpgIdSize, 0);
      const eDefectSize = nullOrZeroNumberTo(edited.topography?.defectSize, 0);
      const oDefectSize = nullOrZeroNumberTo(original.topography?.defectSize, 0);
      const eDefectTpgIdStep = nullOrZeroNumberTo(edited.topography?.defectTpgIdStep, 0);
      const oDefectTpgIdStep = nullOrZeroNumberTo(original.topography?.defectTpgIdStep, 0);
      const eDefectStep = nullOrZeroNumberTo(edited.topography?.defectStep, 0);
      const oDefectStep = nullOrZeroNumberTo(original.topography?.defectStep, 0);
      const eDefectTpgIdDistance = nullOrZeroNumberTo(edited.topography?.defectTpgIdDistance, 0);
      const oDefectTpgIdDistance = nullOrZeroNumberTo(original.topography?.defectTpgIdDistance, 0);
      const eDefectDistanceFromEdge = nullOrZeroNumberTo(edited.topography?.defectDistanceFromEdge, 0);
      const oDefectDistanceFromEdge = nullOrZeroNumberTo(original.topography?.defectDistanceFromEdge, 0);

      if (
        (eDefectTpgIdSize !== oDefectTpgIdSize)
        ||
        (eDefectSize !== oDefectSize)
        ||
        (eDefectTpgIdStep !== oDefectTpgIdStep)
        ||
        (eDefectStep !== oDefectStep)
        ||
        (eDefectTpgIdDistance !== oDefectTpgIdDistance)
        ||
        (eDefectDistanceFromEdge !== oDefectDistanceFromEdge)
      )
      {
        isEquals = false;
      }
    }

    if (isEquals)
    {
      if (edited.fields !== null && original.fields !== null)
      {
        if (edited.fields.length !== original.fields.length)
        {
          isEquals = false;
        }
        else
        {
          for (const field of edited.fields)
          {
            const fieldO = original.fields.find((item) => item.id === field.id) ?? null;
            if (fieldO === null)
            {
              isEquals = false;
              break;
            }
            else
            {
              if (Util.nullOrEmptyStringTo(fieldO.value, 'X') !== Util.nullOrEmptyStringTo(field.value, 'X'))
              {
                isEquals = false;
                break;
              }
            }
          }
        }
      }
    }

    if (isEquals)
    {
      for (const file of edited.files)
      {
        if (file.mode !== PieceFileMode.EXIST)
        {
          isEquals = false;
          break;
        }
      }
    }

    if (isEquals && defectType === TERM && !eIsAllPoses && !oIsAllPoses)
    {
      const ePoses = edited.specificationPoses ?? [];
      const oPoses = original.specificationPoses ?? [];

      if (
        ePoses.length !== oPoses.length
        ||
        !ePoses.every(
          (eItem) => oPoses.findIndex(
            (oItem) => eItem.pos === oItem.pos
              &&
              eItem.metalDelivered === oItem.metalDelivered
              &&
              eItem.volumeDeliveredTime === oItem.volumeDeliveredTime
              &&
              eItem.unit.id === oItem.unit.id
              &&
              eItem.daysOverdue === oItem.daysOverdue
          ) > -1
        )
      )
      {
        isEquals = false;
      }
    }

    if (isEquals && defectType === DIRTY_WAGONS)
    {
      const eQcs = edited.carriageQcs ?? [];
      const oQcs = original.carriageQcs ?? [];

      if (
        eQcs.length !== oQcs.length
        ||
        !eQcs.every(
          (eItem) => oQcs.findIndex(
            (oItem) => eItem.qcNum === oItem.qcNum
          ) > -1
        )
      )
      {
        isEquals = false;
      }
    }
  }

  return isEquals;
}

// #region commonChecksBeforeAddPieceAsync
type CommonChecksBeforeAddPieceArgs = void;
type CommonChecksBeforeAddPieceResolve = {
  pieceEditedIsDouble: boolean;
};
export type CommonChecksBeforeAddPieceReject = string;
export const commonChecksBeforeAddPieceAsync = createAsyncThunk<
  CommonChecksBeforeAddPieceResolve,
  CommonChecksBeforeAddPieceArgs,
  {
    state: RootState,
    rejectValue: CommonChecksBeforeAddPieceReject,
  }
>(
  `${SLICE_NAME}/commonChecksBeforeAddPieceAsync`,
  async (_, thunkAPI) => {
    const state = thunkAPI.getState();

    const { QUALITY } = types.inspection.DefectType;

    const account = authCommonSelectors.selectAccount(state)!;
    const isEntryAccounting = generalSelectors.selectGeneralIsEntryAccounting(state);
    const inspectionState = generalSelectors.selectGeneralInspectionState(state);
    const isPiece = pieceSelectors.selectPieceEditedIsPiece(state);
    const isWireRod = account.isWireRod;
    const isKolpino = (account.configs.find((item) => item.code === "KOLPINO")?.value ?? '0') === '1';
    const defectGuid = defectSelectors.selectDefectExpandedGuid(state);
    const defectType = defectSelectors.selectDefectDefectType(state, defectGuid) ?? QUALITY;
    const defectId = defectSelectors.selectDefectDefectId(state, defectGuid);
    const defectRefer = state.inspectionEdit.generalDefectRefer;
    const defectReferItem = defectRefer.find((item) => item.id === defectId) ?? null;

    // #region ПРОВЕРКА НА НАЛИЧИЕ ИЗМЕНЕНИЙ
    const edited = pieceSelectors.selectPieceEdited(state);

    if (edited !== null)
    {
      let isEquals = true;
      
      const original = pieceSelectors.selectPieceEditedOriginal(state)!;

      if ((edited.mode === PieceMode.NEW) && (original === null))
      {
        isEquals = false;
      }
      else
      {
        isEquals = isPiecesEquals(defectType, edited, original, account);
      }

      if (isEquals)
      {
        return thunkAPI.rejectWithValue('Изменения отсутствуют');
      }
    }
    // #endregion
    
    // #region ПРОВЕРКА ВВОДА ВСЕХ ОБЯЗАТЕЛЬНЫХ ПОЛЕЙ
    const pieceErrors = getPieceErrorMessage(
      edited!,
      pieceSelectors.selectPiecePiecesForExpandedDefect(state),
      isEntryAccounting,
      isWireRod,
      false,
      account,
      defectType,
      defectReferItem,
      inspectionState,
      state.inspectionEdit.generalOriginal !== null && inspectionState !== InspectionState.CREATE,
    );

    if (pieceErrors.length > 0)
    {
      return thunkAPI.rejectWithValue(pieceErrors);
    }
    // #endregion

    // #region ПРОВЕРКА НА ДУБЛИ ВНУТРЕННИЕ
    const qmetId = pieceSelectors.selectPieceEditedQmetId(state);
    const heat = pieceSelectors.selectPieceEditedHeat(state);
    const piece = pieceSelectors.selectPieceEditedPiece(state);
    const groupAtt = pieceSelectors.selectPieceEditedGroupAtt(state);
    const qcNum = pieceSelectors.selectPieceEditedQCNum(state);
    const carriage = pieceSelectors.selectPieceEditedCarriage(state);
    const specificationNum = pieceSelectors.selectPieceEditedSpecificationNum(state);
    const productTypeKolpinoId = pieceSelectors.selectPieceEditedProductTypeKolpinoId(state);
    const drawNum = pieceSelectors.selectPieceEditedDrawNum(state);

    const pieceEditedGuid = pieceSelectors.selectPieceEditedGuid(state);
    const pieces = pieceSelectors.selectPiecePiecesForExpandedDefect(state);

    const isDouble = (pieces || []).find(
      (p) => p.guid !== pieceEditedGuid && p.mode !== PieceMode.DEL &&
        isPiecesDuplicated(
          inspectionState,
          defectType,
          isPiece,
          isKolpino,
          {
            qmetId,
            heat,
            piece,
            groupAtt,
            qcNum,
            carriage,
            specificationNum,
            productTypeKolpinoId,
            drawNum,
          },
          {
            qmetId: p.qmetId,
            heat: p.heat,
            piece: p.piece,
            groupAtt: p.groupAtt,
            qcNum: p.qcNum,
            carriage: p.carriage,
            specificationNum: p.specificationNum,
            productTypeKolpinoId: p.productTypeKolpinoId,
            drawNum: p.drawNum,
          }
        )
    ) !== undefined;

    if (isDouble)
    {
      return { pieceEditedIsDouble: true }
    }
    // #endregion

    return { pieceEditedIsDouble: false }
  }
);
extraReducers.push((builder) => {
  builder
    .addCase(commonChecksBeforeAddPieceAsync.fulfilled, (state, action) => {
      const result = action.payload;

      if (result.pieceEditedIsDouble)
      {
        state.pieceEditedIsDouble = true;
      }
    })
    .addCase(commonChecksBeforeAddPieceAsync.rejected, (state, action) => {
    });
});
// #endregion

// #region extdoublesChecksBeforeAddPieceAsync
type ExtdoublesChecksBeforeAddPieceArgs = void;
type ExtdoublesChecksBeforeAddPieceResolve = {
  externalDoubles: types.inspection.Inspection[];
};
export type ExtdoublesChecksBeforeAddPieceReject = string;
export const extdoublesChecksBeforeAddPieceAsync = createAsyncThunk<
  ExtdoublesChecksBeforeAddPieceResolve,
  ExtdoublesChecksBeforeAddPieceArgs,
  {
    state: RootState,
    rejectValue: ExtdoublesChecksBeforeAddPieceReject,
  }
>(
  `${SLICE_NAME}/extdoublesChecksBeforeAddPieceAsync`,
  async (_, thunkAPI) => {
    const state = thunkAPI.getState();

    const { QUALITY, SERVICE, DIRTY_WAGONS, TERM } = types.inspection.DefectType;
    const { MECHANICAL_ENGINEERING_PART, PROCESSED_SHEET } = types.inspection.ProductTypeKolpino;

    const isEntryAccounting = generalSelectors.selectGeneralIsEntryAccounting(state);
    const isPiece = pieceSelectors.selectPieceEditedIsPiece(state);
    const defectGuid = defectSelectors.selectDefectExpandedGuid(state);
    const defectType = defectSelectors.selectDefectDefectType(state, defectGuid) ?? QUALITY;
    const qmetId = pieceSelectors.selectPieceEditedQmetId(state);
    const heat = pieceSelectors.selectPieceEditedHeat(state);
    const piece = pieceSelectors.selectPieceEditedPiece(state);
    const groupAtt = pieceSelectors.selectPieceEditedGroupAtt(state);
    const qcNum = pieceSelectors.selectPieceEditedQCNum(state);
    const carriage = pieceSelectors.selectPieceEditedCarriage(state);
    const specificationNum = pieceSelectors.selectPieceEditedSpecificationNum(state);
    const productTypeKolpinoId = pieceSelectors.selectPieceEditedProductTypeKolpinoId(state);

    // #region ПРОВЕРКА НА ДУБЛИ ВНЕШНЯЯ
    thunkAPI.dispatch(inspectionEditActions.generalOpStatusChanged(AsyncOpStatus.BUSY));
    thunkAPI.dispatch(inspectionEditActions.generalOpStatusLabelChanged('Проверка на дубли'));

    try
    {
      const body: types.inspection.PostInspectionDuplicateByGuidIn = {
        guid: generalSelectors.selectGeneralEditedGuid(state),
        heat: null,
        piece: null,
        groupAtt: null,
        qcNum: null,
        qmetId: null,
        carriage: null,
        specificationNum: null,
        isEntryAccounting,
      };

      switch (defectType) {
        case QUALITY:
        case SERVICE:
          if (isPiece && productTypeKolpinoId !== MECHANICAL_ENGINEERING_PART && productTypeKolpinoId !== PROCESSED_SHEET) {
            body.heat = heat;
            body.piece = piece;
            body.groupAtt = groupAtt;
            body.qcNum = qcNum;
          } else {
            body.qmetId = qmetId;
          }
          break;
        case DIRTY_WAGONS:
          body.carriage = carriage;
          break;
        case TERM:
          body.specificationNum = specificationNum;
          break;
        default:
      }

      const externalDoubles = await Api.postInspectionDuplicateByGuid(body);

      thunkAPI.dispatch(inspectionEditActions.generalOpStatusChanged(AsyncOpStatus.SUCCESS));

      if (externalDoubles.length > 0)
      {
        return { externalDoubles }
      }
    }
    catch (error)
    {
      thunkAPI.dispatch(inspectionEditActions.generalOpStatusChanged(AsyncOpStatus.SUCCESS));
    }
    // #endregion
	
    return { externalDoubles: [] }
  }
);
extraReducers.push((builder) => {
  builder
    .addCase(extdoublesChecksBeforeAddPieceAsync.fulfilled, (state, action) => {
      const result = action.payload;

      if (result.externalDoubles.length > 0)
      {
        state.pieceEditedExternalDoublesOpened = true;
        state.pieceEditedExternalDoublesList = [...result.externalDoubles];
      }
    })
    .addCase(extdoublesChecksBeforeAddPieceAsync.rejected, (state, action) => {
    });
});
// #endregion

// #region cvChecksBeforeAddPieceAsync
type CVChecksBeforeAddPieceArgs = void;
type CVChecksBeforeAddPieceResolve = {
  cvCheckFilesResultGroup: Nullable<Record<string, string[]>>;
  cvCheckFilesResultSingle: string[];
  cvCheckFilesAttempt: string[];
  cvCheckFilesDuplicates: Record<string, string[]>;
  cvCheckFilesBlur: Record<string, string[]>;
};
export type CVChecksBeforeAddPieceReject = string;
export const cvChecksBeforeAddPieceAsync = createAsyncThunk<
  CVChecksBeforeAddPieceResolve,
  CVChecksBeforeAddPieceArgs,
  {
    state: RootState,
    rejectValue: CVChecksBeforeAddPieceReject,
  }
>(
  `${SLICE_NAME}/cvChecksBeforeAddPieceAsync`,
  async (_, thunkAPI) => {
    const state = thunkAPI.getState();

    const account = authCommonSelectors.selectAccount(state)!;
    const isEntryAccounting = generalSelectors.selectGeneralIsEntryAccounting(state);
    const inspectionState = generalSelectors.selectGeneralInspectionState(state);
    const defectGuid = defectSelectors.selectDefectExpandedGuid(state);
    const defectId = defectSelectors.selectDefectDefectId(state, defectGuid);
    const defectRefer = state.inspectionEdit.generalDefectRefer;
    const defectReferItem = defectRefer.find((item) => item.id === defectId) ?? null;
    const edited = pieceSelectors.selectPieceEdited(state);

    let cvCheckFilesAttempt = [] as string[];

	  // ВЫЗОВ CV-МОДЕЛИ
    const cvCallAttempts = pieceSelectors.selectPieceCVCheckFilesAttempts(state);
    const pieceCVCallCount = cvCallAttempts.filter((guid) => guid === edited!.guid).length;
    const cvCntAttemps = parseInt(
      account.configs.find(
        (item) => item.code === "CV_CNT_ATTEMPT"
      )?.value ?? `${Constants.CV_CNT_ATTEMPT_DEFAULT}`,
      10
    );

    if (pieceCVCallCount < cvCntAttemps)
    {
      const placeOfFixationId = defectSelectors.selectDefectPlaceOfFixationId(state, defectGuid);
      const cvFilesMax = parseInt(
        account.configs.find(
          (item) => item.code === "CV_CNF_FILES_MAX"
        )?.value ?? `${Constants.CV_CNF_FILES_MAX_DEFAULT}`,
        10
      );

      const { GENERAL, PHOTO } = PieceFileType;
      const allowedFiles = edited!.files.filter(
        (file) => [GENERAL, PHOTO].includes(file.type) && Util.checkFileTypeByExt(file.name) !== 'TIF'
      );

      const passPlaceOfFixationId = (placeOfFixationId === null) || (placeOfFixationId !== 3);
      const passInspectionState = [InspectionState.NEW, InspectionState.DRAFT, InspectionState.CREATE].includes(inspectionState);
      const passNewFiles = allowedFiles.some((file) => file.mode === PieceFileMode.NEW);
      const passCountFiles = allowedFiles.filter((file) => file.mode !== PieceFileMode.DEL).length <= cvFilesMax;
      const passEntryAccounting = !isEntryAccounting;

      const isCheckCVNeeded = passPlaceOfFixationId && passInspectionState && passNewFiles && passCountFiles && passEntryAccounting;
      
      if (isCheckCVNeeded)
      {
        thunkAPI.dispatch(inspectionEditActions.generalOpStatusChanged(AsyncOpStatus.BUSY));
        thunkAPI.dispatch(inspectionEditActions.generalOpStatusLabelChanged('Проверка фото CV-моделью'));

        try
        {
          cvCheckFilesAttempt = [edited!.guid];

          const cvSeconds = parseInt(
            account.configs.find(
              (item) => item.code === "CV_SECONDS"
            )?.value ?? `${Constants.CV_SECONDS_DEFAULT}`,
            10
          );

          const cvResult = await Api.postInspectionCheckFilesWithCVModel({
            cvSeconds,
            positionGuid: edited!.guid,
            photos: allowedFiles
              .filter((file) => file.mode !== PieceFileMode.DEL)
              .map((file) => ({ photoGuid: file.guid, photoName: file.name, }))
          });

          const cfgControlTicket = defectReferItem?.configs?.find((item) => item.code === "CONTROL_TICKET")?.value ?? "0";
          const cfgControlLine = defectReferItem?.configs?.find((item) => item.code === "CONTROL_LINE")?.value ?? "0";
          const cfgControlScales = defectReferItem?.configs?.find((item) => item.code === "CONTROL_SCALES")?.value ?? "0";
          const cfgControlDelivery = defectReferItem?.configs?.find((item) => item.code === "CONTROL_RENTAL_STATE_OF_DELIVERY")?.value ?? "0";
          const cfgControlImageDefect = defectReferItem?.configs?.find((item) => item.code === "CONTROL_DUPLICATE_AND_BLURR")?.value ?? "0";
          const cfgControlQmetId = defectReferItem?.configs?.find((item) => item.code === "CONTROL_QMET_ID")?.value ?? "0";

          let group = [] as string[];
          let singles = [] as string[];
          let duplicates = [] as string[];
          const blur = [] as string[];

          const everyFalse = (attr: string): boolean => cvResult.photos.every((photo) => 
            (photo.attributes.find((item) => item.name === attr)?.value ?? false) === false
          );

          if (cfgControlTicket === "1" && everyFalse("ticket"))
          {
            group.push("фото бирки");
          }
          else
          {
            if (cfgControlQmetId === "1" && !(edited!.isPiece) && edited!.qmetId !== null)
            {
              const tagCodeValue = cvResult.photos.map(
                (photo) => (photo.attributes.find((attr) => attr.name === "tagCode")?.value ?? []) as string[]
              ).flat();

              if (tagCodeValue.length > 0 && !tagCodeValue.includes(`${edited!.qmetId}`))
              {
                singles.push(`Проверьте Идн номер позиции ${edited!.qmetId} на соответствие штрих коду на фото бирки в позиции.`);
              }
            }
          }

          if (cfgControlScales === "1" && everyFalse("scales"))
          {
            group.push("фото с результатом провески");
          }

          if (cfgControlLine === "1" && everyFalse("line"))
          {
            group.push("фото с измерительным инструментом (линейка и т.п)");
          }

          const defectStateOfDelivery = defectSelectors.selectDefectRentalStateOfDelivery(state, defectGuid);

          if (cfgControlDelivery === "1" && defectStateOfDelivery === 1 && everyFalse("roll"))
          {
            group.push("фото металлопроката в состоянии поставки");
          }

          if (cfgControlImageDefect === "1")
          {
            const cvBlur = parseInt(
              account.configs.find(
                (item) => item.code === "CV_BLURR"
              )?.value ?? `${Constants.CV_BLUR_DEFAULT}`,
              10
            );
            cvResult.photos.forEach((photo) => {
              const blurAttrValue = photo.attributes.find((attr) => attr.name === "blurring")?.value ?? 0;
              if (blurAttrValue < cvBlur)
              {
                blur.push(photo.photoGuid);
              }
            });

            if (blur.length > 0)
            {
              singles.push("Выявлено наличие нечеткого изображения на фото. Необходимо заменить фото.");
            }

            const generalGuid = allowedFiles.find((file) => file.type === PieceFileType.GENERAL)?.guid ?? null;
            cvResult.photos.sort((a, b) => {
              if (a.photoGuid === generalGuid)
              {
                return -1;
              }
              if (b.photoGuid === generalGuid)
              {
                return 1;
              }
              return 0;
            });

            cvResult.photos.forEach((photo) => {
              if (duplicates.includes(photo.photoGuid))
              {
                return;
              }
              const duplicatesAttrValue = (photo.attributes.find((attr) => attr.name === "duplicatesGuidFoto")?.value ?? []) as string[];
              duplicates = Array.from(new Set([...duplicates, ...duplicatesAttrValue]));
            });

            if (duplicates.length > 0)
            {
              singles.push("Найдены дубликаты фотографий. Необходимо скорректировать фото.");
            }
          }

          thunkAPI.dispatch(inspectionEditActions.generalOpStatusChanged(AsyncOpStatus.SUCCESS));

          if (group.length === 1)
          {
            singles = [`Необходимо добавить в позицию ${group[0]}.`, ...singles];
            group = [];
          }

          return {
            cvCheckFilesResultGroup: group.length > 0 ? {
              "Необходимо добавить в позицию:": group.map((item) => `- ${item};`)
            } : null,
            cvCheckFilesResultSingle: singles,
            cvCheckFilesAttempt,
            cvCheckFilesDuplicates: { [edited!.guid]: duplicates },
            cvCheckFilesBlur: { [edited!.guid]: blur },
          }
        }
        catch (error)
        {
          thunkAPI.dispatch(inspectionEditActions.generalOpStatusChanged(AsyncOpStatus.SUCCESS));
        }
      }
    }

    return {
      cvCheckFilesResultGroup: null,
      cvCheckFilesResultSingle: [],
      cvCheckFilesAttempt,
      cvCheckFilesDuplicates: { },
      cvCheckFilesBlur: { },
    }
  }
);
extraReducers.push((builder) => {
  builder
    .addCase(cvChecksBeforeAddPieceAsync.fulfilled, (state, action) => {
      const result = action.payload;

      if (result.cvCheckFilesAttempt)
      {
        state.pieceCVCheckFilesAttempts.push(...result.cvCheckFilesAttempt);
      }

      state.pieceCVCheckFilesDuplicates = { ...state.pieceCVCheckFilesDuplicates, ...result.cvCheckFilesDuplicates };
      state.pieceCVCheckFilesBlur = { ...state.pieceCVCheckFilesBlur, ...result.cvCheckFilesBlur };

      if (result.cvCheckFilesResultGroup !== null || result.cvCheckFilesResultSingle.length > 0)
      {
        state.pieceEditedCVCheckFilesResultOpened = true;
        state.pieceEditedCVCheckFilesResultGroup = result.cvCheckFilesResultGroup ?? { };
        state.pieceEditedCVCheckFilesResultSingle = result.cvCheckFilesResultSingle;
      }
    })
    .addCase(cvChecksBeforeAddPieceAsync.rejected, (state, action) => {
    });
});
// #endregion

// #region closePieceAsync
type ClosePieceArgs = void;
type ClosePieceResolve = {
  hasChanges: boolean;
};
export type ClosePieceReject = void;
export const closePieceAsync = createAsyncThunk<
  ClosePieceResolve,
  ClosePieceArgs,
  {
    state: RootState,
    rejectValue: ClosePieceReject,
  }
>(
  `${SLICE_NAME}/closePieceAsync`,
  async (_, thunkAPI) => {
    const state = thunkAPI.getState();

    const account = authCommonSelectors.selectAccount(state)!;
    const defectGuid = defectSelectors.selectDefectExpandedGuid(state);
    const defectType = defectSelectors.selectDefectDefectType(state, defectGuid);

    // ПРОВЕРКА НА НАЛИЧИЕ ИЗМЕНЕНИЙ
    let isEquals = true;
    const edited = pieceSelectors.selectPieceEdited(state);

    if (edited !== null)
    {
      const original = pieceSelectors.selectPieceEditedOriginal(state)!;

      if ((edited.mode === PieceMode.NEW) && (original === null))
      {
        isEquals = false;
      }
      else
      {
        isEquals = isPiecesEquals(defectType, edited, original, account);
      }
    }

    return {
      hasChanges: !isEquals
    }
  }
);
// #endregion

// #region downloadPhotoAsync
type DownloadPhotoArgs = {
  fileGuid: string;
};
type DownloadPhotoResolve = {
  fileGuid: string;
  dataUrl: string;
  binaryStr: string;
  name: string;
  mime: string;
};
export type DownloadPhotoReject = ApiError;
export const downloadPhotoAsync = createAsyncThunk<
  DownloadPhotoResolve,
  DownloadPhotoArgs,
  {
    state: RootState,
    rejectValue: DownloadPhotoReject,
  }
>(
  `${SLICE_NAME}/downloadPhotoAsync`,
  async (args, thunkAPI) => {
    const pE = thunkAPI.getState().inspectionEdit.pieceEdited;

    if (pE === null)
    {
      return {
        fileGuid: args.fileGuid,
        dataUrl: '',
        binaryStr: '',
        name: '',
        mime: '',
      }
    }
    else
    {
      let updatedFile: Nullable<types.inspection.PieceFile> = null;

      for (const file of pE.files)
      {
        if (file.guid === args.fileGuid)
        {
          updatedFile = file;
        }
      }

      if (updatedFile === null)
      {
        return {
          fileGuid: args.fileGuid,
          dataUrl: '',
          binaryStr: '',
          name: '',
          mime: '',
        }
      }
      else
      {
        try
        {
          const isTIF = Util.checkFileTypeByExt(updatedFile.name) === 'TIF';
          const mime = isTIF ? 'image/tiff' : 'image/jpeg';
          const remoteFile = await Api.getInspectionFileByGuid({ guid: args.fileGuid, source: 'piece' });
          const fileFromBlob = await Util.blobToFile(remoteFile.blob, updatedFile.name, mime);
          const dataUrlResult = await Util.imageToDataURL(fileFromBlob);
          const binaryStr = await Util.fileToBinaryString(fileFromBlob);

          if (dataUrlResult.ok)
          {
            return {
              fileGuid: args.fileGuid,
              dataUrl: dataUrlResult.result,
              binaryStr: binaryStr,
              name: updatedFile.name,
              mime,
            }
          }
          else
          {
            return {
              fileGuid: args.fileGuid,
              dataUrl: Util.noPhoto,
              binaryStr: '',
              name: updatedFile.name,
              mime: 'image/jpeg',
            }
          }
        }
        catch(error)
        {
          return {
            fileGuid: args.fileGuid,
            dataUrl: Util.noPhoto,
            binaryStr: '',
            name: updatedFile.name,
            mime: 'image/jpeg',
          }
        }
      }
    }
  }
);
extraReducers.push((builder) => {
  builder
    .addCase(downloadPhotoAsync.fulfilled, (state, action) => {
      if (state.pieceEdited !== null)
      {
        for (const file of state.pieceEdited.files)
        {
          if (file.guid === action.payload.fileGuid)
          {
            file.__dataUrl = action.payload.dataUrl;
            file.__binaryStr = action.payload.binaryStr;
            file.__name = action.payload.name;
            file.__mime = action.payload.mime;
          }
        }
      }

      if (state.generalEdited !== null)
      {
        for (const defect of state.generalEdited.inspectionDefects)
        {
          for (const piece of (defect.pieces || []))
          {
            for (const file of piece.files)
            {
              if (file.guid === action.payload.fileGuid)
              {
                file.__dataUrl = action.payload.dataUrl;
                file.__binaryStr = action.payload.binaryStr;
                file.__name = action.payload.name;
                file.__mime = action.payload.mime;
              }
            }
          }
        }
      }
    })
    .addCase(downloadPhotoAsync.rejected, (state, action) => {
    });
});
// #endregion

// #region downloadCertAsync
type DownloadCertArgs = {
  qcNum: string;
};
type DownloadCertResolve = void;
export type DownloadCertReject = ApiError;
export const downloadCertAsync = createAsyncThunk<
  DownloadCertResolve,
  DownloadCertArgs,
  {
    state: RootState,
    rejectValue: DownloadCertReject,
  }
>(
  `${SLICE_NAME}/downloadCertAsync`,
  async (args, thunkAPI) => {
    try
    {
      thunkAPI.dispatch(inspectionEditActions.generalOpStatusChanged(AsyncOpStatus.BUSY));
      thunkAPI.dispatch(inspectionEditActions.generalOpStatusLabelChanged('Постановка в очередь задачи на скачивание сертификата'));
      
      await Api.postQCCheck({ qcNum: args.qcNum });

      thunkAPI.dispatch(inspectionEditActions.generalOpStatusChanged(AsyncOpStatus.SUCCESS));
    }
    catch (error)
    {
      thunkAPI.dispatch(inspectionEditActions.generalOpStatusChanged(AsyncOpStatus.ERROR));
      return thunkAPI.rejectWithValue(error as ApiError);
    }
  }
);
extraReducers.push((builder) => {
  builder
    .addCase(downloadCertAsync.fulfilled, (state, action) => {
      state.generalOpStatusLabel = '';
      state.pieceCertDownloadOpened = true;
      state.pieceEdited!.qmetData.qcState = QCState.LOADING;

      state.generalEdited!.inspectionDefects.forEach((defect) => {
        (defect.pieces ?? []).forEach((piece) => {
          if (piece.guid === state.pieceEdited!.guid) {
            piece.qmetData.qcState = QCState.LOADING;
          }
        })
      })
    })
    .addCase(downloadCertAsync.rejected, (state, action) => {
    });
});
// #endregion

// #region checkCertAsync
type CheckCertArgs = {
  inspectionGuid: string;
};
type CheckCertResolve = QCState;
export type CheckCertReject = ApiError;
export const checkCertAsync = createAsyncThunk<
  CheckCertResolve,
  CheckCertArgs,
  {
    state: RootState,
    rejectValue: CheckCertReject,
  }
>(
  `${SLICE_NAME}/checkCertAsync`,
  async (args, thunkAPI) => {
    try
    {
      thunkAPI.dispatch(inspectionEditActions.generalOpStatusChanged(AsyncOpStatus.BUSY));
      thunkAPI.dispatch(inspectionEditActions.generalOpStatusLabelChanged('Проверка статуса загрузки сертификата'));
      
      const inspection = await Api.getInspectionByGuid({ guid: args.inspectionGuid });
      const selectedPieceGuid = pieceSelectors.selectPieceEditedGuid(thunkAPI.getState())!;

      const updatedDefect = inspection.inspectionDefects.find((defect) => (defect.pieces || []).find((piece) => piece.guid === selectedPieceGuid) !== undefined)!;
      const updatedPiece = (updatedDefect.pieces || []).find((piece) => piece.guid === selectedPieceGuid)!;

      thunkAPI.dispatch(inspectionEditActions.generalOpStatusChanged(AsyncOpStatus.SUCCESS));

      return updatedPiece.qmetData.qcState;
    }
    catch (error)
    {
      thunkAPI.dispatch(inspectionEditActions.generalOpStatusChanged(AsyncOpStatus.ERROR));
      return thunkAPI.rejectWithValue(error as ApiError);
    }
  }
);
extraReducers.push((builder) => {
  builder
    .addCase(checkCertAsync.fulfilled, (state, action) => {
      state.generalOpStatusLabel = '';
      state.pieceEdited!.qmetData.qcState = action.payload;

      state.generalEdited!.inspectionDefects.forEach((defect) => {
        (defect.pieces ?? []).forEach((piece) => {
          if (piece.guid === state.pieceEdited!.guid) {
            piece.qmetData.qcState = action.payload;
          }
        })
      })
    })
    .addCase(checkCertAsync.rejected, (state, action) => {
    });
});
// #endregion

// #region viewCertAsync
type ViewCertArgs = {
  qcNum: string;
};
type ViewCertResolve = void;
export type ViewCertReject = ApiError;
export const viewCertAsync = createAsyncThunk<
  ViewCertResolve,
  ViewCertArgs,
  {
    state: RootState,
    rejectValue: ViewCertReject,
  }
>(
  `${SLICE_NAME}/viewCertAsync`,
  async (args, thunkAPI) => {
    try
    {
      thunkAPI.dispatch(inspectionEditActions.generalOpStatusChanged(AsyncOpStatus.BUSY));
      thunkAPI.dispatch(inspectionEditActions.generalOpStatusLabelChanged('Получение файла сертификата'));
      
      const result = await Api.getQCFile({ qcNum: args.qcNum });

      thunkAPI.dispatch(inspectionEditActions.generalOpStatusChanged(AsyncOpStatus.SUCCESS));

      //window.open(result.dataUrl, '_blank')?.focus();
      const a = document.createElement("a");
      a.href = result.dataUrl;
      a.download = `cert_${args.qcNum}.pdf`;
      a.click();
    }
    catch (error)
    {
      thunkAPI.dispatch(inspectionEditActions.generalOpStatusChanged(AsyncOpStatus.ERROR));
      return thunkAPI.rejectWithValue(error as ApiError);
    }
  }
);
// #endregion

// #region shareCertAsync
type ShareCertArgs = {
  qcNum: string;
};
type ShareCertResolve = void;
export type ShareCertReject = ApiError;
export const shareCertAsync = createAsyncThunk<
  ShareCertResolve,
  ShareCertArgs,
  {
    state: RootState,
    rejectValue: ShareCertReject,
  }
>(
  `${SLICE_NAME}/shareCertAsync`,
  async (args, thunkAPI) => {
    try
    {
      thunkAPI.dispatch(inspectionEditActions.generalOpStatusChanged(AsyncOpStatus.BUSY));
      thunkAPI.dispatch(inspectionEditActions.generalOpStatusLabelChanged('Получение файла сертификата'));
      
      const result = await Api.getRawQCFile({ qcNum: args.qcNum });

      thunkAPI.dispatch(inspectionEditActions.generalOpStatusChanged(AsyncOpStatus.SUCCESS));

      const file = new File([result.rawData], `Сертификат №${args.qcNum}.pdf`, { type: 'application/pdf' });
      navigator.share({
        title: `Сертификат №${args.qcNum}.pdf`,
        text: `Файл сертификата из приложения Checksteel`,
        files: [file]
      });
    }
    catch (error)
    {
      thunkAPI.dispatch(inspectionEditActions.generalOpStatusChanged(AsyncOpStatus.ERROR));
      return thunkAPI.rejectWithValue(error as ApiError);
    }
  }
);
// #endregion

// #region viewDOCAsync
type ViewDOCArgs = {
  fileName: string;
  guid: string;
  source: 'piece' | 'inspection';
};
type ViewDOCResolve = void;
export type ViewDOCReject = ApiError;
export const viewDOCAsync = createAsyncThunk<
  ViewDOCResolve,
  ViewDOCArgs,
  {
    state: RootState,
    rejectValue: ViewDOCReject,
  }
>(
  `${SLICE_NAME}/viewDOCAsync`,
  async (args, thunkAPI) => {
    try
    {
      thunkAPI.dispatch(inspectionEditActions.generalOpStatusChanged(AsyncOpStatus.BUSY));
      thunkAPI.dispatch(inspectionEditActions.generalOpStatusLabelChanged('Получение документа'));

      let resultDataUrl = '';

      switch (Util.getFileExt(args.fileName).toUpperCase()) {
        case 'PDF':
          resultDataUrl = (await Api.getInspectionFileAsPDFByGuid({ guid: args.guid, source: args.source })).dataUrl;
          break;
        case 'XLSX':
          resultDataUrl = (await Api.getInspectionFileAsXLSXByGuid({ guid: args.guid, source: args.source })).dataUrl;
          break;
      }
      
      thunkAPI.dispatch(inspectionEditActions.generalOpStatusChanged(AsyncOpStatus.SUCCESS));

      const a = document.createElement("a");
      a.href = resultDataUrl;
      a.download = args.fileName;
      a.click();
    }
    catch (error)
    {
      thunkAPI.dispatch(inspectionEditActions.generalOpStatusChanged(AsyncOpStatus.ERROR));
      return thunkAPI.rejectWithValue(error as ApiError);
    }
  }
);
// #endregion


const pieceReducers = {
  pieceEditOpened: (state: WritableDraft<InspectionEditState>) => {
    state.pieceEditOpened = true;
  },

  pieceEditClosed: (state: WritableDraft<InspectionEditState>) => {
    state.pieceEditOpened = false;
  },

  pieceForDeleteOneMarked: (state: WritableDraft<InspectionEditState>, action: StringChangedAction) => {
    if (!state.pieceMarkedForDeleteGuids.includes(action.payload))
    {
      state.pieceMarkedForDeleteGuids.push(action.payload);
    }
  },

  pieceForDeleteOneUnmarked: (state: WritableDraft<InspectionEditState>, action: StringChangedAction) => {
    if (state.pieceMarkedForDeleteGuids.includes(action.payload))
    {
      state.pieceMarkedForDeleteGuids = state.pieceMarkedForDeleteGuids.filter((guid) => guid !== action.payload)
    }
  },

  pieceForDeleteAllMarked: (state: WritableDraft<InspectionEditState>, action: StringArrayChangedAction) => {
    state.pieceMarkedForDeleteGuids = [...action.payload];
  },

  pieceForDeleteAllUnmarked: (state: WritableDraft<InspectionEditState>) => {
    state.pieceMarkedForDeleteGuids = [];
  },

  pieceForDeleteMarkedDeleted: (state: WritableDraft<InspectionEditState>) => {
    const gE = state.generalEdited;

    if (gE !== null)
    {
      const eD = gE.inspectionDefects.find((defect) => defect.guid === state.defectExpandedGuid) ?? null;

      if (eD !== null)
      {
        eD.pieces = (eD.pieces || [])
          .map(
            (piece) => state.pieceMarkedForDeleteGuids.includes(piece.guid) ?
              (piece.mode === PieceMode.NEW ? null : { ...piece, mode: PieceMode.DEL })
              :
              piece
          )
          .filter((piece) => piece !== null) as types.inspection.Piece[];
        state.pieceMarkedForDeleteGuids = [];
      }
    }
  },

  pieceOneDeleted: (state: WritableDraft<InspectionEditState>, action: StringChangedAction) => {
    const gE = state.generalEdited;

    if (gE !== null)
    {
      const eD = gE.inspectionDefects.find((defect) => defect.guid === state.defectExpandedGuid) ?? null;

      if (eD !== null)
      {
        eD.pieces = (eD.pieces || [])
          .map(
            (piece) => piece.guid === action.payload ?
              (piece.mode === PieceMode.NEW ? null : { ...piece, mode: PieceMode.DEL })
              :
              piece
          )
          .filter((piece) => piece !== null) as types.inspection.Piece[];
      }
    }
  },

  pieceForEditSelected: (state: WritableDraft<InspectionEditState>, action: NullableStringChangedAction) => {
    if (action.payload === null)
    {
      const pieceGuid = Util.guid(true);
      let unitId = Preferences.preferredWeightUnit;

      if (!state.generalIsEntryAccounting && unitId === 35)
      {
        unitId = 1;
      }

      const prefferedUnit = state.generalUnitRefer.find((item) => item.id === unitId && item.isActual) ?? null;
      const firstActualUnit = state.generalUnitRefer.find((item) => item.isActual)!;
      const workUnit = prefferedUnit ?? firstActualUnit;
      const details = workUnit.details.find((item) => item.isEntryAccounting === state.generalIsEntryAccounting);
      const defectGuid = defectSelectors.selectDefectExpandedGuid({ inspectionEdit: state });
      const defectType = defectSelectors.selectDefectDefectType({ inspectionEdit: state }, defectGuid);

      const isQualityOrService = [null, DefectType.QUALITY, DefectType.SERVICE].includes(defectType);
      const isDirtyWagons = defectType === DefectType.DIRTY_WAGONS;
      const isTerm = defectType === DefectType.TERM;

      state.pieceEdited = {
        fields: [],
        files: [],
        groupAtt: null,
        guid: pieceGuid,
        heat: null,
        pieceNumId: null,
        productTypeKolpinoId: null,
        productTypeKolpinoTitle: null,
        drawNum: null,
        isPiece: isQualityOrService ? Preferences.preferredMethodUEEnter === 'piece' : false,
        specificationNum: null,
        isAllPoses: false,
        metalDelivered: null,
        specificationPoses: isTerm
          ? [{
              isControl: true,
              pos: null,
              metalDelivered: null,
              volumeDeliveredTime: null,
              unit: {
                id: 2,
                title: 'T',
                isInteger: false,
              },
              daysOverdue: null,
              __volumeDeliveredTime: '',
            }]
          : [],
        carriage: null,
        carriageQcs: isDirtyWagons ? [{ isControl: true, qcNum: '', qcState: QCState.NONE }] : [],
        mode: types.inspection.PieceMode.NEW,
        note: '',
        piece: null,
        qcNum: null,
        qmetData: {
          group: null,
          qcNum: null,
          qcState: QCState.NONE,
          productType: "",
          actualWeight: null,
        },
        qmetId: null,
        topography: {
          defectDistanceFromEdge: null,
          defectSize: null,
          defectStep: null,
          defectTpgIdDistance: null,
          defectTpgIdSize: null,
          defectTpgIdStep: null,
          defectTpgTitleDistance: null,
          defectTpgTitleSize: null,
          defectTpgTitleStep: null,
        },
        weightBeforeProcessing: null,
        weightAfterProcessing: null,
        marriageShare: null,
        weight: null,
        isSmc: false,
        unit: {
          id: workUnit.id,
          title: workUnit.title,
          fieldTitle: details?.fieldTitle ?? 'Вес',
          fieldHint: details?.fieldHint ?? 'Вес',
        }
      };
      state.pieceEditOpened = true;
      state.pieceSelectedGuid = pieceGuid;
      state.pieceEditedWeight = '';
      state.pieceEditedWeightBeforeProcessing = '';
      state.pieceEditedWeightAfterProcessing = '';
      state.pieceEditedWeightAfterProcessingError = '';
      state.pieceEditedQmetIdError = '';
      state.pieceEditedCarriageError = '';
      state.pieceEditedCarriageQcsInError = [];
      state.pieceEditedSpecificationNumError = '';
      state.pieceEditedSpecificationPosInError = [];
      state.pieceEditedHeatError = '';
      state.pieceEditedPieceError = '';
      state.pieceEditedGroupAttError = '';
      state.pieceEditedQCNumError = '';
      state.pieceEditedCustomFieldsErrors = {}
      state.pieceEditedIsMarriageShareExpanded = false;
      state.pieceEditedWeightError = '';
      state.pieceEditedNoteError = '';
      state.pieceEditedIsTopographyExpanded = false;
      state.pieceEditedTopoSize = '';
      state.pieceEditedTopoStep = '';
      state.pieceEditedTopoDistance = '';
    }
    else
    {
      const piece = (state.generalEdited!.inspectionDefects
        .find((defect) => defect.guid === state.defectExpandedGuid)!.pieces ?? [])
        .find((piece:types.inspection.Piece) => piece.guid === action.payload)!;

      const pieceEdited = (JSON.parse(JSON.stringify(piece)) as types.inspection.Piece);

      pieceEdited.topography.defectSize = pieceEdited.topography.defectSize === 0 ? null : pieceEdited.topography.defectSize;
      pieceEdited.topography.defectDistanceFromEdge = pieceEdited.topography.defectDistanceFromEdge === 0 ? null : pieceEdited.topography.defectDistanceFromEdge;
      pieceEdited.topography.defectStep = pieceEdited.topography.defectStep === 0 ? null : pieceEdited.topography.defectStep;
      pieceEdited.weightBeforeProcessing = pieceEdited.weightBeforeProcessing === 0 ? null : pieceEdited.weightBeforeProcessing;
      pieceEdited.weightAfterProcessing = pieceEdited.weightAfterProcessing === 0 ? null : pieceEdited.weightAfterProcessing;
      
      state.pieceEdited = pieceEdited;
      state.pieceEditOpened = true;
      state.pieceSelectedGuid = piece.guid;
      state.pieceEditedWeightBeforeProcessing = `${pieceEdited.weightBeforeProcessing ?? ''}`;
      state.pieceEditedWeightAfterProcessing = `${pieceEdited.weightAfterProcessing ?? ''}`;
      state.pieceEditedWeightAfterProcessingError = '';
      state.pieceEditedWeight = `${pieceEdited.weight ?? ''}`;
      state.pieceEditedQmetIdError = '';
      state.pieceEditedCarriageError = '';
      state.pieceEditedCarriageQcsInError = (pieceEdited.carriageQcs ?? []).filter((item) => !item.isControl).map((item) => `${pieceEdited.carriage}:${item.qcNum}`);
      state.pieceEditedSpecificationNumError = '';
      state.pieceEditedSpecificationPosInError = (pieceEdited.specificationPoses ?? []).filter((item) => !item.isControl && item.pos !== null).map((item) => `${pieceEdited.specificationNum}:${item.pos}`);
      state.pieceEditedHeatError = '';
      state.pieceEditedPieceError = '';
      state.pieceEditedGroupAttError = '';
      state.pieceEditedQCNumError = '';
      state.pieceEditedCustomFieldsErrors = {};
      state.pieceEditedIsMarriageShareExpanded = (
                                                   pieceEdited.weightAfterProcessing !== null
                                                   &&
                                                   pieceEdited.weightAfterProcessing !== 0
                                                 );
      state.pieceEditedWeightError = '';
      state.pieceEditedNoteError = '';
      state.pieceEditedIsTopographyExpanded = !Object.values(pieceEdited.topography).every((value) => value === null);
      state.pieceEditedTopoSize = `${(pieceEdited.topography.defectSize ?? '')}`;
      state.pieceEditedTopoStep = `${(pieceEdited.topography.defectStep ?? '')}`;
      state.pieceEditedTopoDistance = `${(pieceEdited.topography.defectDistanceFromEdge ?? '')}`;

      pieceEdited.specificationPoses = pieceEdited.specificationPoses.map((item) => ({
        ...item,
        __volumeDeliveredTime: `${item.volumeDeliveredTime ?? ''}`,
      }))
    }
  },

  pieceEditedIsPieceChanged: (state: WritableDraft<InspectionEditState>, action: BooleanChangedAction) => {
    if (state.pieceEdited !== null)
    {
      state.pieceEdited.isPiece = action.payload;

      if (state.pieceEdited.qmetData)
      {
        state.pieceEdited.qmetData.productType = '';
        state.pieceEdited.qmetData.qcNum = null;
      }

      if (action.payload)
      {
        state.pieceEdited.qmetId = null;
        state.pieceEdited.qcNum = null;
        state.pieceEdited.pieceNumId = null;
      }
      else
      {
        if (state.pieceEdited.qmetData)
        {
          state.pieceEdited.qmetData.productType = '';
        }

        state.pieceEdited.qmetId = null;
        state.pieceEdited.heat = null;
        state.pieceEdited.piece = null;
        state.pieceEdited.qcNum = null;
        state.pieceEdited.groupAtt = null;
        state.pieceEdited.drawNum = null;
        state.pieceEdited.productTypeKolpinoId = null;
        state.pieceEdited.productTypeKolpinoTitle = null;
      }
      Preferences.setPreferredMethodUEEnter(action.payload ? 'piece' : 'id');

      state.pieceEditedQCNumError = getKolpinoQCNumError(state.pieceEdited.productTypeKolpinoId, state.pieceEdited.qcNum);

      state.pieceEdited.mode = getPieceMode(state);
    }
  },

  pieceEditedCarriageChanged: (state: WritableDraft<InspectionEditState>, action: StringChangedAction) => {
    if (state.pieceEdited !== null)
    {
      state.pieceEdited.carriage = action.payload;

      if (action.payload !== '' && action.payload.length < Constants.PIECE_CARRIAGE_LENGTH_MIN)
      {
        state.pieceEditedCarriageError = `Не менее ${Constants.PIECE_CARRIAGE_LENGTH_MIN} символов`;
      }
      else
      {
        state.pieceEditedCarriageError = '';
      }

      const { pieceEditedIsDouble, pieceEditedMode } = checkDoublesAndGetMode(state);

      state.pieceEditedIsDouble = pieceEditedIsDouble;
      state.pieceEdited.mode = pieceEditedMode;
    }
  },

  pieceEditedCarriageErrorFixed: (state: WritableDraft<InspectionEditState>) => {
    state.pieceEditedCarriageError = '';
  },

  pieceEditedCarriageCertAdded: (state: WritableDraft<InspectionEditState>) => {
    if (state.pieceEdited !== null)
    {
      state.pieceEdited.carriageQcs.push({
        isControl: true,
        qcNum: '',
        qcState: types.inspection.QCState.NONE
      });

      const { pieceEditedIsDouble, pieceEditedMode } = checkDoublesAndGetMode(state);

      state.pieceEditedIsDouble = pieceEditedIsDouble;
      state.pieceEdited.mode = pieceEditedMode;
    }
  },

  pieceEditedCarriageCertDeleted: (state: WritableDraft<InspectionEditState>, action: NumberChangedAction) => {
    if (state.pieceEdited !== null)
    {
      state.pieceEdited.carriageQcs.splice(action.payload, 1);

      const { pieceEditedIsDouble, pieceEditedMode } = checkDoublesAndGetMode(state);

      state.pieceEditedIsDouble = pieceEditedIsDouble;
      state.pieceEdited.mode = pieceEditedMode;
    }
  },

  pieceEditedCarriageCertChanged: (state: WritableDraft<InspectionEditState>, action: StringWithIndexChangedAction) => {
    if (state.pieceEdited !== null)
    {
      state.pieceEdited.carriageQcs[action.payload.index].qcNum = action.payload.value;

      const { pieceEditedIsDouble, pieceEditedMode } = checkDoublesAndGetMode(state);

      state.pieceEditedIsDouble = pieceEditedIsDouble;
      state.pieceEdited.mode = pieceEditedMode;
    }
  },

  pieceEditedSpecificationNumChanged: (state: WritableDraft<InspectionEditState>, action: StringChangedAction) => {
    if (state.pieceEdited !== null)
    {
      state.pieceEdited.specificationNum = action.payload;

      if (action.payload !== '' && action.payload.length < Constants.PIECE_SPECIFICATION_NUM_LENGTH_MIN)
      {
        state.pieceEditedSpecificationNumError = `Не менее ${Constants.PIECE_SPECIFICATION_NUM_LENGTH_MIN} символов`;
      }
      else
      {
        state.pieceEditedSpecificationNumError = '';
      }

      const { pieceEditedIsDouble, pieceEditedMode } = checkDoublesAndGetMode(state);

      state.pieceEditedIsDouble = pieceEditedIsDouble;
      state.pieceEdited.mode = pieceEditedMode;
    }
  },

  pieceEditedSpecificationNumErrorFixed: (state: WritableDraft<InspectionEditState>) => {
    state.pieceEditedSpecificationNumError = '';
  },

  pieceEditedIsAllPosesChanged: (state: WritableDraft<InspectionEditState>, action: BooleanChangedAction) => {
    if (state.pieceEdited !== null)
    {
      state.pieceEdited.isAllPoses = action.payload;
      state.pieceEdited.specificationPoses = action.payload ?
        []
        :
        [{
          isControl: true,
          pos: null,
          metalDelivered: null,
          volumeDeliveredTime: null,
          unit: {
            id: 2,
            title: 'T',
            isInteger: false,
          },
          daysOverdue: null,
          __volumeDeliveredTime: ''
        }];
      state.pieceEdited.metalDelivered = null;

      const { pieceEditedIsDouble, pieceEditedMode } = checkDoublesAndGetMode(state);

      state.pieceEditedIsDouble = pieceEditedIsDouble;
      state.pieceEdited.mode = pieceEditedMode;
    }
  },

  pieceEditedMetalDeliveredChanged: (state: WritableDraft<InspectionEditState>, action: PieceMetalDeliveredChangedAction) => {
    if (state.pieceEdited !== null)
    {
      state.pieceEdited.metalDelivered = action.payload;

      const { pieceEditedIsDouble, pieceEditedMode } = checkDoublesAndGetMode(state);

      state.pieceEditedIsDouble = pieceEditedIsDouble;
      state.pieceEdited.mode = pieceEditedMode;
    }
  },

  pieceEditedSpecificationPosAdded: (state: WritableDraft<InspectionEditState>) => {
    if (state.pieceEdited !== null)
    {
      state.pieceEdited.specificationPoses.push({
        isControl: true,
        metalDelivered: null,
        pos: null,
        volumeDeliveredTime: null,
        unit: {
          id: 2,
          title: 'T',
          isInteger: false,
        },
        daysOverdue: null,
        __volumeDeliveredTime: '',
      });

      const { pieceEditedIsDouble, pieceEditedMode } = checkDoublesAndGetMode(state);

      state.pieceEditedIsDouble = pieceEditedIsDouble;
      state.pieceEdited.mode = pieceEditedMode;
    }
  },

  pieceEditedSpecificationPosDeleted: (state: WritableDraft<InspectionEditState>, action: NumberChangedAction) => {
    if (state.pieceEdited !== null)
    {
      state.pieceEdited.specificationPoses.splice(action.payload, 1);

      const { pieceEditedIsDouble, pieceEditedMode } = checkDoublesAndGetMode(state);

      state.pieceEditedIsDouble = pieceEditedIsDouble;
      state.pieceEdited.mode = pieceEditedMode;
    }
  },

  pieceEditedSpecificationPosPosChanged: (state: WritableDraft<InspectionEditState>, action: StringWithIndexChangedAction) => {
    if (state.pieceEdited !== null)
    {
      const pos = action.payload.value === '' ? null : parseInt(action.payload.value, 10);
      state.pieceEdited.specificationPoses[action.payload.index].pos = pos;

      const { pieceEditedIsDouble, pieceEditedMode } = checkDoublesAndGetMode(state);

      state.pieceEditedIsDouble = pieceEditedIsDouble;
      state.pieceEdited.mode = pieceEditedMode;
    }
  },

  pieceEditedSpecificationPosMetalDeliveredChanged: (state: WritableDraft<InspectionEditState>, action: PieceMetalDeliveredWithIndexChangedAction) => {
    if (state.pieceEdited !== null)
    {
      state.pieceEdited.specificationPoses[action.payload.index].metalDelivered = action.payload.value;

      const { pieceEditedIsDouble, pieceEditedMode } = checkDoublesAndGetMode(state);

      state.pieceEditedIsDouble = pieceEditedIsDouble;
      state.pieceEdited.mode = pieceEditedMode;
    }
  },

  pieceEditedSpecificationPosVolumeDeliveredTimeChanged: (state: WritableDraft<InspectionEditState>, action: StringWithIndexChangedAction) => {
    if (state.pieceEdited !== null)
    {
      const volumeDeliveredTime = action.payload.value === '' ? null : parseFloat(action.payload.value);
      state.pieceEdited.specificationPoses[action.payload.index].volumeDeliveredTime = volumeDeliveredTime;
      state.pieceEdited.specificationPoses[action.payload.index].__volumeDeliveredTime = action.payload.value;

      const { pieceEditedIsDouble, pieceEditedMode } = checkDoublesAndGetMode(state);

      state.pieceEditedIsDouble = pieceEditedIsDouble;
      state.pieceEdited.mode = pieceEditedMode;
    }
  },

  pieceEditedSpecificationPosUnitChanged: (state: WritableDraft<InspectionEditState>, action: PieceSpecificationPosUnitWithIndexChangedAction) => {
    const { payload: { index, value } } = action;
    const pE = state.pieceEdited;

    if (pE !== null)
    {
      pE.specificationPoses[index].unit = value;
      if (value.isInteger && pE.specificationPoses[index].volumeDeliveredTime !== null)
      {
        pE.specificationPoses[index].volumeDeliveredTime = Math.floor(pE.specificationPoses[index].volumeDeliveredTime!);
        pE.specificationPoses[index].__volumeDeliveredTime = `${pE.specificationPoses[index].volumeDeliveredTime}`;
      }

      const { pieceEditedIsDouble, pieceEditedMode } = checkDoublesAndGetMode(state);

      state.pieceEditedIsDouble = pieceEditedIsDouble;
      pE.mode = pieceEditedMode;
    }
  },

  pieceEditedSpecificationPosDaysOverdueChanged: (state: WritableDraft<InspectionEditState>, action: StringWithIndexChangedAction) => {
    if (state.pieceEdited !== null)
    {
      const daysOverdue = action.payload.value === '' ? null : parseInt(action.payload.value, 10);
      state.pieceEdited.specificationPoses[action.payload.index].daysOverdue = daysOverdue;

      const { pieceEditedIsDouble, pieceEditedMode } = checkDoublesAndGetMode(state);

      state.pieceEditedIsDouble = pieceEditedIsDouble;
      state.pieceEdited.mode = pieceEditedMode;
    }
  },

  pieceEditedQmetIdChanged: (state: WritableDraft<InspectionEditState>, action: StringChangedAction) => {
    if (state.pieceEdited !== null)
    {
      state.pieceEdited.qmetId = action.payload === '' ? null : parseInt(action.payload, 10);

      if (action.payload !== '' && action.payload.length < Constants.PIECE_NUM_ID_LENGTH_MIN)
      {
        state.pieceEditedQmetIdError = `Не менее ${Constants.PIECE_NUM_ID_LENGTH_MIN} символов`;
      }
      else
      {
        state.pieceEditedQmetIdError = '';
      }

      if (state.generalEdited?.__fromEntryAccounting)
      {
        state.pieceEdited.pieceNumId = null;

        if (state.pieceEdited.qmetData)
        {
          state.pieceEdited.qmetData.productType = '';
          state.pieceEdited.qmetData.qcNum = null;
          state.pieceEdited.qmetData.group = null;
          state.pieceEdited.qmetData.qcState = QCState.NONE;
        }

        state.pieceEdited.heat = null;
        state.pieceEdited.piece = null;
        state.pieceEdited.groupAtt = null;
        state.pieceEdited.qcNum = null;
      }

      const { pieceEditedIsDouble, pieceEditedMode } = checkDoublesAndGetMode(state);

      state.pieceEditedIsDouble = pieceEditedIsDouble;
      state.pieceEdited.mode = pieceEditedMode;
    }
  },

  pieceEditedQmetIdErrorFixed: (state: WritableDraft<InspectionEditState>) => {
    state.pieceEditedQmetIdError = '';
  },

  pieceEditedHeatChanged: (state: WritableDraft<InspectionEditState>, action: StringChangedAction) => {
    if (state.pieceEdited !== null)
    {
      state.pieceEdited.heat = action.payload.trim() === '' ? null : action.payload.trim();
      
      const { pieceEditedIsDouble, pieceEditedMode } = checkDoublesAndGetMode(state);

      state.pieceEditedIsDouble = pieceEditedIsDouble;
      state.pieceEdited.mode = pieceEditedMode;
    }
  },

  pieceEditedHeatErrorFixed: (state: WritableDraft<InspectionEditState>) => {
    state.pieceEditedHeatError = '';
  },

  pieceEditedPieceChanged: (state: WritableDraft<InspectionEditState>, action: StringChangedAction) => {
    if (state.pieceEdited !== null)
    {
      state.pieceEdited.piece = action.payload.trim() === '' ? null : action.payload;
      
      const { pieceEditedIsDouble, pieceEditedMode } = checkDoublesAndGetMode(state);

      state.pieceEditedIsDouble = pieceEditedIsDouble;
      state.pieceEdited.mode = pieceEditedMode;
    }
  },

  pieceEditedPieceErrorFixed: (state: WritableDraft<InspectionEditState>) => {
    state.pieceEditedPieceError = '';
  },

  pieceEditedGroupAttChanged: (state: WritableDraft<InspectionEditState>, action: StringChangedAction) => {
    if (state.pieceEdited !== null)
    {
      state.pieceEdited.groupAtt = action.payload.trim() === '' ? null : action.payload.trim();
      
      const { pieceEditedIsDouble, pieceEditedMode } = checkDoublesAndGetMode(state);

      state.pieceEditedIsDouble = pieceEditedIsDouble;
      state.pieceEdited.mode = pieceEditedMode;
    }
  },

  pieceEditedGroupAttErrorFixed: (state: WritableDraft<InspectionEditState>) => {
    state.pieceEditedGroupAttError = '';
  },

  pieceEditedQCNumChanged: (state: WritableDraft<InspectionEditState>, action: StringChangedAction) => {
    if (state.pieceEdited !== null)
    {
      state.pieceEdited.qcNum = action.payload.trim() === '' ? null : action.payload.trim();

      const isKolpino = (state.generalAccount?.configs?.find((item) => item.code === 'KOLPINO')?.value ?? '0') === '1';

      if (isKolpino)
      {
        state.pieceEditedQCNumError = getKolpinoQCNumError(state.pieceEdited.productTypeKolpinoId, state.pieceEdited.qcNum);
      }
      
      const { pieceEditedIsDouble, pieceEditedMode } = checkDoublesAndGetMode(state);

      state.pieceEditedIsDouble = pieceEditedIsDouble;
      state.pieceEdited.mode = pieceEditedMode;
    }
  },

  pieceEditedQCNumErrorFixed: (state: WritableDraft<InspectionEditState>) => {
    state.pieceEditedQCNumError = '';
  },

  pieceEditedProductTypeKolpinoIdChanged: (state: WritableDraft<InspectionEditState>, action: NumberChangedAction) => {
    if (state.pieceEdited !== null)
    {
      state.pieceEdited.productTypeKolpinoId = action.payload;

      switch (state.pieceEdited.productTypeKolpinoId)
      {
        case ProductTypeKolpino.MECHANICAL_ENGINEERING_PART:
          state.pieceEdited.heat = null;
          state.pieceEdited.piece = null;
          state.pieceEdited.qmetId = null;
          break;
        case ProductTypeKolpino.PROCESSED_SHEET:
          state.pieceEdited.drawNum = null;
          state.pieceEdited.heat = null;
          state.pieceEdited.piece = null;
          state.pieceEdited.qmetId = null;
          break;
        case ProductTypeKolpino.UNPROCESSED_SHEET:
          state.pieceEdited.drawNum = null;
          state.pieceEdited.piece = null;
          state.pieceEdited.qmetId = null;
          break;
      }

      state.pieceEditedQCNumError = getKolpinoQCNumError(state.pieceEdited.productTypeKolpinoId, state.pieceEdited.qcNum);
      
      const { pieceEditedIsDouble, pieceEditedMode } = checkDoublesAndGetMode(state);

      state.pieceEditedIsDouble = pieceEditedIsDouble;
      state.pieceEdited.mode = pieceEditedMode;
    }
  },

  pieceEditedKolpinoPieceChanged: (state: WritableDraft<InspectionEditState>, action: KolpinoPieceChangedAction) => {
    if (state.pieceEdited !== null)
    {
      state.pieceEdited.piece = action.payload.piece;
      state.pieceEdited.qmetId = parseInt(action.payload.batchGp ?? '0', 10);

      pieceReducers.pieceEditedWeightUnitChanged(
        state,
        {
          type: `${SLICE_NAME}/pieceEditedWeightUnitChanged`,
          payload: action.payload.unit.id
        }
      );
     
      const { pieceEditedIsDouble, pieceEditedMode } = checkDoublesAndGetMode(state);

      state.pieceEditedIsDouble = pieceEditedIsDouble;
      state.pieceEdited.mode = pieceEditedMode;
    }
  },

  pieceEditedKolpinoDrawChanged: (state: WritableDraft<InspectionEditState>, action: KolpinoDrawChangedAction) => {
    if (state.pieceEdited !== null)
    {
      state.pieceEdited.drawNum = action.payload.drawNum;
      state.pieceEdited.piece = action.payload.piece;
      state.pieceEdited.qmetId = parseInt(action.payload.batchGp ?? '0', 10);

      pieceReducers.pieceEditedWeightUnitChanged(
        state,
        {
          type: `${SLICE_NAME}/pieceEditedWeightUnitChanged`,
          payload: action.payload.unit.id
        }
      );
     
      const { pieceEditedIsDouble, pieceEditedMode } = checkDoublesAndGetMode(state);

      state.pieceEditedIsDouble = pieceEditedIsDouble;
      state.pieceEdited.mode = pieceEditedMode;
    }
  },

  pieceEditedCustomFieldChanged: (state: WritableDraft<InspectionEditState>, action: CustomFieldChangedAction) => {
    if (state.pieceEdited !== null)
    {
      state.pieceEdited.fields = state.pieceEdited.fields
        .filter((item) => item.code !== action.payload.code)
        .concat(action.payload.value === '' ? [] : [action.payload]);

      state.pieceEdited.mode = getPieceMode(state);
    }
  },

  pieceEditedCustomFieldErrorFixed: (state: WritableDraft<InspectionEditState>, action: StringChangedAction) => {
    delete state.pieceEditedCustomFieldsErrors[action.payload];
  },

  pieceEditedIsMarriageShareChanged: (state: WritableDraft<InspectionEditState>, action: BooleanChangedAction) => {
    if (state.pieceEdited !== null)
    {
      state.pieceEditedIsMarriageShareExpanded = action.payload;

      if (!action.payload)
      {
        state.pieceEdited.weightBeforeProcessing = null;
        state.pieceEditedWeightBeforeProcessing = '';
        state.pieceEdited.weightAfterProcessing = null;
        state.pieceEditedWeightAfterProcessing = '';
        state.pieceEdited.marriageShare = null;
      }

      state.pieceEdited.mode = getPieceMode(state);
    }
  },

  pieceEditedWeightBeforeProcessingChanged: (state: WritableDraft<InspectionEditState>, action: StringChangedAction) => {
    if (state.pieceEdited !== null)
    {
      state.pieceEditedWeightBeforeProcessing = action.payload;
      state.pieceEdited.weightBeforeProcessing = action.payload === '' ? null : parseFloat(action.payload);

      if ((state.pieceEdited.weightBeforeProcessing ?? 0) === 0 || (state.pieceEdited.weightAfterProcessing ?? 0) === 0)
      {
        state.pieceEditedWeightAfterProcessingError = '';
        state.pieceEdited.marriageShare = null;
      }
      else
      {
        if (state.pieceEdited.weightBeforeProcessing! < state.pieceEdited.weightAfterProcessing!)
        {
          state.pieceEditedWeightAfterProcessingError = 'Вес после переработки не может быть больше веса до переработки';
          state.pieceEdited.marriageShare = null;
        }
        else
        {
          state.pieceEditedWeightAfterProcessingError = '';
          state.pieceEdited.marriageShare = parseFloat((100 * (1 - (state.pieceEdited.weightAfterProcessing!/state.pieceEdited.weightBeforeProcessing!))).toFixed(2));
        }
      }

      state.pieceEdited.mode = getPieceMode(state);
    }
  },

  pieceEditedWeightAfterProcessingChanged: (state: WritableDraft<InspectionEditState>, action: StringChangedAction) => {
    if (state.pieceEdited !== null)
    {
      state.pieceEditedWeightAfterProcessing = action.payload;
      state.pieceEdited.weightAfterProcessing = action.payload === '' ? null : parseFloat(action.payload);

      if ((state.pieceEdited.weightBeforeProcessing ?? 0) === 0 || (state.pieceEdited.weightAfterProcessing ?? 0) === 0)
      {
        state.pieceEditedWeightAfterProcessingError = '';
        state.pieceEdited.marriageShare = null;
      }
      else
      {
        if (state.pieceEdited.weightBeforeProcessing! < state.pieceEdited.weightAfterProcessing!)
        {
          state.pieceEditedWeightAfterProcessingError = 'Вес после переработки не может быть больше веса до переработки';
          state.pieceEdited.marriageShare = null;
        }
        else
        {
          state.pieceEditedWeightAfterProcessingError = '';
          state.pieceEdited.marriageShare = parseFloat((100 * (1 - (state.pieceEdited.weightAfterProcessing!/state.pieceEdited.weightBeforeProcessing!))).toFixed(2));
        }
      }

      state.pieceEdited.mode = getPieceMode(state);
    }
  },

  pieceEditedWeightChanged: (state: WritableDraft<InspectionEditState>, action: StringChangedAction) => {
    if (state.pieceEdited !== null)
    {
      state.pieceEditedWeight = action.payload;
      state.pieceEdited.weight = action.payload === '' ? null : parseFloat(action.payload);

      state.pieceEdited.mode = getPieceMode(state);
    }
  },

  pieceEditedWeightErrorFixed: (state: WritableDraft<InspectionEditState>) => {
    state.pieceEditedWeightError = '';
  },

  pieceEditedWeightUnitChanged: (state: WritableDraft<InspectionEditState>, action: NumberChangedAction) => {
    if (state.pieceEdited !== null)
    {
      const unit = state.generalUnitRefer.find((item) => item.id === action.payload)!;
      const details = unit.details.find((item) => item.isEntryAccounting === state.generalIsEntryAccounting)!
      state.pieceEdited.unit = {
        id: unit.id,
        title: unit.title,
        fieldTitle: details.fieldTitle,
        fieldHint: details.fieldHint,
      };

      if (unit.isInteger && state.pieceEditedWeight !== '')
      {
        state.pieceEditedWeight = `${Math.floor(parseFloat(state.pieceEditedWeight))}`;
        state.pieceEdited.weight = parseFloat(state.pieceEditedWeight);
      }

      Preferences.setPreferredWeightUnit(action.payload);

      state.pieceEdited.mode = getPieceMode(state);
    }
  },

  pieceEditedNoteChanged: (state: WritableDraft<InspectionEditState>, action: StringChangedAction) => {
    if (state.pieceEdited !== null)
    {
      state.pieceEdited.note = action.payload.trim() === '' ? null : action.payload;

      state.pieceEdited.mode = getPieceMode(state);
    }
  },

  pieceEditedNoteErrorFixed: (state: WritableDraft<InspectionEditState>) => {
    state.pieceEditedNoteError = '';
  },

  pieceEditedPhotoAdded: (state: WritableDraft<InspectionEditState>, action: PhotoAddedAction) => {
    if (state.pieceEdited !== null)
    {
      state.pieceEdited.files.push(action.payload);

      state.pieceEdited.mode = getPieceMode(state);
    }
  },

  pieceEditedPhotoDeleted: (state: WritableDraft<InspectionEditState>, action: StringChangedAction) => {
    if (state.pieceEdited !== null)
    {
      state.pieceEdited.files = state.pieceEdited.files
        .map((file) => {
          if (file.guid !== action.payload)
          {
            return file;
          }

          if (file.mode === types.inspection.PieceFileMode.EXIST)
          {
            return { ...file, mode: types.inspection.PieceFileMode.DEL };
          }
          
          return null;
        })
        .filter((file) => file !== null) as types.inspection.PieceFile[];

      Object.keys(state.pieceCVCheckFilesDuplicates).forEach((pieceGuid) => {
        state.pieceCVCheckFilesDuplicates[pieceGuid] = state.pieceCVCheckFilesDuplicates[pieceGuid].filter((fileGuid) => fileGuid !== action.payload);
      });
      Object.keys(state.pieceCVCheckFilesBlur).forEach((pieceGuid) => {
        state.pieceCVCheckFilesBlur[pieceGuid] = state.pieceCVCheckFilesBlur[pieceGuid].filter((fileGuid) => fileGuid !== action.payload);
      });

      state.pieceEdited.mode = getPieceMode(state);
    }
  },

  pieceEditedIsTopographyChanged: (state: WritableDraft<InspectionEditState>, action: BooleanChangedAction) => {
    if (state.pieceEdited !== null)
    {
      state.pieceEditedIsTopographyExpanded = action.payload;

      if (!action.payload)
      {
        state.pieceEdited.topography.defectSize = null;
        state.pieceEdited.topography.defectTpgIdSize = null;
        state.pieceEdited.topography.defectTpgTitleSize = null;
        state.pieceEdited.topography.defectStep = null;
        state.pieceEdited.topography.defectTpgIdStep = null;
        state.pieceEdited.topography.defectTpgTitleStep = null;
        state.pieceEdited.topography.defectDistanceFromEdge = null;
        state.pieceEdited.topography.defectTpgIdDistance = null;
        state.pieceEdited.topography.defectTpgTitleDistance = null;
        state.pieceEditedTopoSize = '';
        state.pieceEditedTopoStep = '';
        state.pieceEditedTopoDistance = '';
      }

      state.pieceEdited.mode = getPieceMode(state);
    }
  },

  pieceEditedTopoSizeIdChanged: (state: WritableDraft<InspectionEditState>, action: NumberChangedAction) => {
    if (state.pieceEdited !== null)
    {
      const id = action.payload;
      const title = state.generalTopographyRefer.find((item) => item.id === id)!.title;
      state.pieceEdited.topography.defectTpgIdSize = id;
      state.pieceEdited.topography.defectTpgTitleSize = title;

      state.pieceEdited.mode = getPieceMode(state);
    }
  },

  pieceEditedTopoSizeChanged: (state: WritableDraft<InspectionEditState>, action: StringChangedAction) => {
    if (state.pieceEdited !== null)
    {
      state.pieceEditedTopoSize = action.payload;
      state.pieceEdited.topography.defectSize = action.payload === '' ? null : parseFloat(action.payload);

      state.pieceEdited.mode = getPieceMode(state);
    }
  },

  pieceEditedTopoSizeErrorFixed: (state: WritableDraft<InspectionEditState>) => {
    state.pieceEditedTopoSizeError = '';
  },

  pieceEditedTopoStepIdChanged: (state: WritableDraft<InspectionEditState>, action: NumberChangedAction) => {
    if (state.pieceEdited !== null)
    {
      const id = action.payload;
      const title = state.generalTopographyRefer.find((item) => item.id === id)!.title;
      state.pieceEdited.topography.defectTpgIdStep = id;
      state.pieceEdited.topography.defectTpgTitleStep = title;

      state.pieceEdited.mode = getPieceMode(state);
    }
  },

  pieceEditedTopoStepChanged: (state: WritableDraft<InspectionEditState>, action: StringChangedAction) => {
    if (state.pieceEdited !== null)
    {
      state.pieceEditedTopoStep = action.payload;
      state.pieceEdited.topography.defectStep = action.payload === '' ? null : parseFloat(action.payload);

      state.pieceEdited.mode = getPieceMode(state);
    }
  },

  pieceEditedTopoStepErrorFixed: (state: WritableDraft<InspectionEditState>) => {
    state.pieceEditedTopoStepError = '';
  },

  pieceEditedTopoDistanceIdChanged: (state: WritableDraft<InspectionEditState>, action: NumberChangedAction) => {
    if (state.pieceEdited !== null)
    {
      const id = action.payload;
      const title = state.generalTopographyRefer.find((item) => item.id === id)!.title;
      state.pieceEdited.topography.defectTpgIdDistance = id;
      state.pieceEdited.topography.defectTpgTitleDistance = title;

      state.pieceEdited.mode = getPieceMode(state);
    }
  },

  pieceEditedTopoDistanceChanged: (state: WritableDraft<InspectionEditState>, action: StringChangedAction) => {
    if (state.pieceEdited !== null)
    {
      state.pieceEditedTopoDistance = action.payload;
      state.pieceEdited.topography.defectDistanceFromEdge = action.payload === '' ? null : parseFloat(action.payload);

      state.pieceEdited.mode = getPieceMode(state);
    }
  },

  pieceEditedTopoDistanceErrorFixed: (state: WritableDraft<InspectionEditState>) => {
    state.pieceEditedTopoDistanceError = '';
  },

  pieceEditedIsDoubleChanged: (state: WritableDraft<InspectionEditState>, action: BooleanChangedAction) => {
    state.pieceEditedIsDouble = action.payload;
  },

  pieceEditedExternalDoublesHidden: (state: WritableDraft<InspectionEditState>) => {
    state.pieceEditedExternalDoublesOpened = false;
  },

  pieceEditedExternalDoublesShow: (state: WritableDraft<InspectionEditState>) => {
    if (state.pieceEditedExternalDoublesList.length > 0)
    {
      state.pieceEditedExternalDoublesOpened = true;
    }
  },

  pieceEditedExternalDoublesClosed: (state: WritableDraft<InspectionEditState>) => {
    state.pieceEditedExternalDoublesOpened = false;
    state.pieceEditedExternalDoublesList = [];
  },

  pieceEditedCVCheckFilesResultClosed: (state: WritableDraft<InspectionEditState>) => {
    state.pieceEditedCVCheckFilesResultOpened = false;
    state.pieceEditedCVCheckFilesResultGroup = { };
    state.pieceEditedCVCheckFilesResultSingle = [];
  },

  pieceEditedSaveChanges: (state: WritableDraft<InspectionEditState>) => {
    const gE = state.generalEdited;
    const pE = state.pieceEdited;

    if (gE !== null && pE !== null)
    {
      const eD = gE.inspectionDefects.find((defect) => defect.guid === state.defectExpandedGuid) ?? null;

      if (eD !== null)
      {
        const piece = (eD.pieces || []).find((item) => item.guid === pE.guid) ?? null;

        if (piece === null)
        {
          if (eD.pieces === null)
          {
            eD.pieces = [pE]
          }
          else
          {
            eD.pieces.push(pE);
          }
        }
        else
        {
          eD.pieces = (eD.pieces || []).map((item) => item.guid === pE.guid ? { ...pE } : item);
        }
      }
    }

    state.pieceEditOpened = false;
    state.pieceEdited = null;
  },

  pieceCertDownloadClosed: (state: WritableDraft<InspectionEditState>) => {
    state.pieceCertDownloadOpened = false;
  },
}

const pieceSelectors = {
  selectPieceEditDialogTitle: createSelector(
    [
      (state: Pick<RootState, "inspectionEdit">, pieceGuid: string) => ({ state, pieceGuid })
    ],
    ({ state, pieceGuid }) => {
      const gE = state.inspectionEdit.generalEdited;

      if (gE === null)
      {
        return '';
      }

      const eD = gE.inspectionDefects.find((defect) => defect.guid === state.inspectionEdit.defectExpandedGuid) ?? null;
      
      if (eD === null)
      {
        return '';
      }

      const piece = (eD.pieces || []).find((piece) => piece.guid === pieceGuid) ?? null;

      if (piece === null)
      {
        return 'Добавление позиции';
      }

      return 'Изменение позиции';
    }
  ),

  selectPieceEditDialogAcceptText: createSelector(
    [
      (state: Pick<RootState, "inspectionEdit">, pieceGuid: string) => ({ state, pieceGuid })
    ],
    ({ state, pieceGuid }) => {
      const gE = state.inspectionEdit.generalEdited;

      if (gE === null)
      {
        return '';
      }

      const eD = gE.inspectionDefects.find((defect) => defect.guid === state.inspectionEdit.defectExpandedGuid) ?? null;
      
      if (eD === null)
      {
        return '';
      }

      const piece = (eD.pieces || []).find((piece) => piece.guid === pieceGuid) ?? null;

      if (piece === null)
      {
        return 'Добавить';
      }

      return 'Изменить';
    }
  ),

  selectPieceTitle: createSelector(
    [
      (state: Pick<RootState, "inspectionEdit">, pieceGuid: string) => ({ state, pieceGuid })
    ],
    ({ state, pieceGuid }) => {
      const gE = state.inspectionEdit.generalEdited;

      if (gE === null)
      {
        return '';
      }

      const eD = gE.inspectionDefects.find((defect) => defect.guid === state.inspectionEdit.defectExpandedGuid) ?? null;
      
      if (eD === null)
      {
        return '';
      }

      const piece = (eD.pieces || []).find((piece) => piece.guid === pieceGuid) ?? null;

      if (piece === null)
      {
        return '';
      }

      const isQualityOrService = [null, DefectType.QUALITY, DefectType.SERVICE].includes(eD.defectType);
      const isDirtyWagons = eD.defectType === DefectType.DIRTY_WAGONS;
      const isTerm = eD.defectType === DefectType.TERM;
      const productTypeKolpinoId = piece.productTypeKolpinoId;
      
      if (isQualityOrService)
      {
        if (piece.isPiece)
        {
          if (productTypeKolpinoId !== null)
          {
            switch (productTypeKolpinoId)
            {
              case ProductTypeKolpino.MECHANICAL_ENGINEERING_PART:
              case ProductTypeKolpino.PROCESSED_SHEET:
                return 'Лист / Сертификат';
              case ProductTypeKolpino.UNPROCESSED_SHEET:
                return 'Плавка / Партия / Сертификат';
            }
          }
          else
          {
            const isGroupAttNotNull = piece.groupAtt !== null && piece.groupAtt !== '';
            const isQCNumNotNull = piece.qcNum !== null && piece.qcNum !== '';

            return (isGroupAttNotNull || isQCNumNotNull) ?
              'Плавка / Партия атт / Сертификат'
              :
              'Плавка / Партия/Ед';
          }
        }

        return 'Идентификационный №';
      }

      if (isDirtyWagons)
      {
        return '№ вагона';
      }

      if (isTerm)
      {
        return '№ спецификации';
      }
    }
  ),

  selectPieceText: createSelector(
    [
      (state: Pick<RootState, "inspectionEdit">, pieceGuid: string) => ({ state, pieceGuid })
    ],
    ({ state, pieceGuid }) => {
      const gE = state.inspectionEdit.generalEdited;

      if (gE === null)
      {
        return '';
      }

      const eD = gE.inspectionDefects.find((defect) => defect.guid === state.inspectionEdit.defectExpandedGuid) ?? null;
      
      if (eD === null)
      {
        return '';
      }

      const piece = (eD.pieces || []).find((piece) => piece.guid === pieceGuid) ?? null;

      if (piece === null)
      {
        return '';
      }

      const account = state.inspectionEdit.generalAccount!;
      const isKolpino = (account.configs.find((item) => item.code === 'KOLPINO')?.value ?? '0') === '1';

      return getPieceTitle(piece, eD.defectType, isKolpino);
    }
  ),

  selectPieceEditedText: (state: Pick<RootState, "inspectionEdit">) => {
    const gE = state.inspectionEdit.generalEdited;

    if (gE === null)
    {
      return '';
    }

    const eD = gE.inspectionDefects.find((defect) => defect.guid === state.inspectionEdit.defectExpandedGuid) ?? null;

    if (eD === null)
    {
      return '';
    }

    const piece = state.inspectionEdit.pieceEdited;

    if (piece === null)
    {
      return '';
    }

    const account = state.inspectionEdit.generalAccount!;
    const isKolpino = (account.configs.find((item) => item.code === 'KOLPINO')?.value ?? '0') === '1';

    return getPieceTitle(piece, eD.defectType, isKolpino);
  },

  selectPieceIsError: createSelector(
    [
      (state: Pick<RootState, "inspectionEdit">, pieceGuid: string) => ({ state, pieceGuid })
    ],
    ({ state, pieceGuid }) => {
      const gE = state.inspectionEdit.generalEdited;

      if (gE === null)
      {
        return false;
      }

      const eD = gE.inspectionDefects.find((defect) => defect.guid === state.inspectionEdit.defectExpandedGuid) ?? null;
      
      if (eD === null)
      {
        return false;
      }

      const piece = (eD.pieces || []).find((piece) => piece.guid === pieceGuid) ?? null;

      if (piece === null)
      {
        return false;
      }

      const inspectionState = state.inspectionEdit.generalInspectionState;
      const isQualityOrService = [null, DefectType.QUALITY, DefectType.SERVICE].includes(eD.defectType);
      const isDirtyWagons = eD.defectType === DefectType.DIRTY_WAGONS;
      const isTerm = eD.defectType === DefectType.TERM;

      if (inspectionState === InspectionState.ERROR)
      {
        if (isQualityOrService && piece.pieceNumId === null)
        {
          return true;
        }

        if (isDirtyWagons && piece.carriageQcs.some((qc) => !qc.isControl))
        {
          return true;
        }

        if (isTerm && piece.specificationPoses.some((pos) => !pos.isControl))
        {
          return true;
        }
      }

      return false;
    }
  ),

  selectPieceIsSelected: createSelector(
    [
      (state: Pick<RootState, "inspectionEdit">, pieceGuid: string) => ({ state, pieceGuid })
    ],
    ({ state, pieceGuid }) => state.inspectionEdit.pieceSelectedGuid === pieceGuid
  ),

  selectPieceDeleteAllowed: (state: Pick<RootState, "inspectionEdit">) => {
    const inspectionState = state.inspectionEdit.generalInspectionState;
    const inspectionStatusCode = state.inspectionEdit.generalEdited!.status.code;
    const defectEdited = state.inspectionEdit.generalEdited?.inspectionDefects.find((defect) => defect.guid === state.inspectionEdit.defectExpandedGuid)
    const piecesCount = defectEdited?.pieces?.length ?? 0;

    return !(
      (
        inspectionState === InspectionState.EDIT
        &&
        (
          (
            inspectionStatusCode !== InspectionStatusCode.INTERNAL
            &&
            inspectionStatusCode !== InspectionStatusCode.INTERNAL_NO_TRANS_COMPLETED
          )
          ||
          piecesCount === 1
        )
      )
      ||
      inspectionState === InspectionState.CREATE
    );
  },

  selectPieceMarkedForDeleteGuids: (state: Pick<RootState, "inspectionEdit">) => state.inspectionEdit.pieceMarkedForDeleteGuids.join(';'),

  selectPieceIsMarkedForDelete: createSelector(
    [
      (state: Pick<RootState, "inspectionEdit">, pieceGuid: string) => ({ state, pieceGuid })
    ],
    ({ state, pieceGuid }) => state.inspectionEdit.pieceMarkedForDeleteGuids.includes(pieceGuid)
  ),

  selectPieceGuidsForExpandedDefect: (state: Pick<RootState, "inspectionEdit">) => {
    const gE = state.inspectionEdit.generalEdited;

    if (gE === null)
    {
      return '';
    }

    const eD = gE.inspectionDefects.find((defect) => defect.guid === state.inspectionEdit.defectExpandedGuid) ?? null;

    if (eD === null)
    {
      return '';
    }

    return (eD.pieces ?? []).filter((piece) => piece.mode !== PieceMode.DEL).map((piece) => piece.guid).join(';');
  },

  selectPiecePiecesForExpandedDefect: (state: Pick<RootState, "inspectionEdit">) => {
    const gE = state.inspectionEdit.generalEdited;

    if (gE === null)
    {
      return [];
    }

    const eD = gE.inspectionDefects.find((defect) => defect.guid === state.inspectionEdit.defectExpandedGuid) ?? null;

    if (eD === null)
    {
      return [];
    }

    return (eD.pieces ?? []).filter((piece) => piece.mode !== PieceMode.DEL);
  },

  selectPieceEditOpened: (state: Pick<RootState, "inspectionEdit">) => state.inspectionEdit.pieceEditOpened,

  selectPieceEditedIsModeNew: (state: Pick<RootState, "inspectionEdit">) => state.inspectionEdit.pieceEdited?.mode === PieceMode.NEW,

  selectPieceEditedIsDouble: (state: Pick<RootState, "inspectionEdit">) => state.inspectionEdit.pieceEditedIsDouble,

  selectPieceEditedGuid: (state: Pick<RootState, "inspectionEdit">) => state.inspectionEdit.pieceEdited?.guid ?? null,

  selectPieceEditedIsPiece: (state: Pick<RootState, "inspectionEdit">) => state.inspectionEdit.pieceEdited?.isPiece ?? false,

  selectPieceEditedIsLoadedFromQMET: (state: Pick<RootState, "inspectionEdit">) => (state.inspectionEdit.pieceEdited?.pieceNumId ?? null) !== null,

  selectPieceEditedCarriage: (state: Pick<RootState, "inspectionEdit">) => state.inspectionEdit.pieceEdited?.carriage ?? null,

  selectPieceEditedCarriageError: (state: Pick<RootState, "inspectionEdit">) => state.inspectionEdit.pieceEditedCarriageError,

  selectPieceEditedCarriageQcs: (state: Pick<RootState, "inspectionEdit">) => state.inspectionEdit.pieceEdited?.carriageQcs ?? null,

  selectPieceEditedSpecificationNum: (state: Pick<RootState, "inspectionEdit">) => state.inspectionEdit.pieceEdited?.specificationNum ?? null,

  selectPieceEditedCarriageQcsInError: (state: Pick<RootState, "inspectionEdit">) => state.inspectionEdit.pieceEditedCarriageQcsInError ?? null,

  selectPieceEditedSpecificationNumError: (state: Pick<RootState, "inspectionEdit">) => state.inspectionEdit.pieceEditedSpecificationNumError,
  
  selectPieceEditedMetalDelivered: (state: Pick<RootState, "inspectionEdit">) => state.inspectionEdit.pieceEdited?.metalDelivered ?? null,
  
  selectPieceEditedIsAllPoses: (state: Pick<RootState, "inspectionEdit">) => state.inspectionEdit.pieceEdited?.isAllPoses ?? null,

  selectPieceEditedSpecificationPoses: (state: Pick<RootState, "inspectionEdit">) => state.inspectionEdit.pieceEdited?.specificationPoses ?? null,

  selectPieceEditedSpecificationPosInError: (state: Pick<RootState, "inspectionEdit">) => state.inspectionEdit.pieceEditedSpecificationPosInError ?? null,

  selectPieceEditedQmetId: (state: Pick<RootState, "inspectionEdit">) => state.inspectionEdit.pieceEdited?.qmetId ?? null,

  selectPieceEditedQmetIdError: (state: Pick<RootState, "inspectionEdit">) => state.inspectionEdit.pieceEditedQmetIdError,

  selectPieceEditedProductType: (state: Pick<RootState, "inspectionEdit">) => state.inspectionEdit.pieceEdited?.qmetData?.productType ?? null,

  selectPieceEditedHeat: (state: Pick<RootState, "inspectionEdit">) => state.inspectionEdit.pieceEdited?.heat ?? null,

  selectPieceEditedHeatError: (state: Pick<RootState, "inspectionEdit">) => state.inspectionEdit.pieceEditedHeatError,

  selectPieceEditedPiece: (state: Pick<RootState, "inspectionEdit">) => state.inspectionEdit.pieceEdited?.piece ?? null,

  selectPieceEditedPieceError: (state: Pick<RootState, "inspectionEdit">) => state.inspectionEdit.pieceEditedPieceError,

  selectPieceEditedGroupAtt: (state: Pick<RootState, "inspectionEdit">) => state.inspectionEdit.pieceEdited?.groupAtt ?? null,

  selectPieceEditedGroupAttError: (state: Pick<RootState, "inspectionEdit">) => state.inspectionEdit.pieceEditedGroupAttError,

  selectPieceEditedIsSmc: (state: Pick<RootState, "inspectionEdit">) => state.inspectionEdit.pieceEdited?.isSmc ?? false,

  selectPieceEditedQCNum: (state: Pick<RootState, "inspectionEdit">) => state.inspectionEdit.pieceEdited?.qcNum ?? state.inspectionEdit.pieceEdited?.qmetData?.qcNum ?? null,

  selectPieceEditedQCState: (state: Pick<RootState, "inspectionEdit">) => state.inspectionEdit.pieceEdited?.qmetData?.qcState ?? null,

  selectPieceEditedProductTypeKolpinoId: (state: Pick<RootState, "inspectionEdit">) => state.inspectionEdit.pieceEdited?.productTypeKolpinoId ?? null,
  
  selectPieceEditedDrawNum: (state: Pick<RootState, "inspectionEdit">) => state.inspectionEdit.pieceEdited?.drawNum ?? null,

  selectPieceEditedQCNumError: (state: Pick<RootState, "inspectionEdit">) => state.inspectionEdit.pieceEditedQCNumError,

  selectPieceEditedCustomFields: (state: Pick<RootState, "inspectionEdit">) => state.inspectionEdit.pieceEdited?.fields ?? [],

  selectPieceEditedCustomFieldsErrors: (state: Pick<RootState, "inspectionEdit">) => state.inspectionEdit.pieceEditedCustomFieldsErrors,

  selectPieceEditedIsMarriageShareExpanded: (state: Pick<RootState, "inspectionEdit">) => state.inspectionEdit.pieceEditedIsMarriageShareExpanded,

  selectPieceEditedWeightBeforeProcessing: (state: Pick<RootState, "inspectionEdit">) => state.inspectionEdit.pieceEditedWeightBeforeProcessing,

  selectPieceEditedWeightAfterProcessing: (state: Pick<RootState, "inspectionEdit">) => state.inspectionEdit.pieceEditedWeightAfterProcessing,

  selectPieceEditedWeightAfterProcessingError: (state: Pick<RootState, "inspectionEdit">) => state.inspectionEdit.pieceEditedWeightAfterProcessingError,

  selectPieceEditedMarriageShare: (state: Pick<RootState, "inspectionEdit">) => state.inspectionEdit.pieceEdited?.marriageShare,

  selectPieceEditedWeight: (state: Pick<RootState, "inspectionEdit">) => state.inspectionEdit.pieceEditedWeight,

  selectPieceEditedWeightError: (state: Pick<RootState, "inspectionEdit">) => state.inspectionEdit.pieceEditedWeightError,

  selectPieceEditedUnit: (state: Pick<RootState, "inspectionEdit">) => state.inspectionEdit.pieceEdited?.unit ?? null,

  selectPieceEditedNote: (state: Pick<RootState, "inspectionEdit">) => state.inspectionEdit.pieceEdited?.note ?? null,

  selectPieceEditedNoteError: (state: Pick<RootState, "inspectionEdit">) => state.inspectionEdit.pieceEditedNoteError,

  selectPieceEditedIsTopographyExpanded: (state: Pick<RootState, "inspectionEdit">) => state.inspectionEdit.pieceEditedIsTopographyExpanded,

  selectPieceEditedTopoSizeId: (state: Pick<RootState, "inspectionEdit">) => state.inspectionEdit.pieceEdited?.topography?.defectTpgIdSize ?? null,

  selectPieceEditedTopoSizeTitle: (state: Pick<RootState, "inspectionEdit">) => state.inspectionEdit.pieceEdited?.topography?.defectTpgTitleSize ?? null,

  selectPieceEditedTopoSize: (state: Pick<RootState, "inspectionEdit">) => state.inspectionEdit.pieceEditedTopoSize,

  selectPieceEditedTopoSizeError: (state: Pick<RootState, "inspectionEdit">) => state.inspectionEdit.pieceEditedTopoSizeError,

  selectPieceEditedTopoStepId: (state: Pick<RootState, "inspectionEdit">) => state.inspectionEdit.pieceEdited?.topography?.defectTpgIdStep ?? null,

  selectPieceEditedTopoStepTitle: (state: Pick<RootState, "inspectionEdit">) => state.inspectionEdit.pieceEdited?.topography?.defectTpgTitleStep ?? null,

  selectPieceEditedTopoStep: (state: Pick<RootState, "inspectionEdit">) => state.inspectionEdit.pieceEditedTopoStep,

  selectPieceEditedTopoStepError: (state: Pick<RootState, "inspectionEdit">) => state.inspectionEdit.pieceEditedTopoStepError,

  selectPieceEditedTopoDistanceId: (state: Pick<RootState, "inspectionEdit">) => state.inspectionEdit.pieceEdited?.topography?.defectTpgIdDistance ?? null,

  selectPieceEditedTopoDistanceTitle: (state: Pick<RootState, "inspectionEdit">) => state.inspectionEdit.pieceEdited?.topography?.defectTpgTitleDistance ?? null,

  selectPieceEditedTopoDistance: (state: Pick<RootState, "inspectionEdit">) => state.inspectionEdit.pieceEditedTopoDistance,

  selectPieceEditedTopoDistanceError: (state: Pick<RootState, "inspectionEdit">) => state.inspectionEdit.pieceEditedTopoDistanceError,

  selectPieceEditedFiles: (state: Pick<RootState, "inspectionEdit">) => state.inspectionEdit.pieceEdited?.files ?? [],

  selectPieceEditedExternalDoublesOpened: (state: Pick<RootState, "inspectionEdit">) => state.inspectionEdit.pieceEditedExternalDoublesOpened,

  selectPieceEditedExternalDoublesList: (state: Pick<RootState, "inspectionEdit">) => state.inspectionEdit.pieceEditedExternalDoublesList,

  selectPieceEditedCVCheckFilesResultOpened: (state: Pick<RootState, "inspectionEdit">) => state.inspectionEdit.pieceEditedCVCheckFilesResultOpened,

  selectPieceEditedCVCheckFilesResultGroup: (state: Pick<RootState, "inspectionEdit">) => state.inspectionEdit.pieceEditedCVCheckFilesResultGroup,

  selectPieceEditedCVCheckFilesResultSingle: (state: Pick<RootState, "inspectionEdit">) => state.inspectionEdit.pieceEditedCVCheckFilesResultSingle,

  selectPieceEdited: (state: Pick<RootState, "inspectionEdit">) => state.inspectionEdit.pieceEdited,

  selectPieceEditedOriginal: (state: Pick<RootState, "inspectionEdit">) => {
    return ((state.inspectionEdit.generalEdited?.inspectionDefects ?? [])
      .find((item) => item.guid === state.inspectionEdit.defectExpandedGuid)?.pieces ?? [])
      .find((item) => item.guid === state.inspectionEdit.pieceEdited?.guid) ?? null;
  },

  selectPieceEditedGeneralOriginal: (state: Pick<RootState, "inspectionEdit">) => {
    return (state.inspectionEdit.generalOriginal?.inspectionDefects ?? [])
      .map((defect) => (defect.pieces ?? []).find((piece) => piece.guid === state.inspectionEdit.pieceEdited?.guid) ?? null)
      .filter((piece) => piece !== null)
      .pop() ?? null;
  },

  selectPieceCertDownloadOpened: (state: Pick<RootState, "inspectionEdit">) => state.inspectionEdit.pieceCertDownloadOpened,

  selectPieceCVCheckFilesAttempts: (state: Pick<RootState, "inspectionEdit">) => state.inspectionEdit.pieceCVCheckFilesAttempts,
  selectPieceCVCheckFilesDuplicates: (state: Pick<RootState, "inspectionEdit">) => state.inspectionEdit.pieceCVCheckFilesDuplicates,
  selectPieceCVCheckFilesBlur: (state: Pick<RootState, "inspectionEdit">) => state.inspectionEdit.pieceCVCheckFilesBlur,
}

const getPieceTitle = (piece: types.inspection.Piece, defectType: Nullable<DefectType>, isKolpino: boolean) => {
  const isGroupAttHasValue = piece.groupAtt !== null && piece.groupAtt !== '';
  const isQcNumHasValue = piece.qcNum !== null && piece.qcNum !== '';
  const isHeatHasValue = piece.heat !== null && piece.heat !== '';
  const isPieceHasValue = piece.piece !== null && piece.piece.trim() !== '';

  const isQualityOrService = [null, DefectType.QUALITY, DefectType.SERVICE].includes(defectType);
  const isDirtyWagons = defectType === DefectType.DIRTY_WAGONS;
  const isTerm = defectType === DefectType.TERM;
  const productTypeKolpinoId = piece.productTypeKolpinoId;

  const productType = (piece.qmetData.productType ?? '') !== '' ? `(${piece.qmetData.productType})` : '';

  if (isQualityOrService)
  {
    if (piece.isPiece)
    {
      if (isKolpino)
      {
        switch (productTypeKolpinoId)
        {
          case ProductTypeKolpino.MECHANICAL_ENGINEERING_PART:
          case ProductTypeKolpino.PROCESSED_SHEET:
            if (isPieceHasValue && isQcNumHasValue)
            {
              return `${piece.piece} / ${piece.qcNum} ${productType}`;
            }
            else
            {
              if (!isPieceHasValue && isQcNumHasValue)
              {
                return `Нет листа / ${piece.qcNum} ${productType}`;
              }
              else if (isPieceHasValue)
              {
                return `${piece.piece} / Нет сертиф ${productType}`;
              }
              else
              {
                return `Нет листа / Нет сертиф ${productType}`;
              }
            }
          case ProductTypeKolpino.UNPROCESSED_SHEET:
          default:
            if (isHeatHasValue && isGroupAttHasValue && isQcNumHasValue)
            {
              return `${piece.heat} / ${piece.groupAtt} / ${piece.qcNum} ${productType}`;
            }
            else
            {
              if (isHeatHasValue)
              {
                if (isGroupAttHasValue)
                {
                  return `${piece.heat} / ${piece.groupAtt} / Нет сертиф ${productType}`;
                }
                else
                {
                  return `${piece.heat} / Нет партии / ${piece.qcNum} ${productType}`;
                }
              }
              else
              {
                if (isGroupAttHasValue && isQcNumHasValue)
                {
                  return `Нет плавки / ${piece.groupAtt} / ${piece.qcNum} ${productType}`;
                }
                else
                {
                  if (isGroupAttHasValue)
                  {
                    return `Нет плавки / ${piece.groupAtt} / Нет сертиф ${productType}`;
                  }
                  else
                  {
                    return `Нет плавки / Нет партии / ${piece.qcNum} ${productType}`;
                  }
                }
              }
            }
        }
      }
      else
      {
        if (isGroupAttHasValue || isQcNumHasValue)
        {
          if (isHeatHasValue && isGroupAttHasValue && isQcNumHasValue)
          {
            return `${piece.heat} / ${piece.groupAtt} / ${piece.qcNum} ${productType}`;
          }
          else
          {
            if (isHeatHasValue)
            {
              if (isGroupAttHasValue)
              {
                return `${piece.heat} / ${piece.groupAtt} / Нет сертиф ${productType}`;
              }
              else
              {
                return `${piece.heat} / Нет партии атт / ${piece.qcNum} ${productType}`;
              }
            }
            else
            {
              if (isGroupAttHasValue && isQcNumHasValue)
              {
                return `Нет плавки / ${piece.groupAtt} / ${piece.qcNum} ${productType}`;
              }
              else
              {
                if (isGroupAttHasValue)
                {
                  return `Нет плавки / ${piece.groupAtt} / Нет сертиф ${productType}`;
                }
                else
                {
                  return `Нет плавки / Нет партии атт / ${piece.qcNum} ${productType}`;
                }
              }
            }
          }
        }
        else
        {
          if (isHeatHasValue && isPieceHasValue)
          {
              return `${piece.heat} / ${piece.piece} ${productType}`;
          }
          else
          {
            if (!isHeatHasValue && isPieceHasValue)
            {
              return `Нет плавки / ${piece.piece} ${productType}`;
            }
            else if (isHeatHasValue)
            {
              return `${piece.heat} / Нет партии/ед ${productType}`;
            }
            else
            {
              return `Нет плавки / Нет партии/ед ${productType}`;
            }
          }
        }
      }
    }
    else
    {
      if (piece.qmetId === null || piece.qmetId === 0)
      {
        return `Нет номера ${productType}`;
      }
      else
      {
        return `${piece.qmetId} ${productType}`;
      }
    }
  }

  if (isDirtyWagons)
  {
    return piece.carriage ?? 'Нет номера';
  }

  if (isTerm)
  {
    return piece.specificationNum ?? 'Нет номера';
  }
}

// #endregion

// #region ТРЕБОВАНИЯ

const isComplaintsEquals = (
  forSave: Nullable<types.inspection.Complaint>,
  old: Nullable<types.inspection.Complaint>,
  defectFiles: types.inspection.DefectFile[],
): boolean => {
  if (forSave !== null && old !== null)
  {
    let isEquals = true;

    const { nullOrEmptyStringTo, nullOrZeroNumberTo } = Util;

    const scrapPrice = forSave.scrapPrice ?? 0;
    const scrapPriceOld = old.scrapPrice ?? 0;
    const requirementRolledMetall = nullOrEmptyStringTo(forSave.requirementRolledMetall, 'X');
    const requirementRolledMetallOld = nullOrEmptyStringTo(old.requirementRolledMetall, 'X');
    const sumReqRolledMetall = forSave.sumReqRolledMetall ?? 0;
    const sumReqRolledMetallOld = old.sumReqRolledMetall ?? 0;
    const amountOfCompensation = forSave.amountOfCompensation ?? 0;
    const amountOfCompensationOld = old.amountOfCompensation ?? 0;
    const sale = forSave.sale ?? 0;
    const saleOld = old.sale ?? 0;
    const penalty = forSave.penalty ?? 0;
    const penaltyOld = old.penalty ?? 0;
    const otherReq = nullOrEmptyStringTo(forSave.otherReq, 'X');
    const otherReqOld = nullOrEmptyStringTo(old.otherReq, 'X');
    const fio = nullOrEmptyStringTo(forSave.fio, 'X');
    const fioOld = nullOrEmptyStringTo(old.fio, 'X');
    const enterprise = nullOrEmptyStringTo(forSave.enterprise, 'X');
    const enterpriseOld = nullOrEmptyStringTo(old.enterprise, 'X');
    const phone = nullOrEmptyStringTo(forSave.phone, 'X');
    const phoneOld = nullOrEmptyStringTo(old.phone, 'X');
    const email = nullOrEmptyStringTo(forSave.email, 'X');
    const emailOld = nullOrEmptyStringTo(old.email, 'X');
    const groupRevised = nullOrEmptyStringTo(forSave.groupRevised, 'X');
    const groupRevisedOld = nullOrEmptyStringTo(old.groupRevised, 'X');
    const groupRevisedValue = forSave.groupRevisedValue ?? 0;
    const groupRevisedValueOld = old.groupRevisedValue ?? 0;
    const groupUnrevised = nullOrEmptyStringTo(forSave.groupUnrevised, 'X');
    const groupUnrevisedOld = nullOrEmptyStringTo(old.groupUnrevised, 'X');
    const groupUnrevisedValue = forSave.groupUnrevisedValue ?? 0;
    const groupUnrevisedValueOld = old.groupUnrevisedValue ?? 0;

    if (
      !(
        forSave.typeComplaint === old.typeComplaint
        &&
        forSave.isToScrap === old.isToScrap
        &&
        scrapPrice === scrapPriceOld
        &&
        forSave.isEvaluateRolledMetal === old.isEvaluateRolledMetal
        &&
        requirementRolledMetall === requirementRolledMetallOld
        &&
        sumReqRolledMetall === sumReqRolledMetallOld
        &&
        forSave.isToOffsetCosts === old.isToOffsetCosts
        &&
        amountOfCompensation === amountOfCompensationOld
        &&
        forSave.isReturnOfRolledMetal === old.isReturnOfRolledMetal
        &&
        forSave.isSale === old.isSale
        &&
        sale === saleOld
        &&
        forSave.isRepresentative === old.isRepresentative
        &&
        forSave.isProvideResponse === old.isProvideResponse
        &&
        forSave.isIdentifyDangerous === old.isIdentifyDangerous
        &&
        forSave.isAnalizProblem === old.isAnalizProblem
        &&
        forSave.is8d === old.is8d
        &&
        forSave.isAddMetall === old.isAddMetall
        &&
        forSave.isInformDeadline === old.isInformDeadline
        &&
        forSave.isCompensatePenalty === old.isCompensatePenalty
        &&
        penalty === penaltyOld
        &&
        otherReq === otherReqOld
        &&
        fio === fioOld
        &&
        enterprise === enterpriseOld
        &&
        phone === phoneOld
        &&
        email === emailOld
        &&
        groupRevised === groupRevisedOld
        &&
        groupRevisedValue === groupRevisedValueOld
        &&
        groupUnrevised === groupUnrevisedOld
        &&
        groupUnrevisedValue === groupUnrevisedValueOld
        &&
        (
          (forSave.__initiatorAOSDSelected ?? false) === (old.__initiatorAOSDSelected ?? false)
        )
        &&
        (
          (forSave.__contactDataExpanded ?? false) === (old.__contactDataExpanded ?? false)
        )
      )
    )
    {
      isEquals = false;
    }

    if (isEquals)
    {
      if (
        (forSave.initiator === null && old.initiator !== null)
        ||
        (forSave.initiator !== null && old.initiator === null)
        ||
        (
          forSave.initiator !== null
          &&
          old.initiator !== null
          &&
          (
            forSave.initiator.enterpriseId !== old.initiator.enterpriseId
            ||
            nullOrEmptyStringTo(forSave.initiator.fio, 'X') !== nullOrEmptyStringTo(old.initiator.fio, 'X')
            ||
            nullOrEmptyStringTo(forSave.initiator.post, 'X') !== nullOrEmptyStringTo(old.initiator.post, 'X')
            ||
            nullOrZeroNumberTo(forSave.initiator.phone, -1) !== nullOrZeroNumberTo(old.initiator.phone, -1)
            ||
            nullOrEmptyStringTo(forSave.initiator.email, 'X') !== nullOrEmptyStringTo(old.initiator.email, 'X')
          )
        )
      )
      {
        isEquals = false;
      }
    }

    if (isEquals)
    {
      const isFilesChanged = defectFiles.some(
        (file) => file.type === DefectFileType.ADDITIONAL_COSTS && file.mode !== DefectFileMode.EXIST
      );

      if (isFilesChanged)
      {
        isEquals = false;
      }
    }
    
    return isEquals;
  }
  else
  {
    return ((forSave === null) && (old === null)); 
  }
};

// #region closeComplaintAsync
type CloseComplaintArgs = void;
type CloseComplaintResolve = {
  hasChanges: boolean;
};
export type CloseComplaintReject = void;
export const closeComplaintAsync = createAsyncThunk<
  CloseComplaintResolve,
  CloseComplaintArgs,
  {
    state: RootState,
    rejectValue: CloseComplaintReject,
  }
>(
  `${SLICE_NAME}/closeComplaintAsync`,
  async (_, thunkAPI) => {
    const state = thunkAPI.getState();

    // ПРОВЕРКА НА НАЛИЧИЕ ИЗМЕНЕНИЙ
    let isEquals = true;
    const edited = complaintSelectors.selectComplaintEdited(state);

    if (edited !== null)
    {
      const original = complaintSelectors.selectComplaintEditedOriginal(state);
      const defectGuid = defectSelectors.selectDefectExpandedGuid(state);
      const defectFiles = defectSelectors.selectDefectFiles(state, defectGuid)

      if ((edited.mode === ComplaintMode.NEW) && (original === null))
      {
        isEquals = false;
      }
      else
      {
        isEquals = isComplaintsEquals(edited, original, defectFiles ?? []);
      }
    }

    return {
      hasChanges: !isEquals
    }
  }
);
// #endregion

// #region checkComplaintBeforeAddAsync
type CheckComplaintBeforeAddArgs = void;
type CheckComplaintBeforeAddResolve = void;
export type CheckComplaintBeforeAddReject = string;
export const checkComplaintBeforeAddAsync = createAsyncThunk<
  CheckComplaintBeforeAddResolve,
  CheckComplaintBeforeAddArgs,
  {
    state: RootState,
    rejectValue: CheckComplaintBeforeAddReject,
  }
>(
  `${SLICE_NAME}/checkComplaintBeforeAddAsync`,
  async (args, thunkAPI) => {
    const state = thunkAPI.getState();

    //const defectGuid = defectSelectors.selectDefectExpandedGuid(state);
    const defectGuid = complaintSelectors.selectComplaintDefectGuid(state);
    const defectType = defectSelectors.selectDefectDefectType(state, defectGuid);
    const account = generalSelectors.selectGeneralAccount(state)!;
    const defectFiles = defectSelectors.selectDefectFiles(state, defectGuid) ?? [];
    const edited = complaintSelectors.selectComplaintEdited(state);
    const original = complaintSelectors.selectComplaintEditedOriginal(state);
    const complaintMode = complaintSelectors.selectComplaintEditedMode(state);
    const isChangeComplaintCosts = generalSelectors.selectGeneralIsChangeComplaintCosts(state);
    const isChangeToComplaintFinance = generalSelectors.selectGeneralIsChangeToComplaintFinance(state);
    const isChangeToComplaintAll = generalSelectors.selectGeneralIsChangeComplaintAll(state);
    const initiatorAOSDSelected = complaintSelectors.selectComplaintEditedInitiatorAOSDSelected(state);

    const resultError = getComplaintErrorMessage(
      account,
      defectType,
      defectFiles,
      edited,
      original,
      isChangeComplaintCosts,
      isChangeToComplaintFinance,
      isChangeToComplaintAll,
      initiatorAOSDSelected,
      complaintMode === 'edit',
    );

    if (resultError !== '')
    {
      return thunkAPI.rejectWithValue(resultError);
    }
  }
);
// #endregion

// #region addComplaintAsync
type AddComplaintArgs = {
  isTransferImmediately: boolean;
};
type AddComplaintResolve = {
  isTransferImmediately: boolean;
};
export type AddComplaintReject = string;
export const addComplaintAsync = createAsyncThunk<
  AddComplaintResolve,
  AddComplaintArgs,
  {
    state: RootState,
    rejectValue: AddComplaintReject,
  }
>(
  `${SLICE_NAME}/addComplaintAsync`,
  async (args, thunkAPI) => {
    const state = thunkAPI.getState();

    //const defectGuid = defectSelectors.selectDefectExpandedGuid(state);
    const defectGuid = complaintSelectors.selectComplaintDefectGuid(state);
    const defectType = defectSelectors.selectDefectDefectType(state, defectGuid);
    const account = generalSelectors.selectGeneralAccount(state)!;
    const defectFiles = defectSelectors.selectDefectFiles(state, defectGuid) ?? [];
    const edited = complaintSelectors.selectComplaintEdited(state);
    const original = complaintSelectors.selectComplaintEditedOriginal(state);
    const complaintMode = complaintSelectors.selectComplaintEditedMode(state);
    const isChangeComplaintCosts = generalSelectors.selectGeneralIsChangeComplaintCosts(state);
    const isChangeComplaintAll = generalSelectors.selectGeneralIsChangeComplaintAll(state);
    const isChangeToComplaintFinance = generalSelectors.selectGeneralIsChangeToComplaintFinance(state);
    const initiatorAOSDSelected = complaintSelectors.selectComplaintEditedInitiatorAOSDSelected(state);

    const resultError = getComplaintErrorMessage(
      account,
      defectType,
      defectFiles,
      edited,
      original,
      isChangeComplaintCosts,
      isChangeToComplaintFinance,
      isChangeComplaintAll,
      initiatorAOSDSelected,
      complaintMode === 'edit',
    );

    if (resultError !== '')
    {
      return thunkAPI.rejectWithValue(resultError);
    }

    return {
      isTransferImmediately: args.isTransferImmediately,
    }
  }
);
extraReducers.push((builder) => {
  builder
    .addCase(addComplaintAsync.fulfilled, (state, action) => {
      if (state.generalEdited !== null)
      {
        const defect = state.generalEdited.inspectionDefects.find((item) => item.guid === state.complaintDefectGuid) ?? null;

        if (defect !== null)
        {
          if (state.complaintEdited !== null)
          {
            state.complaintDefectGuid = '';
            state.complaintEditOpened = false;
            defect.complaint = { ...state.complaintEdited };
            defect.complaint.isTransferImmediately = action.payload.isTransferImmediately ? true : null;
            state.complaintEdited = null;
          }
        }
      }
    })
    .addCase(addComplaintAsync.rejected, (state, action) => {
    });
});
// #endregion

// #region replaceScrapPriceAsync
type ReplaceScrapPriceArgs = {
  enterpriseId: Nullable<number>;
  filialId: Nullable<number>;
};
type ReplaceScrapPriceResolve = {
  scrapPrice: number;
};
export type ReplaceScrapPriceReject = ApiError;
export const replaceScrapPriceAsync = createAsyncThunk<
  ReplaceScrapPriceResolve,
  ReplaceScrapPriceArgs,
  {
    state: RootState,
    rejectValue: ReplaceScrapPriceReject,
  }
>(
  `${SLICE_NAME}/replaceScrapPriceAsync`,
  async (args, thunkAPI) => {
    try
    {
      thunkAPI.dispatch(inspectionEditActions.generalOpStatusChanged(AsyncOpStatus.BUSY));
      thunkAPI.dispatch(inspectionEditActions.generalOpStatusLabelChanged('Получение цены лома'));
      
      const scrapPriceResult = await Api.getInspectionScrapPriceByEnterpriseId({
        enterpriseId: args.enterpriseId,
        filialId: args.filialId,
      });

      thunkAPI.dispatch(inspectionEditActions.generalOpStatusChanged(AsyncOpStatus.SUCCESS));

      return {
        scrapPrice: parseFloat(Util.selectDigitsAndDotFromStringN_M(`${scrapPriceResult.scrapPrice}`, 5, 2))
      };
    }
    catch (error)
    {
      thunkAPI.dispatch(inspectionEditActions.generalOpStatusChanged(AsyncOpStatus.ERROR));
      return thunkAPI.rejectWithValue(error as ApiError);
    }
  }
);
extraReducers.push((builder) => {
  builder
    .addCase(replaceScrapPriceAsync.fulfilled, (state, action) => {
      if (state.complaintEdited !== null)
      {
        state.complaintEdited.scrapPrice = action.payload.scrapPrice;
        state.complaintEditedScrapPrice = `${action.payload.scrapPrice}`;
      }
    })
    .addCase(replaceScrapPriceAsync.rejected, (state, action) => {
    });
});
// #endregion

const complaintReducers = {
  complaintEditOpened: (state: WritableDraft<InspectionEditState>) => {
    state.complaintEditOpened = true;
  },

  complaintEditClosed: (state: WritableDraft<InspectionEditState>) => {
    state.complaintEditOpened = false;
  },

  complaintForEditSelected: (state: WritableDraft<InspectionEditState>, action: StringChangedAction) => {
    if (state.generalEdited !== null)
    {
      const [mode, complaintGuid] = action.payload.split(':');

      const defect = state.generalEdited.inspectionDefects
        .find((item) => item.guid === complaintGuid);

      const defectRef = state.generalDefectRefer
        .find((item) => item.id === (defect?.defectId ?? -999999))

      const complaint = defect?.complaint ?? null;
      const markOnlyClaim = defectRef?.configs?.find((conf) => conf.code === 'ONLY_CLAIM')?.value ?? '0';
      const markProhibitionOfRemarks = defectRef?.configs?.find((conf) => conf.code === 'PROHIBITION_OF_REMARKS')?.value ?? '0';
      const isOnlyClaim = markOnlyClaim === '1' || markProhibitionOfRemarks === '1';
      const typeComplaintForAppend = isOnlyClaim ? ComplaintType.COMPLAINT : ComplaintType.REMARK;

      if (complaint === null)
      {
        state.complaintEdited = {
          typeComplaint: typeComplaintForAppend,
          isToScrap: null,
          scrapPrice: null,
          isEvaluateRolledMetal: null,
          requirementRolledMetall: null,
          sumReqRolledMetall: null,
          isToOffsetCosts: null,
          amountOfCompensation: null,
          isReturnOfRolledMetal: null,
          isSale: null,
          sale: null,
          isRepresentative: null,
          isProvideResponse: null,
          isIdentifyDangerous: null,
          isAnalizProblem: null,
          is8d: null,
          isAddMetall: null,
          isCompensatePenalty: null,
          penalty: null,
          isInformDeadline: null,
          otherReq: null,
          groupRevised: null,
          groupRevisedValue: null,
          groupUnrevised: null,
          groupUnrevisedValue: null,
          decision: null,
          fio: null,
          enterprise: null,
          phone: null,
          email: null,
          initiator: null,
          isTransferImmediately: null,
          mode: ComplaintMode.NEW,
          __initiatorAOSDSelected: false,
          __contactDataExpanded: false,
        };
        state.complaintEditedSale = '';
        state.complaintEditedScrapPrice = '';
        state.complaintEditedAmountOfCompensation = '';
        state.complaintEditedPenalty = '';
        state.complaintEditedSumReqRolledMetall = '';
        state.complaintEditedInitiatorAOSDSelected = false;
        state.complaintContactDataExpanded = false;
      }
      else
      {
        state.complaintEdited = { ...complaint };

        const cE = state.complaintEdited;

        state.complaintEditedSale = cE.sale === 0 ? '' : `${cE.sale ?? ''}`;
        state.complaintEditedScrapPrice = cE.scrapPrice === 0 ? '' : `${cE.scrapPrice ?? ''}`;
        state.complaintEditedAmountOfCompensation = cE.amountOfCompensation === 0 ? '' : `${cE.amountOfCompensation ?? ''}`;
        state.complaintEditedPenalty = cE.penalty === 0 ? '' : `${cE.penalty ?? ''}`;
        state.complaintEditedSumReqRolledMetall = cE.sumReqRolledMetall === 0 ? '' : `${cE.sumReqRolledMetall ?? ''}`;
        state.complaintEditedInitiatorAOSDSelected = (cE.__initiatorAOSDSelected ?? false) || cE.initiator !== null;
        state.complaintContactDataExpanded = (cE.__contactDataExpanded ?? false) ||
                                             [cE.fio, cE.enterprise, cE.phone, cE.email].some((v) => v !== null);
        state.complaintEdited.sale = cE.sale === 0 ? null : cE.sale;
        state.complaintEdited.groupRevisedValue = cE.groupRevisedValue === 0 ? null : cE.groupRevisedValue;
        state.complaintEdited.groupUnrevisedValue = cE.groupUnrevisedValue === 0 ? null : cE.groupUnrevisedValue;
        state.complaintEdited.mode = cE.mode === ComplaintMode.NEW ? ComplaintMode.NEW : ComplaintMode.EDIT;
      }

      state.complaintDefectGuid = complaintGuid;
      state.complaintEditedMode = mode as 'new' | 'edit' | 'tocomplaint' | 'tocomplaintfinance' | 'costs' | 'all' | 'view';
      state.complaintEditOpened = true;
    }
  },

  complaintEditedTypeComplaintChanged: (state: WritableDraft<InspectionEditState>, action: ComplaintTypeChangedAction) => {
    if (state.complaintEdited !== null)
    {
      state.complaintEdited.typeComplaint = action.payload;

      state.complaintEdited.isToScrap = null;
      state.complaintEdited.scrapPrice = null;
      state.complaintEdited.isEvaluateRolledMetal = null;
      state.complaintEdited.requirementRolledMetall = null;
      state.complaintEdited.sumReqRolledMetall = null;
      state.complaintEdited.isToOffsetCosts = null;
      state.complaintEdited.amountOfCompensation = null;

      const gE = state.generalEdited;

      if (gE !== null)
      {
        const eD = gE.inspectionDefects.find((defect) => defect.guid === state.defectExpandedGuid) ?? null;

        if (eD !== null)
        {
          eD.files = eD.files
            .map((file) => {
              if (file.type === types.inspection.DefectFileType.ADDITIONAL_COSTS)
              {
                return file.mode === types.inspection.DefectFileMode.NEW ?
                  null
                  :
                  { ...file, mode: types.inspection.DefectFileMode.DEL };
              }

              return file;
            })
            .filter((file) => file !== null) as types.inspection.DefectFile[];

          if (eD.mode !== types.inspection.DefectMode.NEW)
          {
            eD.mode = types.inspection.DefectMode.EDIT;
          }
        }
      }

      state.complaintEdited.isReturnOfRolledMetal = null;
      state.complaintEdited.isSale = null;
      state.complaintEdited.sale = null;
      state.complaintEdited.isRepresentative = null;
      state.complaintEdited.isProvideResponse = null;
      state.complaintEdited.isIdentifyDangerous = null;
      state.complaintEdited.isAnalizProblem = null;
      state.complaintEdited.is8d = null;
    }
  },

  complaintEditedIsToScrapChanged: (state: WritableDraft<InspectionEditState>, action: BooleanChangedAction) => {
    if (state.complaintEdited !== null)
    {
      state.complaintEdited.isToScrap = action.payload || null;

      if (!action.payload)
      {
        state.complaintEdited.scrapPrice = null;
        state.complaintEditedScrapPrice = '';
      }
    }
  },

  complaintEditedScrapPriceChanged: (state: WritableDraft<InspectionEditState>, action: StringChangedAction) => {
    if (state.complaintEdited !== null)
    {
      state.complaintEditedScrapPrice = action.payload;
      state.complaintEdited.scrapPrice = action.payload === '' ? null : parseFloat(action.payload);
    }
  },

  complaintEditedIsToOffsetCostsChanged: (state: WritableDraft<InspectionEditState>, action: BooleanChangedAction) => {
    if (state.complaintEdited !== null)
    {
      state.complaintEdited.isToOffsetCosts = action.payload || null;

      if (!action.payload)
      {
        state.complaintEdited.amountOfCompensation = null;
        state.complaintEditedAmountOfCompensation = '';
      }
    }
  },

  complaintEditedAmountOfCompensationChanged: (state: WritableDraft<InspectionEditState>, action: StringChangedAction) => {
    if (state.complaintEdited !== null)
    {
      state.complaintEditedAmountOfCompensation = action.payload;
      state.complaintEdited.amountOfCompensation = action.payload === '' ? null : parseFloat(action.payload);
    }
  },

  complaintEditedIsReturnOfRolledMetalChanged: (state: WritableDraft<InspectionEditState>, action: BooleanChangedAction) => {
    if (state.complaintEdited !== null)
    {
      state.complaintEdited.isReturnOfRolledMetal = action.payload || null;
    }
  },

  complaintEditedIsSaleChanged: (state: WritableDraft<InspectionEditState>, action: BooleanChangedAction) => {
    if (state.complaintEdited !== null)
    {
      state.complaintEdited.isSale = action.payload || null;

      if (!action.payload)
      {
        state.complaintEdited.sale = null;
        state.complaintEditedSale = '';
      }
    }
  },

  complaintEditedSaleChanged: (state: WritableDraft<InspectionEditState>, action: StringChangedAction) => {
    if (state.complaintEdited !== null)
    {
      state.complaintEditedSale = action.payload;
      state.complaintEdited.sale = action.payload === '' ? null : parseFloat(action.payload);
    }
  },

  complaintEditedIsEvaluateRolledMetalChanged: (state: WritableDraft<InspectionEditState>, action: BooleanChangedAction) => {
    if (state.complaintEdited !== null)
    {
      state.complaintEdited.isEvaluateRolledMetal = action.payload || null;

      if (!action.payload)
      {
        state.complaintEdited.requirementRolledMetall = null;
        state.complaintEdited.sumReqRolledMetall = null;
        state.complaintEditedSumReqRolledMetall = '';
      }
    }
  },

  complaintEditedRequirementRolledMetallChanged: (state: WritableDraft<InspectionEditState>, action: StringChangedAction) => {
    if (state.complaintEdited !== null)
    {
      state.complaintEdited.requirementRolledMetall = action.payload;
    }
  },

  complaintEditedSumReqRolledMetallChanged: (state: WritableDraft<InspectionEditState>, action: StringChangedAction) => {
    if (state.complaintEdited !== null)
    {
      state.complaintEditedSumReqRolledMetall = action.payload;
      state.complaintEdited.sumReqRolledMetall = action.payload === '' ? null : parseFloat(action.payload);
    }
  },

  complaintEditedIsRepresentativeChanged: (state: WritableDraft<InspectionEditState>, action: BooleanChangedAction) => {
    if (state.complaintEdited !== null)
    {
      state.complaintEdited.isRepresentative = action.payload || null;
    }
  },

  complaintEditedIsProvideResponseChanged: (state: WritableDraft<InspectionEditState>, action: BooleanChangedAction) => {
    if (state.complaintEdited !== null)
    {
      state.complaintEdited.isProvideResponse = action.payload || null;
    }
  },

  complaintEditedIsIdentifyDangerousChanged: (state: WritableDraft<InspectionEditState>, action: BooleanChangedAction) => {
    if (state.complaintEdited !== null)
    {
      state.complaintEdited.isIdentifyDangerous = action.payload || null;
    }
  },

  complaintEditedIsAnalizProblemChanged: (state: WritableDraft<InspectionEditState>, action: BooleanChangedAction) => {
    if (state.complaintEdited !== null)
    {
      state.complaintEdited.isAnalizProblem = action.payload || null;
    }
  },

  complaintEditedIs8dChanged: (state: WritableDraft<InspectionEditState>, action: BooleanChangedAction) => {
    if (state.complaintEdited !== null)
    {
      state.complaintEdited.is8d = action.payload || null;
    }
  },

  complaintEditedIsAddMetallChanged: (state: WritableDraft<InspectionEditState>, action: BooleanChangedAction) => {
    if (state.complaintEdited !== null)
    {
      state.complaintEdited.isAddMetall = action.payload || null;
    }
  },

  complaintEditedIsInformDeadlineChanged: (state: WritableDraft<InspectionEditState>, action: BooleanChangedAction) => {
    if (state.complaintEdited !== null)
    {
      state.complaintEdited.isInformDeadline = action.payload || null;
    }
  },

  complaintEditedIsCompensatePenaltyChanged: (state: WritableDraft<InspectionEditState>, action: BooleanChangedAction) => {
    if (state.complaintEdited !== null)
    {
      state.complaintEdited.isCompensatePenalty = action.payload || null;

      if (!action.payload)
      {
        state.complaintEdited.penalty = null;
        state.complaintEditedPenalty = '';
      }
    }
  },

  complaintEditedPenaltyChanged: (state: WritableDraft<InspectionEditState>, action: StringChangedAction) => {
    if (state.complaintEdited !== null)
    {
      state.complaintEditedPenalty = action.payload;
      state.complaintEdited.penalty = action.payload === '' ? null : parseFloat(action.payload);
    }
  },

  complaintEditedOtherReqChanged: (state: WritableDraft<InspectionEditState>, action: StringChangedAction) => {
    if (state.complaintEdited !== null)
    {
      state.complaintEdited.otherReq = action.payload === '' ? null : action.payload;
    }
  },

  complaintEditedInitiatorAOSDSelectedChanged: (state: WritableDraft<InspectionEditState>, action: BooleanChangedAction) => {
    if (state.complaintEdited !== null)
    {
      state.complaintEditedInitiatorAOSDSelected = action.payload;
      state.complaintEdited.__initiatorAOSDSelected = action.payload;
      if (!action.payload)
      {
        state.complaintEdited.initiator = null;
      }
    }
  },

  complaintEditedInitiatorEnterpriseIdChanged: (state: WritableDraft<InspectionEditState>, action: NumberChangedAction) => {
    if (state.complaintEdited !== null)
    {
      if (state.complaintEdited.initiator === null)
      {
        state.complaintEdited.initiator = {
          enterpriseId: -1,
          enterpriseTitle: '',
          email: '',
          fio: '',
          post: null,
          phone: null,
        }
      }
      const enterprise = state.generalEnterpriseRefer.find((item) => item.id === action.payload);
      const enterpriseTitle = enterprise?.title ?? '';
      state.complaintEdited.initiator.enterpriseId = action.payload;
      state.complaintEdited.initiator.enterpriseTitle = enterpriseTitle;
      state.complaintEdited.initiator.email = enterprise?.email ?? '';
      state.complaintEdited.initiator.fio = enterprise?.fio ?? '';
      state.complaintEdited.initiator.phone = null;
      state.complaintEdited.initiator.post = null;
    }
  },

  complaintEditedInitiatorFIOChanged: (state: WritableDraft<InspectionEditState>, action: StringChangedAction) => {
    if (state.complaintEdited !== null)
    {
      if (state.complaintEdited.initiator === null)
      {
        state.complaintEdited.initiator = {
          enterpriseId: -1,
          enterpriseTitle: '',
          email: '',
          fio: '',
          post: null,
          phone: null,
        }
      }
      state.complaintEdited.initiator.fio = action.payload;
    }
  },

  complaintEditedInitiatorPostChanged: (state: WritableDraft<InspectionEditState>, action: StringChangedAction) => {
    if (state.complaintEdited !== null)
    {
      if (state.complaintEdited.initiator === null)
      {
        state.complaintEdited.initiator = {
          enterpriseId: -1,
          enterpriseTitle: '',
          email: '',
          fio: '',
          post: null,
          phone: null,
        }
      }
      state.complaintEdited.initiator.post = action.payload === '' ? null : action.payload;
    }
  },

  complaintEditedInitiatorPhoneChanged: (state: WritableDraft<InspectionEditState>, action: StringChangedAction) => {
    if (state.complaintEdited !== null)
    {
      const phone = Util.selectDigitsFromString(action.payload);
      if (state.complaintEdited.initiator === null)
      {
        state.complaintEdited.initiator = {
          enterpriseId: -1,
          enterpriseTitle: '',
          email: '',
          fio: '',
          post: null,
          phone: null,
        }
      }
      state.complaintEdited.initiator.phone = phone === '' ? null : parseInt(phone, 10);
    }
  },

  complaintEditedInitiatorEmailChanged: (state: WritableDraft<InspectionEditState>, action: StringChangedAction) => {
    if (state.complaintEdited !== null)
    {
      if (state.complaintEdited.initiator === null)
      {
        state.complaintEdited.initiator = {
          enterpriseId: -1,
          enterpriseTitle: '',
          email: '',
          fio: '',
          post: null,
          phone: null,
        }
      }
      state.complaintEdited.initiator.email = action.payload;
    }
  },

  complaintContactDataExpandedChanged: (state: WritableDraft<InspectionEditState>, action: BooleanChangedAction) => {
    if (state.complaintEdited !== null)
    {
      state.complaintContactDataExpanded = action.payload;
      state.complaintEdited.__contactDataExpanded = action.payload;

      if (!action.payload)
      {
        state.complaintEdited.fio = null;
        state.complaintEdited.enterprise = null;
        state.complaintEdited.phone = null;
        state.complaintEdited.email = null;
      }
    }
  },

  complaintEditedFIOChanged: (state: WritableDraft<InspectionEditState>, action: StringChangedAction) => {
    if (state.complaintEdited !== null)
    {
      state.complaintEdited.fio = action.payload === '' ? null : action.payload;
    }
  },

  complaintEditedEnterpriseChanged: (state: WritableDraft<InspectionEditState>, action: StringChangedAction) => {
    if (state.complaintEdited !== null)
    {
      state.complaintEdited.enterprise = action.payload === '' ? null : action.payload;
    }
  },

  complaintEditedPhoneChanged: (state: WritableDraft<InspectionEditState>, action: StringChangedAction) => {
    if (state.complaintEdited !== null)
    {
      state.complaintEdited.phone = action.payload === '' ? null : action.payload;
    }
  },

  complaintEditedEmailChanged: (state: WritableDraft<InspectionEditState>, action: StringChangedAction) => {
    if (state.complaintEdited !== null)
    {
      state.complaintEdited.email = action.payload === '' ? null : action.payload;
    }
  },

  complaintEditedGroupRevisedChanged: (state: WritableDraft<InspectionEditState>, action: StringChangedAction) => {
    if (state.complaintEdited !== null)
    {
      state.complaintEdited.groupRevised = action.payload === '' ? null : action.payload;
    }
  },

  complaintEditedGroupRevisedValueChanged: (state: WritableDraft<InspectionEditState>, action: StringChangedAction) => {
    if (state.complaintEdited !== null)
    {
      state.complaintEdited.groupRevisedValue = action.payload === '' ? null : parseInt(action.payload, 10);
    }
  },

  complaintEditedGroupUnrevisedChanged: (state: WritableDraft<InspectionEditState>, action: StringChangedAction) => {
    if (state.complaintEdited !== null)
    {
      state.complaintEdited.groupUnrevised = action.payload === '' ? null : action.payload;
    }
  },

  complaintEditedGroupUnrevisedValueChanged: (state: WritableDraft<InspectionEditState>, action: StringChangedAction) => {
    if (state.complaintEdited !== null)
    {
      state.complaintEdited.groupUnrevisedValue = action.payload === '' ? null : parseInt(action.payload, 10);
    }
  },

}

const complaintSelectors = {
  selectComplaintEditOpened: (state: Pick<RootState, "inspectionEdit">) => state.inspectionEdit.complaintEditOpened,
  selectComplaintDefectGuid: (state: Pick<RootState, "inspectionEdit">) => state.inspectionEdit.complaintDefectGuid,
  selectComplaintEdited: (state: Pick<RootState, "inspectionEdit">) => state.inspectionEdit.complaintEdited,
  selectComplaintEditedOriginal: (state: Pick<RootState, "inspectionEdit">) => {
    return (state.inspectionEdit.generalEdited?.inspectionDefects ?? [])
      .find((item) => item.guid === state.inspectionEdit.complaintDefectGuid)?.complaint ?? null;
  },
  selectComplaintEditedTypeComplaint: (state: Pick<RootState, "inspectionEdit">) => state.inspectionEdit.complaintEdited?.typeComplaint ?? null,
  selectComplaintEditedIsToScrap: (state: Pick<RootState, "inspectionEdit">) => state.inspectionEdit.complaintEdited?.isToScrap ?? null,
  selectComplaintEditedScrapPrice: (state: Pick<RootState, "inspectionEdit">) => state.inspectionEdit.complaintEditedScrapPrice ?? null,
  selectComplaintEditedIsEvaluateRolledMetal: (state: Pick<RootState, "inspectionEdit">) => state.inspectionEdit.complaintEdited?.isEvaluateRolledMetal ?? null,
  selectComplaintEditedRequirementRolledMetall: (state: Pick<RootState, "inspectionEdit">) => state.inspectionEdit.complaintEdited?.requirementRolledMetall ?? null,
  selectComplaintEditedSumReqRolledMetall: (state: Pick<RootState, "inspectionEdit">) => state.inspectionEdit.complaintEditedSumReqRolledMetall,
  selectComplaintEditedIsToOffsetCosts: (state: Pick<RootState, "inspectionEdit">) => state.inspectionEdit.complaintEdited?.isToOffsetCosts ?? null,
  selectComplaintEditedAmountOfCompensation: (state: Pick<RootState, "inspectionEdit">) => state.inspectionEdit.complaintEditedAmountOfCompensation,
  selectComplaintEditedIsReturnOfRolledMetal: (state: Pick<RootState, "inspectionEdit">) => state.inspectionEdit.complaintEdited?.isReturnOfRolledMetal ?? null,
  selectComplaintEditedIsSale: (state: Pick<RootState, "inspectionEdit">) => state.inspectionEdit.complaintEdited?.isSale ?? null,
  selectComplaintEditedSale: (state: Pick<RootState, "inspectionEdit">) => state.inspectionEdit.complaintEditedSale,
  selectComplaintEditedIsRepresentative: (state: Pick<RootState, "inspectionEdit">) => state.inspectionEdit.complaintEdited?.isRepresentative ?? null,
  selectComplaintEditedIsProvideResponse: (state: Pick<RootState, "inspectionEdit">) => state.inspectionEdit.complaintEdited?.isProvideResponse ?? null,
  selectComplaintEditedIsIdentifyDangerous: (state: Pick<RootState, "inspectionEdit">) => state.inspectionEdit.complaintEdited?.isIdentifyDangerous ?? null,
  selectComplaintEditedIsAnalizProblem: (state: Pick<RootState, "inspectionEdit">) => state.inspectionEdit.complaintEdited?.isAnalizProblem ?? null,
  selectComplaintEditedIs8d: (state: Pick<RootState, "inspectionEdit">) => state.inspectionEdit.complaintEdited?.is8d ?? null,
  selectComplaintEditedIsAddMetall: (state: Pick<RootState, "inspectionEdit">) => state.inspectionEdit.complaintEdited?.isAddMetall ?? null,
  selectComplaintEditedIsInformDeadline: (state: Pick<RootState, "inspectionEdit">) => state.inspectionEdit.complaintEdited?.isInformDeadline ?? null,
  selectComplaintEditedIsCompensatePenalty: (state: Pick<RootState, "inspectionEdit">) => state.inspectionEdit.complaintEdited?.isCompensatePenalty ?? null,
  selectComplaintEditedPenalty: (state: Pick<RootState, "inspectionEdit">) => state.inspectionEdit.complaintEditedPenalty,
  selectComplaintEditedIsOtherReqVisible: (state: Pick<RootState, "inspectionEdit">) => 
    (state.inspectionEdit.complaintEdited?.isToScrap ?? false)
    ||
    (state.inspectionEdit.complaintEdited?.isEvaluateRolledMetal ?? false)
    ||
    (state.inspectionEdit.complaintEdited?.isToOffsetCosts ?? false)
    ||
    (state.inspectionEdit.complaintEdited?.isReturnOfRolledMetal ?? false)
    ||
    (state.inspectionEdit.complaintEdited?.isSale ?? false)
    ||
    (state.inspectionEdit.complaintEdited?.isRepresentative ?? false)
    ||
    (state.inspectionEdit.complaintEdited?.isProvideResponse ?? false)
    ||
    (state.inspectionEdit.complaintEdited?.isIdentifyDangerous ?? false)
    ||
    (state.inspectionEdit.complaintEdited?.isAnalizProblem ?? false)
    ||
    (state.inspectionEdit.complaintEdited?.is8d ?? false)
    ||
    (state.inspectionEdit.complaintEdited?.isAddMetall ?? false)
    ||
    (state.inspectionEdit.complaintEdited?.isInformDeadline ?? false)
    ||
    (state.inspectionEdit.complaintEdited?.isCompensatePenalty ?? false),
  selectComplaintEditedOtherReq: (state: Pick<RootState, "inspectionEdit">) => state.inspectionEdit.complaintEdited?.otherReq ?? null,
  selectComplaintEditedInitiatorAOSDSelected: (state: Pick<RootState, "inspectionEdit">) => state.inspectionEdit.complaintEditedInitiatorAOSDSelected,
  selectComplaintEditedInitiatorEnterpriseId: (state: Pick<RootState, "inspectionEdit">) => state.inspectionEdit.complaintEdited?.initiator?.enterpriseId ?? -1,
  selectComplaintEditedInitiatorFIO: (state: Pick<RootState, "inspectionEdit">) => state.inspectionEdit.complaintEdited?.initiator?.fio ?? null,
  selectComplaintEditedInitiatorPost: (state: Pick<RootState, "inspectionEdit">) => state.inspectionEdit.complaintEdited?.initiator?.post ?? null,
  selectComplaintEditedInitiatorPhone: (state: Pick<RootState, "inspectionEdit">) => state.inspectionEdit.complaintEdited?.initiator?.phone ?? null,
  selectComplaintEditedInitiatorEmail: (state: Pick<RootState, "inspectionEdit">) => state.inspectionEdit.complaintEdited?.initiator?.email ?? null,
  selectComplaintEditedContactDataExpanded: (state: Pick<RootState, "inspectionEdit">) => state.inspectionEdit.complaintContactDataExpanded,
  selectComplaintEditedMode: (state: Pick<RootState, "inspectionEdit">) => state.inspectionEdit.complaintEditedMode,
  selectComplaintEditedFIO: (state: Pick<RootState, "inspectionEdit">) => state.inspectionEdit.complaintEdited?.fio ?? null,
  selectComplaintEditedEnterprise: (state: Pick<RootState, "inspectionEdit">) => state.inspectionEdit.complaintEdited?.enterprise ?? null,
  selectComplaintEditedPhone: (state: Pick<RootState, "inspectionEdit">) => state.inspectionEdit.complaintEdited?.phone ?? null,
  selectComplaintEditedEmail: (state: Pick<RootState, "inspectionEdit">) => state.inspectionEdit.complaintEdited?.email ?? null,
  selectComplaintEditedGroupRevised: (state: Pick<RootState, "inspectionEdit">) => state.inspectionEdit.complaintEdited?.groupRevised ?? null,
  selectComplaintEditedGroupRevisedValue: (state: Pick<RootState, "inspectionEdit">) => state.inspectionEdit.complaintEdited?.groupRevisedValue ?? null,
  selectComplaintEditedGroupUnrevised: (state: Pick<RootState, "inspectionEdit">) => state.inspectionEdit.complaintEdited?.groupUnrevised ?? null,
  selectComplaintEditedGroupUnrevisedValue: (state: Pick<RootState, "inspectionEdit">) => state.inspectionEdit.complaintEdited?.groupUnrevisedValue ?? null,
  selectComplaintEditedDecision: (state: Pick<RootState, "inspectionEdit">) => state.inspectionEdit.complaintEdited?.decision ?? null,
}

// #endregion

// #region СОХРАНЕНИЕ ОСМОТРА

// #region saveInspectionAsync

const getSpecificationPosErrorMessage = (
  specificationPos: types.inspection.PieceSpecificationPosition,
): string => {
  let isErrorPos = false;
  let isErrorMetalDelivered = false;
  let isErrorVolumeDeliveredTime = false;
  let message = '';

  if ((specificationPos.pos ?? 0) === 0)
  {
    isErrorPos = true;
  }

  if (specificationPos.metalDelivered === null)
  {
    isErrorMetalDelivered = true;
  }

  if (specificationPos.volumeDeliveredTime === null)
  {
    isErrorVolumeDeliveredTime = true;
  }

  if (isErrorPos || isErrorMetalDelivered || isErrorVolumeDeliveredTime)
  {
    if (isErrorPos)
    {
      message = 'Позиция';
    }

    if (isErrorMetalDelivered)
    {
      if (message === '')
      {
        message = 'Металл доставлен';
      }
      else
      {
        message = `${message}, Металл доставлен`;
      }
    }

    if (isErrorVolumeDeliveredTime)
    {
      if (message === '')
      {
        message = 'Объем, не поставленный в срок';
      }
      else
      {
        message = `${message}, Объем, не поставленный в срок`;
      }
    }
  }
  else
  {
    message = '';
  }

  return message;
}

const getCarriageQcErrorMessage = (
  carriageQc: types.inspection.PieceCarriageQc,
): string => {
  let isErrorQcNum = false;
  let message = '';

  if (carriageQc.qcNum === null || carriageQc.qcNum.trim() === '')
  {
    isErrorQcNum = true;
  }

  if (isErrorQcNum)
  {
    message = '№ сертификата качества';
  }

  return message;
}

const getPieceErrorMessage = (
  piece: types.inspection.Piece,
  allPieces: types.inspection.Piece[],
  isEntryAccounting: boolean,
  isWireRod: boolean,
  // isDefect = true - проверки на дефекте. В этом случае надо выводить, на каком УЕ ошибка
  isDefect: boolean,
  account: types.auth.Account,
  defectType: Nullable<types.inspection.DefectType>,
  defect: Nullable<types.refers.DefectReferElement>,
  inspectionState: types.inspection.InspectionState,
  // isEditMode = true - осмотр открыли для редактирования. В таком случае, если не было
  // фото общего вида, то не нужно проверять его на обязательность.
  isEditMode: boolean
) => {
  const { DefectType, PieceFileType, PieceFileMode } = types.inspection;
  const { AccountRole, FieldTypeUsage, FieldType } = types.auth;

  const isQualityService = [null, DefectType.QUALITY, DefectType.SERVICE].includes(defectType);
  const isDirtyWagons = defectType === DefectType.DIRTY_WAGONS;
  const isTerm = defectType === DefectType.TERM;

  const isKolpino = (account.configs.find((item) => item.code === 'KOLPINO')?.value ?? '0') === '1';

  let isErrorHeat = false;
  let isErrorPiece = false;
  let isErrorGroupAtt = false;
  let isErrorQcNum = false;
  let isErrorPieceWireRod = false;
  let isErrorOnlyHeatWireRod = false;
  let isErrorKolpinoProductType = false;
  let isErrorKolpinoDrawNum = false;
  let isErrorPieceIdentity = false;
  let isErrorMetalDelivered = false;
  let isErrorSpecDetail = false;
  let isErrorCarrDetail = false;
  let isErrorWeight = false;
  let isErrorWeightControlKg = false;
  let isErrorWeightControlT = false;
  let isErrorWeightControlSht = false;
  let isErrorWeightElevation = false;
  let isErrorField = false;
  let isErrorSizeField = false;
  let isErrorPhoto = true;
  let specDetailError = '';
  let carrDetailError = '';

  if (piece.isPiece)
  {
    const heatEmpty = piece.heat == null || piece.heat.trim() === '';
    const pieceEmpty = piece.piece === null || piece.piece.trim() === '';
    const groupAttEmpty = piece.groupAtt === null || piece.groupAtt.trim() === '';
    const qcNumEmpty = piece.qcNum === null || piece.qcNum.trim() === '';

    if (isKolpino)
    {
      if (groupAttEmpty)
      {
        isErrorGroupAtt = true;
      }

      if (qcNumEmpty)
      {
        isErrorQcNum = true;
      }

      if (piece.productTypeKolpinoId === null)
      {
        isErrorKolpinoProductType = true;
      }
      else
      {
        switch (piece.productTypeKolpinoId)
        {
          case ProductTypeKolpino.MECHANICAL_ENGINEERING_PART:
            if (piece.drawNum === null)
            {
              isErrorKolpinoDrawNum = true;
            }
            break;

          case ProductTypeKolpino.PROCESSED_SHEET:
            if (piece.piece === null)
            {
              isErrorPiece = true;
            }
            break;

          default:
        }
      }
    }
    else
    {
      if (isWireRod)
      {
        if (heatEmpty && pieceEmpty && groupAttEmpty && qcNumEmpty)
        {
          isErrorPieceWireRod = true;
        }
        else if (!heatEmpty && pieceEmpty && groupAttEmpty && qcNumEmpty)
        {
          isErrorOnlyHeatWireRod = true;
        }
        else if ((!groupAttEmpty || !qcNumEmpty) && (groupAttEmpty || qcNumEmpty))
        {
          if (groupAttEmpty)
          {
            isErrorGroupAtt = true;
          }

          if (qcNumEmpty)
          {
            isErrorQcNum = true;
          }
        }
        else if (heatEmpty && !pieceEmpty)
        {
          isErrorHeat = true;
        }
      }
      else
      {
        if (heatEmpty)
        {
          isErrorHeat = true;
        }

        if (pieceEmpty)
        {
          isErrorPiece = true;
        }
      }
    }
  }
  else
  {
    if (isQualityService)
    {
      if (
        piece.qmetId === null
        ||
        piece.qmetId === 0
        ||
        (
          piece.qmetId.toString().length < Constants.PIECE_NUM_ID_LENGTH_MIN
          ||
          piece.qmetId.toString().length > Constants.PIECE_NUM_ID_LENGTH_MAX
        )
      )
      {
        isErrorPieceIdentity = true;
      }
    }
    
    if (isTerm)
    {
      const specificationNumEmpty = piece.specificationNum === null || piece.specificationNum.trim() === '';

      if (specificationNumEmpty || piece.specificationNum!.length < Constants.PIECE_SPECIFICATION_NUM_LENGTH_MIN)
      {
        isErrorPieceIdentity = true;
      }

      if (!piece.isAllPoses)
      {
        isErrorSpecDetail = piece.specificationPoses === null || piece.specificationPoses.length === 0;

        if (piece.specificationPoses !== null && !isErrorSpecDetail)
        {
          for (let specificationPos of piece.specificationPoses)
          {
            const errorMessage = getSpecificationPosErrorMessage(specificationPos);

            if (errorMessage !== '')
            {
              specDetailError = errorMessage;
              isErrorSpecDetail = true;
              break;
            }
          }
        }
      }
    }
    
    if (isDirtyWagons)
    {
      const carriageEmpty = piece.carriage === null || piece.carriage.trim() === '';

      if (carriageEmpty || piece.carriage!.length < Constants.PIECE_CARRIAGE_LENGTH_MIN)
      {
        isErrorPieceIdentity = true;
      }

      isErrorCarrDetail = piece.carriageQcs === null || piece.carriageQcs.length === 0;

      if (piece.carriageQcs !== null && !isErrorCarrDetail)
      {
        for (let carriageQc of piece.carriageQcs)
        {
          const errorMessage = getCarriageQcErrorMessage(carriageQc);

          if (errorMessage !== '')
          {
            carrDetailError = errorMessage;
            isErrorCarrDetail = true;
            break;
          }
        }
      }
    }
  }

  if (isTerm && piece.isAllPoses)
  {
    if (piece.metalDelivered === null)
    {
      isErrorMetalDelivered = true;
    }
  }

  const pWeight = piece.weight;
  const pUnitId = piece.unit?.id;
  const pActualWeight = piece.qmetData?.actualWeight;

  if (
    !isEntryAccounting
    &&
    account.role !== AccountRole.Contractor
    &&
    isQualityService
  )
  {
    if (pWeight === null || pWeight === 0)
    {
      isErrorWeight = true
    }
  }

  if (!isErrorWeight)
  {
    if (
      pUnitId === 1
      &&
      pWeight !== null
      &&
      (pWeight < 1 || pWeight > 100000)
    )
    {
      isErrorWeightControlKg = true;
    }
    else if (
      pUnitId === 2
      &&
      pWeight !== null
      &&
      (pWeight < 0.001 || pWeight > 100)
    )
    {
      isErrorWeightControlT = true;
    }
    else if (
      pUnitId === 3
      &&
      pWeight != null
      &&
      (pWeight > 100000)
    )
    {
      isErrorWeightControlSht = true;
    }
  }

  if (
    !isEntryAccounting
    &&
    (account.role !== AccountRole.Contractor)
    &&
    ((pWeight ?? 0) > 0)
    &&
    ((pActualWeight ?? 0) > 0)
    &&
    pUnitId !== 3
    &&
    (pUnitId === 2 ? Math.round((pWeight ?? 0) * 1000) : pWeight ?? 0) > Math.round((pActualWeight ?? 0) * 1000)
  )
  {
    isErrorWeightElevation = true
  }

  const emptyRequiredFields: string[] = [];
  const badSizeFields: string[] = [];

  if (isQualityService)
  {
    const { All, Defect, EntryAccounting } = FieldTypeUsage;
    account.fields
      .filter((field) => [All, (isEntryAccounting ? EntryAccounting : Defect)].includes(field.typeUsage))
      .filter((field) => !field.isOptional)
      .forEach((aField) => {
        const pField = piece.fields.find((field) => aField.id === field.id) ?? null;

        if (pField === null || pField.value === null || pField.value.trim() === '')
        {
          emptyRequiredFields.push(aField.title);
          isErrorField = true;
        }
        else
        {
          if (aField.fieldType === FieldType.String && aField.size !== null && aField.size > 0)
          {
            if (pField.value.length > aField.size)
            {
              badSizeFields.push(aField.title);
              isErrorSizeField = true;
            }
          }
        }
      });
  }

  /**
   * Алгоритм определения обязательности фото общего вида следующий:
   * По-умолчанию мы предполагаем, что ошибка есть, а значит нам нужно определить случаи, когда ее нет
   * 1. Если у дефекта есть метка NO_ATTACH = 1, то проверку на обязательность фото делать не нужно.
   * 2. Осмотры по срокам и входной учет - фото не обязательно.
   * 3. Для остальных случаев обязательно с одним исключением - если мы открыли на редактирование осмотр
   *    и он был без фото общего вида изначально (ЦЕС так может сделать в АСУК, им разрешено, для них
   *    собственно и исключение)
   */
  const noAttachCfg = defect?.configs?.find((item) => item.code === "NO_ATTACH")?.value ?? "0";
  if (noAttachCfg === "1") {
    isErrorPhoto = false;
  } else if (isTerm || isEntryAccounting)
  {
    isErrorPhoto = false;
  }
  else
  {
    const generalPhotos = (piece.files ?? []).filter((file) => file.type === PieceFileType.GENERAL);

    if (generalPhotos.length > 0)
    {
      const generalPhoto = generalPhotos.find((file) => file.guid !== null && file.mode !== PieceFileMode.DEL) ?? null;

      if (generalPhoto !== null)
      {
        isErrorPhoto = false;
      }
    }
    else
    {
      if (isEditMode)
      {
        isErrorPhoto = false;
      }
    }
  }

  const isControlDoubleInDefect = isEditedPieceHasDouble(inspectionState, defectType, piece, allPieces, isKolpino);

  if (
    (
      isErrorHeat
      ||
      isErrorPiece
      ||
      isErrorGroupAtt
      ||
      isErrorQcNum
      ||
      isErrorPieceWireRod
      ||
      isErrorOnlyHeatWireRod
      ||
      isErrorPieceIdentity
      ||
      isErrorKolpinoProductType
      ||
      isErrorKolpinoDrawNum
      ||
      isErrorMetalDelivered
      ||
      isErrorSpecDetail
      ||
      isErrorCarrDetail
      ||
      isErrorWeight
      ||
      isErrorWeightControlKg
      ||
      isErrorWeightControlT
      ||
      isErrorWeightControlSht
      ||
      isErrorWeightElevation
      ||
      isErrorField
      ||
      isErrorSizeField
      ||
      isErrorPhoto
    )
    ||
    isControlDoubleInDefect
  )
  {
    let aMessage: string[] = [];
    let aMessageSize: string[] = [];
    let messageWeight = '';
    let messageDouble = '';

    isErrorHeat && aMessage.push('№ плавки');
    isErrorPiece && aMessage.push(isKolpino ? '№ листа' : '№ рулона/листа/трубы');
    isErrorGroupAtt && aMessage.push(isKolpino ? '№ партии по маркировке' : '№ партии аттестации');
    isErrorQcNum && aMessage.push('№ сертификата качества');
    isErrorPieceWireRod && aMessage.push('№ плавки и № рулона/листа/трубы или № партии аттестации и № сертификата качества');
    isErrorOnlyHeatWireRod && aMessage.push('№ рулона/листа/трубы или № партии аттестации и № сертификата качества');
    isErrorKolpinoProductType && aMessage.push('Вид продукции');
    isErrorKolpinoDrawNum && aMessage.push('№ чертежа');

    if (isErrorPieceIdentity)
    {
      isQualityService && aMessage.push('Идентификационный номер');
      isDirtyWagons && aMessage.push('Номер вагона');
      isTerm && aMessage.push('Номер спецификации');
    }

    isErrorMetalDelivered && aMessage.push('Металл доставлен');
    isErrorSpecDetail && Util.nullOrEmptyStringTo(specDetailError, 'X') !== 'X' && aMessage.push(specDetailError);
    isErrorCarrDetail && Util.nullOrEmptyStringTo(carrDetailError, 'X') !== 'X' && aMessage.push(carrDetailError);
    isErrorWeight && aMessage.push('Количество брака');

    if (isErrorField)
    {
      aMessage.push(...emptyRequiredFields);
    }

    isErrorPhoto && aMessage.push('Фото общего вида');

    if (isErrorSizeField)
    {
      aMessageSize.push(...badSizeFields);
    }

    if (isErrorWeightControlKg)
    {
      messageWeight = `${piece.unit?.fieldTitle ?? 'Вес'} не должен быть менее 1 кг и не более 100000 кг, а Вы пытаетесь ввести ${piece.weight}`;
    }

    if (isErrorWeightControlT)
    {
      messageWeight = `${piece.unit?.fieldTitle ?? 'Вес'} не должен быть менее 0.001 т и не более 100 т, а Вы пытаетесь ввести ${piece.weight}`;
    }

    if (isErrorWeightControlSht) {
      messageWeight = `${piece.unit?.fieldTitle ?? 'Количество'} не должно быть более 100000 шт, а Вы пытаетесь ввести ${piece.weight}`;
    }

    if (isErrorWeightElevation)
    {
        if (messageWeight === '')
        {
          messageWeight = `Количество брака превышает вес нетто ${piece.qmetData?.actualWeight}т`;
        }
        else
        {
          messageWeight = `${messageWeight}. Количество брака превышает вес нетто ${piece.qmetData?.actualWeight}т`;
        }
    }

    if (isControlDoubleInDefect)
    {
      messageDouble = `Позиция с идентификационным номером ${piece.qmetId} уже введена по дефекту`;
    }

    let messageAll = '';

    if (aMessage.length > 0)
    {
        messageAll = `Заполните обязательные поля: ${aMessage.join(', ')}`;
    }

    if (aMessageSize.length > 0)
    {
      if (messageAll === '')
      {
        messageAll = `Превышена размерность поля: ${aMessageSize.join(', ')}`;
      }
      else
      {
        messageAll = `${messageAll}. Превышена размерность поля: ${aMessageSize.join(', ')}`;
      }
    }

    if (messageWeight !== '')
    {
      if (messageAll === '')
      {
        messageAll = messageWeight;
      }
      else
      {
        messageAll = `${messageAll}. ${messageWeight}`;
      }
    }

    if (messageDouble !== '')
    {
      if (messageAll === '')
      {
        messageAll = messageDouble;
      }
      else
      {
        messageAll = `${messageAll}. ${messageDouble}`;
      }
    }

    if (isDefect)
    {
      if (isTerm)
      {
        messageAll = `Спецификация ${getPieceTitle(piece, defectType, isKolpino)} - ${messageAll}`;
      }
      else if (isDirtyWagons)
      {
        messageAll = `Вагон ${getPieceTitle(piece, defectType, isKolpino)} - ${messageAll}`;
      }
      else
      {
        messageAll = `УЕ ${getPieceTitle(piece, defectType, isKolpino)} - ${messageAll}`;
      }
    }

    return messageAll;
  }
  
  return '';
}

const getComplaintErrorMessage = (
  account: types.auth.Account,
  defectType: Nullable<DefectType>,
  defectFiles: types.inspection.DefectFile[],
  edited: Nullable<types.inspection.Complaint>,
  original: Nullable<types.inspection.Complaint>,
  isChangeComplaintCosts: boolean,
  isChangeToComplaintFinance: boolean,
  isChangeToComplaintAll: boolean,
  initiatorAOSDSelected: boolean,
  isEditMode: boolean,
) => {
  let resultError = '';

  const isQualityOrService = [null, DefectType.QUALITY, DefectType.SERVICE].includes(defectType);
  const isDirtyWagons = defectType === DefectType.DIRTY_WAGONS;
  const isTerm = defectType === DefectType.TERM;

  const typeComplaint = edited?.typeComplaint ?? null;

  const isToScrap = edited?.isToScrap ?? false;
  const isEvaluateRolledMetal = edited?.isEvaluateRolledMetal ?? false;
  const isToOffsetCosts = edited?.isToOffsetCosts ?? false;
  const isReturnOfRolledMetal = edited?.isReturnOfRolledMetal ?? false;
  const isSale = edited?.isSale ?? false;
  const isRepresentative = edited?.isRepresentative ?? false;
  const isProvideResponse = edited?.isProvideResponse ?? false;
  const isIdentifyDangerous = edited?.isIdentifyDangerous ?? false;
  const isAnalizProblem = edited?.isAnalizProblem ?? false;
  const is8d = edited?.is8d ?? false;
  const isAddMetall = edited?.isAddMetall ?? false;
  const isCompensatePenalty = edited?.isCompensatePenalty ?? false;
  const isInformDeadline = edited?.isInformDeadline ?? false;

  const otherReq = edited?.otherReq ?? '';

  const scrapPrice = `${edited?.scrapPrice ?? ''}`;
  const amountOfCompensation = `${edited?.amountOfCompensation ?? ''}`;
  const penalty = `${edited?.penalty ?? ''}`;
  const sale = `${edited?.sale ?? ''}`;
  const sumReqRolledMetall = `${edited?.sumReqRolledMetall ?? ''}`;

  const isFileAdded = defectFiles.filter((file) => file.type === DefectFileType.ADDITIONAL_COSTS && file.mode !== DefectFileMode.DEL).length > 0;
  const isFilesChanged = defectFiles.some((file) => file.type === DefectFileType.ADDITIONAL_COSTS && file.mode !== DefectFileMode.EXIST);
  
  const requirementRolledMetall = edited?.requirementRolledMetall ?? null;
  const email = edited?.email ?? null;

  const qsRequestedRequirements = (
    isToScrap ||
    isEvaluateRolledMetal ||
    isToOffsetCosts ||
    isReturnOfRolledMetal ||
    isSale ||
    isRepresentative ||
    isProvideResponse ||
    isIdentifyDangerous ||
    isAnalizProblem ||
    is8d
  );

  const dwRequestedRequirements = (
    isToScrap ||
    isEvaluateRolledMetal ||
    isToOffsetCosts ||
    isReturnOfRolledMetal ||
    isSale ||
    isRepresentative ||
    isProvideResponse ||
    isAnalizProblem
  );

  const tRequestedRequirements = (
    isAddMetall ||
    isCompensatePenalty ||
    isInformDeadline ||
    isSale ||
    isEvaluateRolledMetal
  );

  if (
    typeComplaint === ComplaintType.COMPLAINT &&
    (
      (isQualityOrService && !qsRequestedRequirements) ||
      (isDirtyWagons && !dwRequestedRequirements) ||
      (isTerm && !tRequestedRequirements)
    )
  )
  {
    return 'Заполните хотя бы одно поле в разделе «Требования и запрашиваемые действия к поставщику». ';
  }

  if (isQualityOrService || isDirtyWagons)
  {
    if ([isToScrap, isEvaluateRolledMetal, isReturnOfRolledMetal, isSale].filter((item) => item).length > 1)
    {
      return 'Нельзя одновременно выбрать требования: «Уценить м/п до цены лома», «Уценить м/п», «Осуществить возврат м/п», «Предоставить скидку на забракованную партию проката». Выберите только одно из этих требований. ';
    }
  }

  if (isTerm)
  {
    if ([isEvaluateRolledMetal, isSale].filter((item) => item).length > 1)
    {
      return 'Нельзя одновременно выбрать требования: «Уценить м/п», «Предоставить скидку на забракованную партию проката». Выберите только одно из этих требований. ';
    }
  }

  if (isQualityOrService || isDirtyWagons)
  {
    const isScrapPriceEmpty = scrapPrice === '' || scrapPrice === '0';

    if (!isChangeComplaintCosts && isToScrap && isScrapPriceEmpty)
    {
      resultError += 'Цена лома должна быть заполнена. ';
    }

    if (!isChangeComplaintCosts && isToScrap && !isScrapPriceEmpty && Util.countDigitsBeforeDot(scrapPrice) < 4)
    {
      resultError += 'Цена лома должна быть не менее 4х знаков до запятой. ';
    }
  }

  if (isQualityOrService || isDirtyWagons)
  {
    const isAmountOfCompensationEmpty = amountOfCompensation === '' || amountOfCompensation === '0';

    if (isToOffsetCosts && ((isAmountOfCompensationEmpty && !isEditMode) || !isFileAdded))
    {
      if ((isAmountOfCompensationEmpty && !isEditMode) && !isFileAdded)
      {
        resultError += 'Сумма компенсации без НДС должна быть заполнена, прикрепите документ, подтверждающий дополнительные затраты. ';
      }
      else if ((isAmountOfCompensationEmpty && !isEditMode))
      {
        resultError += 'Сумма компенсации без НДС должна быть заполнена. ';
      }
      else
      {
        resultError += 'Прикрепите документ, подтверждающий дополнительные затраты. ';
      }
    }
  }

  if (isTerm)
  {
    const isPenaltyEmpty = penalty === '' || penalty === '0';

    if (isCompensatePenalty && ((isPenaltyEmpty && !isEditMode) || !isFileAdded))
    {
      if ((isPenaltyEmpty && !isEditMode) && !isFileAdded)
      {
        resultError += 'Размер штрафной неустойки должен быть заполнен, прикрепите документ, подтверждающий штрафную неустойку. ';
      }
      else if (isPenaltyEmpty && !isEditMode)
      {
        resultError += 'Размер штрафной неустойки должен быть заполнен. ';
      }
      else
      {
        resultError += 'Прикрепите документ, подтверждающий штрафную неустойку. ';
      }
    }
  }

  if (isQualityOrService || isDirtyWagons || isTerm)
  {
    const isSumReqRolledMetallEmpty = sumReqRolledMetall === '' || sumReqRolledMetall === '0';

    if (!isChangeComplaintCosts)
    {
      if (isEvaluateRolledMetal && (isSumReqRolledMetallEmpty && !isEditMode))
      {
        resultError += 'Новая цена должна быть заполнена. ';
      }

      if (isEvaluateRolledMetal && !isEditMode && !isSumReqRolledMetallEmpty && Util.countDigitsBeforeDot(sumReqRolledMetall) < 4)
      {
        resultError += 'Сумма требования к уценке в руб. должна быть не менее 4х знаков до запятой. ';
      }

      if (isSale && ((sale === '' || sale === '0') && !isEditMode))
      {
        resultError += 'Размер скидки должен быть заполнен. ';
      }
    }
  }

  if (!isChangeComplaintCosts && email !== null)
  {
    const badEmails: string[] = [];

    email.split(';').forEach((emailOne) => {
      if (!Util.isEmailCorrect(emailOne))
      {
        badEmails.push(emailOne);
      }
    });

    if (badEmails.length > 0)
    {
      resultError += `Некорректно введенный E-mail контактного лица представителя (${badEmails.join(', ')}). `;
    }
  }

  if (!isChangeComplaintCosts && (account.role === AccountRole.Stpk || initiatorAOSDSelected))
  {
    const initiatorEnterpriseId = edited?.initiator?.enterpriseId ?? -1;
    const initiatorFIO = edited?.initiator?.fio ?? null;
    const initiatorPhone = edited?.initiator?.phone ?? null;
    const initiatorEmail = edited?.initiator?.email ?? null;

    if (initiatorEnterpriseId === -1)
    {
      resultError += 'Предприятие инициатора должно быть заполнено. ';
    }

    if (initiatorFIO === null || initiatorFIO === '')
    {
      resultError += 'ФИО инициатора должно быть заполнено. ';
    }

    if (initiatorPhone !== null && !Util.isPhoneValid(`${initiatorPhone}`))
    {
      resultError += `Номер телефона инициатора не корректный. ${Util.getPhoneErrorForValidator(`${initiatorPhone}`)}. `;
    }

    if (initiatorEmail === null || initiatorEmail === '')
    {
      resultError += 'Email инициатора должен быть заполнен. ';
    }

    if (initiatorEmail !== null && initiatorEmail !== '' && !Util.isEmailCorrect(initiatorEmail))
    {
      resultError += 'Некорректно введенный E-mail инициатора. ';
    }
  }

  if (
    (isChangeToComplaintFinance || isChangeToComplaintAll) &&
    !isToScrap &&
    !isEvaluateRolledMetal &&
    !isToOffsetCosts &&
    !isCompensatePenalty &&
    !isReturnOfRolledMetal &&
    !isSale
  )
  {
    resultError += 'Заполните хотя бы одно финансовое требование в разделе «Требования и запрашиваемые действия к поставщику». ';
  }

  if (isChangeComplaintCosts && !isToOffsetCosts)
  {
    resultError += 'Заполните информацию о компенсации дополнительных затрат. ';
  }

  if (resultError.length >  0)
  {
    return resultError;
  }

  if (original !== null)
  {
    const initiatorEnterpriseId = edited?.initiator?.enterpriseId ?? -1;
    const initiatorFIO = edited?.initiator?.fio ?? null;
    const initiatorPost = edited?.initiator?.post ?? null;
    const initiatorPhone = edited?.initiator?.phone ?? null;
    const initiatorEmail = edited?.initiator?.email ?? null;

    const fio = edited?.fio ?? null;
    const enterprise = edited?.enterprise ?? null;
    const phone = edited?.phone ?? null;
    const groupRevised = edited?.groupRevised ?? null;
    const groupRevisedValue = edited?.groupRevisedValue ?? null;
    const groupUnrevised = edited?.groupUnrevised ?? null;
    const groupUnrevisedValue = edited?.groupUnrevisedValue ?? null;

    if (
      original.typeComplaint === typeComplaint
      &&
      (original.isToScrap ?? false) === isToScrap
      &&
      (`${(original.scrapPrice ?? '')}` === scrapPrice)
      &&
      (original.isEvaluateRolledMetal ?? false) === isEvaluateRolledMetal
      &&
      Util.nullOrEmptyStringTo(original.requirementRolledMetall, 'X') === Util.nullOrEmptyStringTo(requirementRolledMetall, 'X')
      &&
      (`${(original.sumReqRolledMetall ?? '')}` === sumReqRolledMetall)
      &&
      (original.isToOffsetCosts ?? false) === isToOffsetCosts
      &&
      (`${(original.amountOfCompensation ?? '')}` === amountOfCompensation)
      &&
      (!isFilesChanged)
      &&
      (original.isReturnOfRolledMetal ?? false) === isReturnOfRolledMetal
      &&
      (original.isSale ?? false) === isSale
      &&
      (`${(original.sale ?? '')}` === sale)
      &&
      (original.isRepresentative ?? false) === isRepresentative
      &&
      (original.isProvideResponse ?? false) === isProvideResponse
      &&
      (original.isIdentifyDangerous ?? false) === isIdentifyDangerous
      &&
      (original.isAnalizProblem ?? false) === isAnalizProblem
      &&
      (original.is8d ?? false) === is8d
      &&
      (original.isAddMetall ?? false) === isAddMetall
      &&
      (original.isIdentifyDangerous ?? false) === isIdentifyDangerous
      &&
      (original.isCompensatePenalty ?? false) === isCompensatePenalty
      &&
      (`${(original.penalty ?? '')}` === penalty)
      &&
      Util.nullOrEmptyStringTo(original.otherReq, 'X') === Util.nullOrEmptyStringTo(otherReq, 'X')
      &&
      Util.nullOrEmptyStringTo(`${original.initiator?.enterpriseId ?? ''}`, 'X') === Util.nullOrEmptyStringTo(initiatorEnterpriseId === -1 ? null : `${initiatorEnterpriseId}`, 'X')
      &&
      Util.nullOrEmptyStringTo(`${original.initiator?.fio ?? ''}`, 'X') === Util.nullOrEmptyStringTo(initiatorFIO, 'X')
      &&
      Util.nullOrEmptyStringTo(`${original.initiator?.post ?? ''}`, 'X') === Util.nullOrEmptyStringTo(initiatorPost, 'X')
      &&
      Util.nullOrEmptyStringTo(`${original.initiator?.phone ?? ''}`, 'X') === Util.nullOrEmptyStringTo(`${initiatorPhone ?? ''}`, 'X')
      &&
      Util.nullOrEmptyStringTo(`${original.initiator?.email ?? ''}`, 'X') === Util.nullOrEmptyStringTo(initiatorEmail, 'X')
      &&
      Util.nullOrEmptyStringTo(original.fio, 'X') === Util.nullOrEmptyStringTo(fio, 'X')
      &&
      Util.nullOrEmptyStringTo(original.enterprise, 'X') === Util.nullOrEmptyStringTo(enterprise, 'X')
      &&
      Util.nullOrEmptyStringTo(original.phone, 'X') === Util.nullOrEmptyStringTo(phone, 'X')
      &&
      Util.nullOrEmptyStringTo(original.email, 'X') === Util.nullOrEmptyStringTo(email, 'X')
      &&
      Util.nullOrEmptyStringTo(original.groupRevised, 'X') === Util.nullOrEmptyStringTo(groupRevised, 'X')
      &&
      ((original.groupRevisedValue ?? 0) === (groupRevisedValue ?? 0))
      &&
      Util.nullOrEmptyStringTo(original.groupUnrevised, 'X') === Util.nullOrEmptyStringTo(groupUnrevised, 'X')
      &&
      ((original.groupUnrevisedValue ?? 0) === (groupUnrevisedValue ?? 0))
    ) 
    {
      return 'Изменения отсутствуют';
    }
  }

  return resultError;
}

const getDefectErrorMessage = (
  defect: types.inspection.Defect,
  modeControlDefect: types.inspection.ModeControlDefect,
  isEntryAccounting: boolean,
  account: types.auth.Account,
  inspectionState: types.inspection.InspectionState,
  inspectionStatusCode: types.inspection.InspectionStatusCode,
  defectRefer: types.refers.DefectReferElement[],
  isEditMode: boolean,
  isChangeComplaintCosts: boolean,
  isChangeToComplaint: boolean,
  isChangeToComplaintFinance: boolean,
  isChangeComplaintAll: boolean,
): string => {
  if (modeControlDefect === types.inspection.ModeControlDefect.SKIP)
  {
    return '';
  }

  const {
    DefectMode,
    ModeControlDefect,
    DefectFileType,
    DefectFileMode,
    ComplaintType, 
    ComplaintMode,
    PieceMode,
  } = types.inspection;
  const { AccountRole } = types.auth;

  const isStpk = account.role === AccountRole.Stpk;
  const isClientAdminOrStpk = [AccountRole.ClientAdmin, AccountRole.Stpk].includes(account.role);
  const isClientOrContractor = [AccountRole.Client, AccountRole.Contractor].includes(account.role);
  const isClientOrClientAdminOrStpk = [AccountRole.Client, AccountRole.ClientAdmin, AccountRole.Stpk].includes(account.role);

  const isNullOrQualityOrService = [null, DefectType.QUALITY, DefectType.SERVICE].includes(defect.defectType);
  const isNewOrDraftOrErrorOrCreate = [InspectionState.NEW, InspectionState.DRAFT, InspectionState.ERROR, InspectionState.CREATE].includes(inspectionState);

  let isErrorDefectId = false;
  let isErrorPlaceOfFixationsId = false;
  let isErrorRentalStateOfDelivery = false;
  let isErrorDeviationValue = false;
  let isErrorLocationsOfDefect = false;
  let isErrorViolatedRequirements = false;
  let isErrorWayToUseRegectedId = false;
  let isErrorActJoinInsp = false;
  let isErrorDateJoinInsp = false;
  let isErrorFileJoinInsp = false;
  let isErrorComplaint = false;
  let isErrorComplaintChanges = false;
  let isErrorComplaintFilled = false;
  let complaintError = '';
  let isErrorPiece = false;
  let isErrorPieceFilled = false
  let pieceError = '';

  const defectReferItem = defectRefer.find((item) => item.id === defect.defectId) ?? null;

  if (defect.mode !== DefectMode.DEL)
  {
    if (!isEntryAccounting)
    {
      isErrorDefectId = (defect.defectId === null || defect.defectId === 0);

      if (modeControlDefect === ModeControlDefect.NONE)
      {
        isErrorDefectId = isClientOrContractor && defect.defectId === -1;
      }
      else
      {
        isErrorDefectId = defect.defectId === -1;
      }

      isErrorPlaceOfFixationsId = defect.defectId === 99
                                  &&
                                  defect.placeOfFixationId === null
                                  &&
                                  isClientOrClientAdminOrStpk;
     
      const defectConfigs = defectReferItem?.configs ?? [];

      {
        const configValue = defectConfigs.find((item) => item.code === 'RENTAL_STATE_OF_DELIVERY_FILL')?.value ?? '0';
        const isValueMustBeButEmpty = configValue === '1' && defect.rentalStateOfDelivery === null;

        isErrorRentalStateOfDelivery = isValueMustBeButEmpty
                                       &&
                                       isNullOrQualityOrService
                                       &&
                                       isNewOrDraftOrErrorOrCreate
                                       &&
                                       isClientOrClientAdminOrStpk;
      }

      {
        const configValue = defectConfigs.find((item) => item.code === 'FIELD_DEVIATION_VALUE')?.value ?? '0';
        const isValueMustBeButEmpty = configValue === '1' && (defect.deviationValue === null || defect.deviationValue === 0);

        isErrorDeviationValue = isValueMustBeButEmpty
                                &&
                                isNullOrQualityOrService
                                &&
                                isNewOrDraftOrErrorOrCreate
                                &&
                                isClientOrClientAdminOrStpk;
      }

      {
        const configValue = defectConfigs.find((item) => item.code === 'FIELD_LOCATION_OF_DEFECT')?.value ?? '0';
        const isValueMustBeButEmpty = configValue === '1' && (defect.locationsOfDefect === null || defect.locationsOfDefect.length === 0);

        isErrorLocationsOfDefect = isValueMustBeButEmpty
                                   &&
                                   isNullOrQualityOrService
                                   &&
                                   isNewOrDraftOrErrorOrCreate
                                   &&
                                   isClientOrClientAdminOrStpk;
      }

      isErrorViolatedRequirements = defect.isViolationOfRequirements
                                    &&
                                    (Util.nullOrEmptyStringTo(defect.violatedRequirements, 'X') === 'X');

      isErrorWayToUseRegectedId = defect.isPossibilityUsingRejected
                                  &&
                                  (defect.wayToUseRegectedId === null || defect.wayToUseRegectedId === 0);

      isErrorActJoinInsp = defect.__isJoinInspection
                           &&
                           (Util.nullOrEmptyStringTo(defect.actJoinInsp, 'X') === 'X');

      isErrorDateJoinInsp = defect.__isJoinInspection
                            &&
                            (defect.timestampJoinInsp === null || defect.timestampJoinInsp === 0);

      if (
        defect.__isJoinInspection
        &&
        (defect.files ?? []).filter((item) => {
          return (
            item.type === DefectFileType.ACT_JOIN_INSP
            &&
            item.mode !== DefectFileMode.DEL
            &&
            item.guid !== null
          )
        }).length === 0
      )
      {
        isErrorFileJoinInsp = true;
      }

      if (
        ((modeControlDefect === ModeControlDefect.COMPLETE) && isStpk)
        ||
        ((modeControlDefect === ModeControlDefect.CHANGING_REQUIREMENTS) && isClientAdminOrStpk)
      )
      {
        isErrorComplaint = defect.complaint === null || (defect.complaint.typeComplaint === ComplaintType.NONE);
      }

      if (modeControlDefect === ModeControlDefect.CHANGING_REQUIREMENTS)
      {
        isErrorComplaintChanges = defect.complaint?.mode === ComplaintMode.EXIST;
      }

      if (defect.complaint !== null && modeControlDefect !== ModeControlDefect.EXCEPT_COMPLAINT)
      {
        let isComplaintModeView = false;
        let isComplaintModeEdit = false;

        if (!isChangeToComplaint && !isChangeToComplaintFinance && !isChangeComplaintCosts && !isChangeComplaintAll)
        {
          if (
            inspectionState === InspectionState.EDIT
            &&
            [InspectionStatusCode.INTERNAL, InspectionStatusCode.INTERNAL_NO_TRANS_COMPLETED].includes(inspectionStatusCode)
          )
          {
            isComplaintModeEdit = true;
          }
          else
          {
            isComplaintModeView = true;
          }
        }
        complaintError = isComplaintModeView ? '' : getComplaintErrorMessage(
          account,
          defect.defectType,
          defect.files,
          defect.complaint,
          null,
          isChangeComplaintCosts,
          isChangeToComplaintFinance,
          isChangeComplaintAll,
          defect.complaint.__initiatorAOSDSelected ?? false,
          isComplaintModeEdit,
        );

        isErrorComplaintFilled = complaintError !== '';
      }
    }

    isErrorPiece = defect.pieces === null || defect.pieces.filter((item) => item.mode !== PieceMode.DEL).length === 0;

    if (defect.pieces !== null && !isErrorPiece)
    {
      (defect.pieces ?? []).forEach((piece) => {
        if (isErrorPieceFilled) return;

        if (piece.mode !== PieceMode.DEL)
        {
          pieceError = getPieceErrorMessage(
            piece,
            defect.pieces ?? [],
            isEntryAccounting,
            account.isWireRod,
            true,
            account,
            defect.defectType,
            defectReferItem,
            inspectionState,
            isEditMode,
          );

          isErrorPieceFilled = pieceError !== '';
        }
      });
    }

    if (
      isErrorDefectId
      ||
      isErrorPlaceOfFixationsId
      ||
      isErrorRentalStateOfDelivery
      ||
      isErrorDeviationValue
      ||
      isErrorLocationsOfDefect
      ||
      isErrorViolatedRequirements
      ||
      isErrorWayToUseRegectedId
      ||
      isErrorActJoinInsp
      ||
      isErrorDateJoinInsp
      ||
      isErrorFileJoinInsp
      ||
      isErrorComplaint
      ||
      isErrorComplaintChanges
      ||
      isErrorComplaintFilled
      ||
      isErrorPiece
      ||
      isErrorPieceFilled
    )
    {
      let message: string[] = [];

      isErrorDefectId && message.push('Вид дефекта');
      isErrorPlaceOfFixationsId && message.push('Место фиксации дефекта');
      isErrorRentalStateOfDelivery && message.push('Прокат в состоянии поставки');
      isErrorDeviationValue && message.push('Величина отклонения');
      isErrorLocationsOfDefect && message.push('Расположения дефекта');
      isErrorViolatedRequirements && message.push('Укажите какое требование нарушено');
      isErrorWayToUseRegectedId && message.push('Способ использования забрак. проката');
      isErrorActJoinInsp && message.push('Номер акта СИ');
      isErrorDateJoinInsp && message.push('Дата СИ');
      isErrorFileJoinInsp && message.push('Файл СИ');
      isErrorComplaint && message.push('Требование');
      isErrorPiece && message.push('Информация о продукции');
      isErrorPieceFilled && message.push(pieceError);
      
      if (message.length > 0)
      {
        message = [`Заполните обязательные поля: ${message.join('. ')}`];
      }
      
      isErrorComplaintChanges && message.push('Не внесены изменения в требования.');
      isErrorComplaintFilled && message.push(`Ошибки в требованиях: ${complaintError}`);

      return message.join('. ');
    }
  }

  return '';
}

type SaveInspectionArgs = {
  isComplete: boolean;
  modeControlDefect: ModeControlDefect;
  isTransferOnly?: boolean;
};
type SaveInspectionResolve = void;
export type SaveInspectionReject = {
  error: string;
  redirectToList: boolean;
  isWarning: boolean;
};
export const saveInspectionAsync = createAsyncThunk<
  SaveInspectionResolve,
  SaveInspectionArgs,
  {
    state: RootState,
    rejectValue: SaveInspectionReject,
  }
>(
  `${SLICE_NAME}/saveInspectionAsync`,
  async (args, thunkAPI) => {
    try
    {
      const state = thunkAPI.getState();
      const gER = state.inspectionEdit.generalEdited;
      const isEntryAccounting = state.inspectionEdit.generalIsEntryAccounting;
      const inspectionState = state.inspectionEdit.generalInspectionState;
      const account = state.authCommon.account!;
      const defectRefer = state.inspectionEdit.generalDefectRefer;

      // const isTransferOnly = args.isTransferOnly ?? false;

      if (gER !== null)
      {
        thunkAPI.dispatch(inspectionEditActions.generalOpStatusChanged(AsyncOpStatus.BUSY));
        thunkAPI.dispatch(inspectionEditActions.generalOpStatusLabelChanged('Внутренние проверки'));

        let gE = JSON.parse(JSON.stringify(gER)) as types.inspection.Inspection;
        let __isCompleted = gE.__isCompleted;

        // #region Всем новым файлам в дефекте и позициях присваиваем новые GUID-ы, чтобы отправка была как в первый раз
        gE.inspectionDefects.forEach((defect) => {
          (defect.pieces ?? []).forEach((piece) => {
            if (piece.piece !== null)
            {
              piece.piece = piece.piece.trim();
            }
            piece.files.forEach((file) => {
              if (file.mode === PieceFileMode.NEW)
              {
                // Каждый раз при сохранении меняем гуиды новых файлов, чтобы загрузка файлов была как в первый раз
                // (т.е. во время предыдущей отправки часть файлов могла быть отправлена на сервер)
                file.guid = Util.guid(true);
              }
            })
          });

          (defect.files ?? []).forEach((file) => {
            if (file.mode === DefectFileMode.NEW)
            {
              // Каждый раз при сохранении меняем гуиды новых файлов, чтобы загрузка файлов была как в первый раз
              // (т.е. во время предыдущей отправки часть файлов могла быть отправлена на сервер)
              file.guid = Util.guid(true);
            }
          });
        });
        // #endregion

        // #region Внутренние проверки
        if (args.isComplete)
        {
          let defectError = '';

          gE.inspectionDefects.forEach((defect) => {
            if (defectError !== '')
            {
              return;
            }

            defectError = getDefectErrorMessage(
              defect,
              args.modeControlDefect,
              isEntryAccounting,
              account,
              inspectionState,
              gER.status.code,
              defectRefer,
              state.inspectionEdit.generalOriginal !== null && inspectionState !== InspectionState.CREATE,
              state.inspectionEdit.generalIsChangeComplaintCosts,
              state.inspectionEdit.generalIsChangeToComplaint,
              state.inspectionEdit.generalIsChangeToComplaintFinance,
              state.inspectionEdit.generalIsChangeComplaintAll,
            );

            if (defectError !== '' && defectError !== 'Не внесены изменения в требования')
            {
              defectError = `${defect.defectTitle ?? ''} ${defectError}`;
            }
          });

          if (defectError !== '')
          {
            thunkAPI.dispatch(inspectionEditActions.generalOpStatusChanged(AsyncOpStatus.ERROR));
            return thunkAPI.rejectWithValue({
              error: defectError,
              redirectToList: false,
              isWarning: false,
            });
          }

          const isEquals = generalSelectors.selectGeneralIsEquals(state, args.isComplete);

          if (isEquals)
          {
            thunkAPI.dispatch(inspectionEditActions.generalOpStatusChanged(AsyncOpStatus.ERROR));
            return thunkAPI.rejectWithValue({
              error: 'Изменения отсутствуют',
              redirectToList: true,
              isWarning: false,
            });
          }

          let isErrorComplaint = false
          let isErrorDefects = false;
          if (
            !isEntryAccounting &&
            (account.role === AccountRole.Stpk) &&
            inspectionState !== InspectionState.ERROR
          )
          {
            gE.inspectionDefects.forEach((defect) => {
              if (defect.mode !== types.inspection.DefectMode.DEL)
              {
                if (defect.complaint === null || defect.complaint.typeComplaint === null)
                {
                  isErrorComplaint = true;
                }
              }
            });
          }

          if (!isEntryAccounting)
          {
            gE.inspectionDefects.forEach((defect) => {
              if (defect.mode !== types.inspection.DefectMode.DEL)
              {
                if (defect.defectId === null || defect.defectId === -1)
                {
                  isErrorDefects = true;
                }
              }
            });
          }

          if (isErrorComplaint || isErrorDefects)
          {
            thunkAPI.dispatch(inspectionEditActions.generalOpStatusChanged(AsyncOpStatus.ERROR));
            return thunkAPI.rejectWithValue({
              error: isErrorDefects ? 'Не заполнены дефекты' : 'Не заполнены требования',
              redirectToList: false,
              isWarning: false,
            });
          }
          else
          {
            __isCompleted = true;
            //Осмотр завершен, но еще не передан
            gE.status.id = -1;

            if ([InspectionState.NEW, InspectionState.CREATE, InspectionState.DRAFT].includes(inspectionState))
            {
              gE.status.code = types.inspection.InspectionStatusCode.NO_TRANS_DRAFT;
            }
            else if (inspectionState === InspectionState.ERROR)
            {
              gE.status.code = types.inspection.InspectionStatusCode.NO_TRANS_ERROR;
            }
            else
            {
              gE.status.code = types.inspection.InspectionStatusCode.NO_TRANS_COMPLETED;
            }

            gE.status.title = 'Не отправлен';
            gE.timestampModified = Date.now();

            try
            {
              thunkAPI.dispatch(inspectionEditActions.generalOpStatusLabelChanged('Сохранение осмотра в локальной базе данных'));
              await Db.saveInspection(gE);
            }
            catch (error)
            {
              thunkAPI.dispatch(inspectionEditActions.generalOpStatusChanged(AsyncOpStatus.ERROR));
              return thunkAPI.rejectWithValue({
                error: 'Не удалось сохранить осмотр в локальной базе данных',
                redirectToList: false,
                isWarning: false,
              });
            }
          }
        }
        else
        {
          let defectError = false;

          /*
          gE.inspectionDefects.forEach((defect) => {
            if (defect.mode !== DefectMode.DEL)
            {
              if (!isEntryAccounting)
              {
                if (defect.defectId === null || defect.defectId === 0)
                {
                  defectError = true;
                }
              }
            }
          });
          */

          if (defectError)
          {
            thunkAPI.dispatch(inspectionEditActions.generalOpStatusChanged(AsyncOpStatus.ERROR));
            return thunkAPI.rejectWithValue({
              error: 'Не заполнены дефекты',
              redirectToList: false,
              isWarning: false,
            });
          }

          const isEquals = generalSelectors.selectGeneralIsEquals(state, args.isComplete);

          if (isEquals)
          {
            thunkAPI.dispatch(inspectionEditActions.generalOpStatusChanged(AsyncOpStatus.ERROR));
            return thunkAPI.rejectWithValue({
              error: 'Изменения отсутствуют',
              redirectToList: true,
              isWarning: false,
            });
          }

          try
          {
            thunkAPI.dispatch(inspectionEditActions.generalOpStatusLabelChanged('Сохранение осмотра в локальной базе данных'));
            await Db.saveInspection(gE);
          }
          catch (error)
          {
            thunkAPI.dispatch(inspectionEditActions.generalOpStatusChanged(AsyncOpStatus.ERROR));
            return thunkAPI.rejectWithValue({
              error: 'Не удалось сохранить осмотр в локальной базе данных',
              redirectToList: false,
              isWarning: false,
            });
          }
        }
        // #endregion

        const filesForSend: (types.inspection.PieceFile | types.inspection.DefectFile)[] = [];

        gE.inspectionDefects.forEach((defect) => {
          if (defect.mode !== types.inspection.DefectMode.DEL)
          {
            (defect.pieces || []).forEach((piece) => {
              if (piece.mode !== types.inspection.PieceMode.DEL)
              {
                piece.files.forEach((file) => {
                  if (file.mode === PieceFileMode.NEW)
                  {
                    filesForSend.push(file);
                  }
                });
              }
            });

            (defect.files ?? []).forEach((file) => {
              if (file.mode === DefectFileMode.NEW)
              {
                filesForSend.push(file);
              }
            });
          }
        });

        if (filesForSend.length > 0)
        {
          thunkAPI.dispatch(inspectionEditActions.generalOpStatusLabelChanged('Отправка файлов'));
          for (const file of filesForSend)
          {
            try
            {
              const data = new FormData();
              const source = await Util.binaryStringToFile(file.__binaryStr!, file.__name!, file.__mime!);

              data.append('guid', file.guid);
              data.append('ab_file_source', source);
              await Api.postInspectionFileByGuid(data);
            }
            catch (error)
            {
              thunkAPI.dispatch(inspectionEditActions.generalOpStatusChanged(AsyncOpStatus.ERROR));
              return thunkAPI.rejectWithValue({
                error: 'Не удалось отправить файлы осмотра, повторите операцию позже',
                redirectToList: true,
                isWarning: false,
              });
            }
          }
        }

        const inspectionToSend: types.inspection.SaveInspectionDTO = {
          guid: gE.guid,
          isGroup: gE.isGroup,
          isEntryAccounting: gE.isEntryAccounting,
          timestampInspection: gE.timestampInspection,
          userId: gE.userId,
          timestampModified: Date.now(),
          isCompleted: __isCompleted,
          inspectionDefects: gE.inspectionDefects.map((defect) => ({
            guid: defect.guid,
            timestampDefect: defect.timestampDefect,
            defectType: defect.defectType,
            defectId: defect.defectId,
            priorityId: defect.priorityId,
            placeOfFixationId: defect.placeOfFixationId,
            product: defect.product,
            note: defect.note,
            numExternal: defect.numExternal,
            isViolationOfRequirements: defect.isViolationOfRequirements,
            violatedRequirements: defect.violatedRequirements,
            isPossibilityUsingRejected: defect.isPossibilityUsingRejected,
            wayToUseRegectedId: defect.wayToUseRegectedId,
            rentalStateOfDelivery: defect.rentalStateOfDelivery,
            deviationValue: defect.deviationValue,
            locationsOfDefect: defect.locationsOfDefect.map((item) => ({ id: item.id })),
            actJoinInsp: defect.__isJoinInspection ? defect.actJoinInsp : null,
            timestampJoinInsp: defect.__isJoinInspection ? defect.timestampJoinInsp : null,
            files: defect.files
              .map((item) => {
                if (item.type === types.inspection.DefectFileType.ACT_JOIN_INSP)
                {
                  if (defect.__isJoinInspection)
                  {
                    return item;
                  }

                  return item.mode === types.inspection.DefectFileMode.NEW ? null : { ...item, mode: types.inspection.DefectFileMode.DEL }
                }

                return item;
              })
              .filter((item) => item !== null)
              .filter((item) => item!.mode !== types.inspection.DefectFileMode.EXIST)
              .map((file) => ({
                guid: file!.guid,
                name: file!.name,
                type: file!.type,
                mode: file!.mode
              })),
            pieces: (defect.pieces || []).map((piece) => ({
              guid: piece.guid,
              qmetId: !piece.isPiece || (piece.productTypeKolpinoId !== null && [ProductTypeKolpino.MECHANICAL_ENGINEERING_PART, ProductTypeKolpino.PROCESSED_SHEET].includes(piece.productTypeKolpinoId)) ? piece.qmetId : null,
              heat: piece.isPiece ? piece.heat : null,
              piece: piece.isPiece ? piece.piece : null,
              groupAtt: piece.isPiece ? piece.groupAtt : null,
              qcNum: piece.isPiece ? piece.qcNum : null,
              specificationNum: piece.specificationNum,
              metalDelivered: piece.metalDelivered,
              specificationPoses: piece.specificationPoses.map((pos) => ({
                pos: pos.pos!,
                metalDelivered: pos.metalDelivered!,
                volumeDeliveredTime: pos.volumeDeliveredTime!,
                unitId: pos.unit.id,
                daysOverdue: pos.daysOverdue,
              })),
              carriage: piece.carriage,
              carriageQcs: piece.carriageQcs.map((qc) => ({
                qcNum: qc.qcNum,
              })),
              productTypeKolpinoId: piece.isPiece ? piece.productTypeKolpinoId : null,
              drawNum: piece.isPiece ? piece.drawNum : null,
              isPiece: piece.isPiece,
              weightBeforeProcessing: piece.weightBeforeProcessing,
              weightAfterProcessing: piece.weightAfterProcessing,
              marriageShare: piece.marriageShare,
              weight: piece.weight ?? 0,
              unitId: piece.unit.id,
              note: piece.note,
              topography: (
                piece.topography !== null && 
                (
                  (piece.topography.defectSize !== null && piece.topography.defectSize !== 0) ||
                  piece.topography.defectTpgIdSize !== null ||
                  (piece.topography.defectStep !== null && piece.topography.defectStep !== 0) ||
                  piece.topography.defectTpgIdStep !== null ||
                  (piece.topography.defectDistanceFromEdge !== null && piece.topography.defectDistanceFromEdge !== 0) ||
                  piece.topography.defectTpgIdDistance !== null
                )) ?
                  {
                    defectSize: piece.topography.defectSize,
                    defectTpgIdSize: piece.topography.defectTpgIdSize,
                    defectStep: piece.topography.defectStep,
                    defectTpgIdStep: piece.topography.defectTpgIdStep,
                    defectDistanceFromEdge: piece.topography.defectDistanceFromEdge,
                    defectTpgIdDistance: piece.topography.defectTpgIdDistance
                  }
                  :
                  null,
              fields: piece.fields.map((field) => ({
                id: field.id,
                value: field.value
              })),
              files: piece.files.filter((item) => item.mode !== types.inspection.PieceFileMode.EXIST).map((file) => ({
                guid: file.guid,
                name: file.name,
                type: file.type,
                mode: file.mode
              })),
              mode: piece.mode,
            })),
            complaint: (defect.complaint === null || defect.complaint.typeComplaint === null) ? null : {
              typeComplaint: defect.complaint.typeComplaint,
              isToScrap: defect.complaint.isToScrap,
              scrapPrice: defect.complaint.scrapPrice,
              isEvaluateRolledMetal: defect.complaint.isEvaluateRolledMetal,
              requirementRolledMetall: defect.complaint.requirementRolledMetall,
              sumReqRolledMetall: defect.complaint.sumReqRolledMetall,
              isToOffsetCosts: defect.complaint.isToOffsetCosts,
              amountOfCompensation: defect.complaint.amountOfCompensation,
              isReturnOfRolledMetal: defect.complaint.isReturnOfRolledMetal,
              isSale: defect.complaint.isSale,
              sale:  defect.complaint.sale,
              isRepresentative: defect.complaint.isRepresentative,
              isProvideResponse: defect.complaint.isProvideResponse,
              isIdentifyDangerous: defect.complaint.isIdentifyDangerous,
              isAnalizProblem: defect.complaint.isAnalizProblem,
              is8d: defect.complaint.is8d,
              isAddMetall: defect.complaint.isAddMetall,
              isCompensatePenalty: defect.complaint.isCompensatePenalty,
              penalty: defect.complaint.penalty,
              isInformDeadline: defect.complaint.isInformDeadline,
              otherReq: defect.complaint.otherReq,
              groupRevised: defect.complaint.groupRevised,
              groupRevisedValue: defect.complaint.groupRevisedValue,
              groupUnrevised: defect.complaint.groupUnrevised,
              groupUnrevisedValue: defect.complaint.groupUnrevisedValue,
              fio: defect.complaint.fio,
              enterprise: defect.complaint.enterprise,
              phone: defect.complaint.phone,
              email: defect.complaint.email,
              initiator: defect.complaint.initiator,
              isTransferImmediately: defect.complaint.isTransferImmediately,
              mode: defect.complaint.mode,
            },
            mode: defect.mode,
          }))
        }

        try
        {
          thunkAPI.dispatch(inspectionEditActions.generalOpStatusLabelChanged('Отправка осмотра'));
          await Api.postInspection(inspectionToSend);
        }
        catch (error)
        {
          const apiError = error as ApiError;

          thunkAPI.dispatch(inspectionEditActions.generalOpStatusChanged(AsyncOpStatus.ERROR));

          if (apiError.statusCode === 423)
          {
            return thunkAPI.rejectWithValue({
              error: 'Осмотр отозван, редактирование недоступно. При необходимости восстановите обращение и после этого внесите корректировки.',
              redirectToList: true,
              isWarning: true,
            });
          }

          if (apiError.statusCode === 424)
          {
            return thunkAPI.rejectWithValue({
              error: 'На сервере отсутствуют запрошенные файлы. Попробуйте заново отправить осмотр',
              redirectToList: true,
              isWarning: false,
            });
          }

          if (apiError.statusCode === 409)
          {
            return thunkAPI.rejectWithValue({
              error: 'На сервере присутствует более новая версия аудита. Попробуйте заново отправить осмотр',
              redirectToList: true,
              isWarning: false,
            });
          }
          
          return thunkAPI.rejectWithValue({
            error: 'Не удалось отправить осмотр. Попробуйте повторить операцию позже',
            redirectToList: true,
            isWarning: false,
          });
        }

        try
        {
          thunkAPI.dispatch(inspectionEditActions.generalOpStatusLabelChanged('Удаление осмотра из локальной базы данных'));
          await Db.removeInspection(gE.guid);
        }
        catch (error)
        {
          thunkAPI.dispatch(inspectionEditActions.generalOpStatusChanged(AsyncOpStatus.ERROR));
          return thunkAPI.rejectWithValue({
            error: 'Осмотр отправлен, но не был удален из локальной базы данных',
            redirectToList: true,
            isWarning: false,
          });
        }

        thunkAPI.dispatch(inspectionEditActions.generalOpStatusChanged(AsyncOpStatus.SUCCESS));
      }
    }
    catch (error)
    {
      thunkAPI.dispatch(inspectionEditActions.generalOpStatusChanged(AsyncOpStatus.ERROR));
      return thunkAPI.rejectWithValue({
        error: 'При сохранении осмотра произошла ошибка, попробуйте повторить операцию позже',
        redirectToList: true,
        isWarning: false,
      });
    }
  }
);
// #endregion

// #region transferToSeverstalAsync
type TransferToSeverstalArgs = {
  guid: string;
};
type TransferToSeverstalResolve = void;
export type TransferToSeverstalReject = string;
export const transferToSeverstalAsync = createAsyncThunk<
  TransferToSeverstalResolve,
  TransferToSeverstalArgs,
  {
    state: RootState,
    rejectValue: TransferToSeverstalReject,
  }
>(
  `${SLICE_NAME}/transferToSeverstalAsync`,
  async (args, thunkAPI) => {
    try
    {
      await Api.postInspectionTransferToSeverstal({ guid: args.guid });
    }
    catch (error)
    {
      return thunkAPI.rejectWithValue('Не удалось передать осмотр в Северсталь');
    }
  }
);
// #endregion

// #region reportHistoryToEmailAsync
type ReportHistoryToEmailArgs = {
  guid: string;
};
type ReportHistoryToEmailResolve = string;
export type ReportHistoryToEmailReject = string;
export const reportHistoryToEmailAsync = createAsyncThunk<
  ReportHistoryToEmailResolve,
  ReportHistoryToEmailArgs,
  {
    state: RootState,
    rejectValue: ReportHistoryToEmailReject,
  }
>(
  `${SLICE_NAME}/reportHistoryToEmailAsync`,
  async (args, thunkAPI) => {
    try
    {
      await Api.getInspectionHistoryRequestAsExcelToEmail({ guid: args.guid });
      const account = thunkAPI.getState().authCommon.account!;
      return `Результаты осмотра поставлены в очередь на отправку на email ${account.email}. Ожидайте результат в течение 5 - 10 минут`;
    }
    catch (error)
    {
      const apiError = error as ApiError;

      if (apiError.statusCode === 404)
      {
        return thunkAPI.rejectWithValue('Осмотр не найден');
      }

      if (apiError.statusCode === 500)
      {
        return thunkAPI.rejectWithValue('Сервер недоступен. Повторите попытку позже');
      }

      return thunkAPI.rejectWithValue('Проверьте подключение к сети. Отчет не отправлен');
    }
  }
);
// #endregion

// #region reportToEmailAsync
type ReportToEmailArgs = {
  guid: string;
};
type ReportToEmailResolve = string;
export type ReportToEmailReject = string;
export const reportToEmailAsync = createAsyncThunk<
  ReportToEmailResolve,
  ReportToEmailArgs,
  {
    state: RootState,
    rejectValue: ReportToEmailReject,
  }
>(
  `${SLICE_NAME}/reportToEmailAsync`,
  async (args, thunkAPI) => {
    try
    {
      await Api.postInspectionReportByGuid({ guid: args.guid });
      const account = thunkAPI.getState().authCommon.account!;
      return `Результаты осмотра поставлены в очередь на отправку на email ${account.email}. Ожидайте результат в течение 5 - 10 минут`;
    }
    catch (error)
    {
      const apiError = error as ApiError;

      if (apiError.statusCode === 400 || apiError.statusCode === 409)
      {
        return thunkAPI.rejectWithValue('По данному осмотру выполняется синхранизация данных. Повторите попытку позже');
      }

      if (apiError.statusCode === 403 || apiError.statusCode === 449 || apiError.statusCode === 500)
      {
        return thunkAPI.rejectWithValue('Сервер недоступен. Повторите попытку позже');
      }

      return thunkAPI.rejectWithValue('Проверьте подключение к сети. Отчет не отправлен');
    }
  }
);
// #endregion

// #region deleteInspectionAsync
type DeleteInspectionArgs = {
  guid: string;
};
type DeleteInspectionResolve = void;
export type DeleteInspectionReject = string;
export const deleteInspectionAsync = createAsyncThunk<
  DeleteInspectionResolve,
  DeleteInspectionArgs,
  {
    state: RootState,
    rejectValue: DeleteInspectionReject,
  }
>(
  `${SLICE_NAME}/deleteInspectionAsync`,
  async (args, thunkAPI) => {
    try
    {
      await Api.deleteInspectionByGuid({ guid: args.guid });
    }
    catch (error)
    {
      const apiError = error as ApiError;

      if (apiError.statusCode !== 404)
      {
        return thunkAPI.rejectWithValue('При попытке удалить осмотр на сервере произошла ошибка');
      }
    }

    try
    {
      await Db.removeInspection(args.guid);
    }
    catch(error)
    {
      return thunkAPI.rejectWithValue('При попытке удалить осмотр из локального хранилища произошла ошибка');
    }
  }
);
// #endregion

// #region getQMMsgAsync
type GetQMMsgArgs = {
  guid: string;
};
type GetQMMsgResolve = types.qmmsg.QMMsg[];
export type GetQMMsgReject = string;
export const getQMMsgAsync = createAsyncThunk<
  GetQMMsgResolve,
  GetQMMsgArgs,
  {
    state: RootState,
    rejectValue: GetQMMsgReject,
  }
>(
  `${SLICE_NAME}/getQMMsgAsync`,
  async (args, thunkAPI) => {
    try
    {
      const result = await Api.getQMMsgClaims({ guidInspection: args.guid });
      return result.company.map((company) => company.qmMsgs).flat();
    }
    catch (error)
    {
      return thunkAPI.rejectWithValue('Не удалось получить статус осмотра');
    }
  }
);
// #endregion



const endingReducers = {
  endingOpened: (state: WritableDraft<InspectionEditState>) => {
    state.endingOpened = true;
  },

  endingClosed: (state: WritableDraft<InspectionEditState>) => {
    state.endingOpened = false;
  },
}

const endingSelectors = {
  selectEndingOpened: (state: Pick<RootState, "inspectionEdit">) => state.inspectionEdit.endingOpened,
  selectEndingEndCaption: (state: Pick<RootState, "inspectionEdit">) => state.inspectionEdit.endingEndCaption,
  selectEndingDraftCaption: (state: Pick<RootState, "inspectionEdit">) => state.inspectionEdit.endingDraftCaption,
  selectEndingCloseCaption: (state: Pick<RootState, "inspectionEdit">) => state.inspectionEdit.endingCloseCaption,
  selectEndingCancelCaption: (state: Pick<RootState, "inspectionEdit">) => state.inspectionEdit.endingCancelCaption,
}

// #endregion

type OpStatusChangedAction = PayloadAction<AsyncOpStatus>;
type OpStatusLabelChangedAction = PayloadAction<string>;
type StringChangedAction = PayloadAction<string>;
type NumberChangedAction = PayloadAction<number>;
type NullableNumberChangedAction = PayloadAction<Nullable<number>>;
type StringArrayChangedAction = PayloadAction<string[]>;
type LocationsArrayChangedAction = PayloadAction<types.refers.LocationReferElement[]>;
type KolpinoPieceChangedAction = PayloadAction<types.inspection.KolpinoDataItem>;
type KolpinoDrawChangedAction = PayloadAction<types.inspection.KolpinoDataItem>;
type NullableStringChangedAction = PayloadAction<Nullable<string>>;
type PhotoAddedAction = PayloadAction<types.inspection.PieceFile>;
type DefectDocumentAddedAction = PayloadAction<{ from: 'defect' | 'complaint'; file: types.inspection.DefectFile; }>;
type CustomFieldChangedAction = PayloadAction<types.inspection.PieceField>;
type ComplaintTypeChangedAction = PayloadAction<types.inspection.ComplaintType>;
type BooleanChangedAction = PayloadAction<boolean>;
type DefectTypeChangedAction = PayloadAction<{ from: Nullable<types.inspection.DefectType>, to: types.inspection.DefectType; }>;
type StringWithIndexChangedAction = PayloadAction<{ index: number, value: string }>;
type PieceMetalDeliveredChangedAction = PayloadAction<types.inspection.PieceMetalDelivered>;
type PieceSpecificationPosUnitWithIndexChangedAction = PayloadAction<{ index: number, value: types.inspection.PieceSpecificationUnit }>;
type PieceMetalDeliveredWithIndexChangedAction = PayloadAction<{ index: number, value: types.inspection.PieceMetalDelivered }>;

export const inspectionEditSlice = createSlice({
  name: SLICE_NAME,
  initialState: { ...initialState },
  reducers: {
    ...generalReducers,
    ...pieceReducers,
    ...defectReducers,
    ...complaintReducers,
    ...endingReducers,
  },
  extraReducers: (builder) => extraReducers.forEach((creator) => creator(builder)),
})

export const inspectionEditSelectors = {
  ...generalSelectors,
  ...pieceSelectors,
  ...defectSelectors,
  ...complaintSelectors,
  ...endingSelectors,
}

export const inspectionEditActions = inspectionEditSlice.actions;

export default inspectionEditSlice.reducer;

// #region Вспомогательные функции

/**
 * Функция определяет, что две УЕ являются дублями с точки зрения идентификаторов.
 * Идентификатором для качества/сервис может быть либо qmetId, либо heat+piece, либо heat+groupAtt+qcNum
 * Идентификатором для грязных вагонов является carriage
 * Идентификатором для сроков является specificationNum
 */
type PieceIdentifiers = {
  qmetId: Nullable<number>;
  heat: Nullable<string>;
  piece: Nullable<string>;
  groupAtt: Nullable<string>;
  qcNum: Nullable<string>;
  specificationNum: Nullable<string>;
  carriage: Nullable<string>;
  productTypeKolpinoId: Nullable<types.inspection.ProductTypeKolpino>;
  drawNum: Nullable<string>;
}
const isPiecesDuplicated = (
  inspectionState: types.inspection.InspectionState,
  defectType: Nullable<types.inspection.DefectType>,
  isPiece: boolean,
  isKolpino: boolean,
  p1: PieceIdentifiers,
  p2: PieceIdentifiers,
): boolean => {
  const { DefectType, InspectionState } = types.inspection;
  const { NEW, DRAFT, CREATE, ERROR } = InspectionState;
  const { MECHANICAL_ENGINEERING_PART, PROCESSED_SHEET, UNPROCESSED_SHEET } = types.inspection.ProductTypeKolpino;

  if ([NEW, DRAFT, CREATE, ERROR].includes(inspectionState))
  {
    if ([null, DefectType.QUALITY, DefectType.SERVICE].includes(defectType))
    {
      if (isPiece)
      {
        if (isKolpino)
        {
          if (
            Util.notNull([p1.productTypeKolpinoId, p2.productTypeKolpinoId])
            &&
            p1.productTypeKolpinoId === p2.productTypeKolpinoId
          )
          {
            switch (p1.productTypeKolpinoId)
            {
              case MECHANICAL_ENGINEERING_PART:
                if (
                  Util.notNull([p1.piece, p1.groupAtt, p1.qcNum, p1.drawNum, p2.piece, p2.groupAtt, p2.qcNum, p2.drawNum])
                  &&
                  p1.piece === p2.piece && p1.groupAtt === p2.groupAtt && p1.qcNum === p2.qcNum && p1.drawNum === p2.drawNum
                )
                {
                  return true;
                }
                break;
              case PROCESSED_SHEET:
                if (
                  Util.notNull([p1.piece, p1.groupAtt, p1.qcNum, p2.piece, p2.groupAtt, p2.qcNum])
                  &&
                  p1.piece === p2.piece && p1.groupAtt === p2.groupAtt && p1.qcNum === p2.qcNum
                )
                {
                  return true;
                }
                break;
              case UNPROCESSED_SHEET:
                if (
                  Util.notNull([p1.groupAtt, p1.qcNum, p2.groupAtt, p2.qcNum])
                  &&
                  p1.groupAtt === p2.groupAtt && p1.qcNum === p2.qcNum
                )
                {
                  return true;
                }
                break;
            }
          }
        }
        else
        {
          if (Util.notNull([p1.groupAtt]))
          {
            if (Util.notNull([p2.groupAtt, p1.qcNum, p2.qcNum]) && p2.groupAtt === p1.groupAtt && p2.qcNum === p1.qcNum)
            {
              return true;
            }
          }
          else
          {
            if (Util.notNull([p1.heat, p2.heat, p1.piece, p2.piece]) && p1.heat === p2.heat && p1.piece === p2.piece)
            {
              return true;
            }
          }
        }
      }
      else
      {
        if (Util.notNull([p1.qmetId, p2.qmetId]) && p1.qmetId === p2.qmetId)
        {
          return true;
        }
      }
    }

    if (defectType === DefectType.TERM)
    {
      if (Util.notNull([p1.specificationNum, p2.specificationNum]) && p1.specificationNum === p2.specificationNum)
      {
        return true;
      }
    }

    if (defectType === DefectType.DIRTY_WAGONS)
    {
      if (Util.notNull([p1.carriage, p2.carriage]) && p1.carriage === p2.carriage)
      {
        return true;
      }
    }
  }

  return false;
}

const isEditedPieceHasDouble = (
  inspectionState: types.inspection.InspectionState,
  defectType: Nullable<types.inspection.DefectType>,
  pieceEdited: types.inspection.Piece,
  allPieces: types.inspection.Piece[],
  isKolpino: boolean,
): boolean => {
  return allPieces
    .filter((piece) => piece.guid !== pieceEdited.guid && piece.mode !== PieceMode.DEL)
    .find((otherPiece) => isPiecesDuplicated(
      inspectionState,
      defectType,
      pieceEdited.isPiece,
      isKolpino,
      {
        qmetId: pieceEdited.qmetId,
        heat: pieceEdited.heat,
        piece: pieceEdited.piece,
        groupAtt: pieceEdited.groupAtt,
        qcNum: pieceEdited.qcNum,
        carriage: pieceEdited.carriage,
        specificationNum: pieceEdited.specificationNum,
        productTypeKolpinoId: pieceEdited.productTypeKolpinoId,
        drawNum: pieceEdited.drawNum,
      },
      {
        qmetId: otherPiece.qmetId,
        heat: otherPiece.heat,
        piece: otherPiece.piece,
        groupAtt: otherPiece.groupAtt,
        qcNum: otherPiece.qcNum,
        carriage: otherPiece.carriage,
        specificationNum: otherPiece.specificationNum,
        productTypeKolpinoId: otherPiece.productTypeKolpinoId,
        drawNum: otherPiece.drawNum,
      }
    )) !== undefined;
}

type CheckDoublesAndGetModeResult = {
  pieceEditedIsDouble: boolean;
  pieceEditedMode: PieceMode;
}
const checkDoublesAndGetMode = (state: WritableDraft<InspectionEditState>): CheckDoublesAndGetModeResult => {
  const defectGuid = defectSelectors.selectDefectExpandedGuid({ inspectionEdit: state });
  const defectType = defectSelectors.selectDefectDefectType({ inspectionEdit: state }, defectGuid);
  const currentDefect = state.generalEdited!.inspectionDefects.find((defect) => defect.guid === defectGuid)!;
  const isKolpino = (state.generalAccount?.configs?.find((item) => item.code === 'KOLPINO')?.value ?? '0') === '1';

  const result: CheckDoublesAndGetModeResult = {
    pieceEditedIsDouble: state.pieceEditedIsDouble,
    pieceEditedMode: state.pieceEdited!.mode,
  }

  result.pieceEditedIsDouble = isEditedPieceHasDouble(
    state.generalInspectionState,
    defectType,
    state.pieceEdited!,
    currentDefect.pieces ?? [],
    isKolpino,
  );

  result.pieceEditedMode = getPieceMode(state);

  return result;
}

const getPieceMode = (state: WritableDraft<InspectionEditState>): PieceMode => {
  const defectGuid = defectSelectors.selectDefectExpandedGuid({ inspectionEdit: state });
  const defectType = defectSelectors.selectDefectDefectType({ inspectionEdit: state }, defectGuid);

  if (state.pieceEdited!.mode !== PieceMode.NEW)
  {
    const original = pieceSelectors.selectPieceEditedGeneralOriginal({ inspectionEdit: state });

    if (original !== null && state.generalAccount !== null)
    {
      if (isPiecesEquals(defectType, state.pieceEdited!, original, state.generalAccount))
      {
        return PieceMode.EXIST;
      }
      else
      {
        return PieceMode.EDIT;
      }
    }
  }

  return state.pieceEdited!.mode;
}

export const getKolpinoQCNumError = (productTypeKolpinoId: Nullable<ProductTypeKolpino>, qcNum: Nullable<string>): string => {
  switch (productTypeKolpinoId)
  {
    case ProductTypeKolpino.MECHANICAL_ENGINEERING_PART:
    case ProductTypeKolpino.PROCESSED_SHEET:
      {
        const r_x_xxxx = new RegExp('^\\d-\\d{4}$', 'gm');
        const r_x_xxxx_x = new RegExp('^\\d-\\d{4}-\\d$', 'gm');
        const r_x_xxxx_xx = new RegExp('^\\d-\\d{4}-\\d{2}$', 'gm');

        if (qcNum !== null && !r_x_xxxx.test(qcNum) && !r_x_xxxx_x.test(qcNum) && !r_x_xxxx_xx.test(qcNum))
        {
          return 'Формат X-XXXX или X-XXXX-X или X-XXXX-XX';
        }
      }
      break;
    case ProductTypeKolpino.UNPROCESSED_SHEET:
      {
        const r_75__ = new RegExp('^75\\d+$', 'gm');
        const r_14__ = new RegExp('^14\\d+$', 'gm');
        const r_40__ = new RegExp('^40\\d+$', 'gm');

        if (qcNum !== null && !r_75__.test(qcNum) && !r_14__.test(qcNum) && !r_40__.test(qcNum))
        {
          return 'Формат 75* или 14* или 40*';
        }
      }
      break;
    default:
  }

  return '';
}



// #endregion